// Scripts editor (?script=<id>). HD-7: moved from pages/Scripts.jsx (the Scripts() component minus the list), then
// migrated onto src/ui (DESIGN rev 2.1 §3.1: the script editor is its own route-level view). The writing surface keeps
// its behaviour; autosave, blur-save, the ops diff, the /changes poll, undo and every save payload are unchanged.
import React, { forwardRef, useImperativeHandle, useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowDown, ArrowDownToLine, ArrowLeft, ArrowUp, Bold, Check, ChevronDown, ClipboardPaste, Columns3, Copy, CopyPlus, Download,
  Ellipsis, ExternalLink, FileDown, FileUp, Italic, Link2, MessageSquareText, Minus, Pencil, Plus, Printer, Replace, Save, Search,
  Send, Share2, Trash2, Underline, Undo2, X,
} from 'lucide-react';
import api from '../../lib/api';
import * as lfe from '../../lib/longformEpisodes';
import { useAuth } from '../../context/AuthContext';
import LongformSeriesPanel from '../../components/LongformSeriesPanel';
import ShareLinkManager from '../../components/ShareLinkManager';
import Badge from '../../ui/Badge';
import Button from '../../ui/Button';
import Checkbox from '../../ui/Checkbox';
import Icon from '../../ui/Icon';
import Input from '../../ui/Input';
import Menu from '../../ui/Menu';
import Modal from '../../ui/Modal';
import Popover from '../../ui/Popover';
import Skeleton from '../../ui/Skeleton';
import StageChip from '../../ui/StageChip';
import Tag from '../../ui/Tag';
import Textarea from '../../ui/Textarea';
import { useConfirm } from '../../ui/Confirm';
import { useToast } from '../../ui/Toast';
import { STAGES, conditionOf, stageOf } from '../../ui/stages';
import { usePageHeader } from '../../shell/pageHeader';
import { clean } from '../../shell/copy';
import { useResizableTableColumns } from '../../hooks/useResizableColumns';
import { useScriptClipboard } from '../../hooks/useScriptClipboard';
import { exportSceneCSV } from '../../lib/scriptExport';
import { diffToOps, applyOps } from '../../utils/scriptOps';
import ClioChat from '../../components/ClioChat';
import CharacterRegistryModal from '../../components/CharacterRegistryModal';
import { useSpokenWordCount } from '../../hooks/useSpokenWordCount';
import { estimateLabel, minutesText, parseMinutes } from '../../lib/runtimeMinutes';
import { SPOKEN_COL, formatRuntime, formatWordCount, WPM_STORAGE_KEY, WPM_DEFAULT, WPM_MIN, WPM_MAX, clampWpm } from '../../utils/wordCount';
import BackburnerBadge from './BackburnerBadge';
import SceneRow, { ColorSwatch } from './SceneRow';
import {
  PROD_NOTES_INSERT_IDX,
  displayCols,
  IN_PRODUCTION_STAGES,
  sentenceLabel,
  normalizeSceneIds,
  emptyRow,
  getAvailableTransitions,
  transitionBtnLabel,
  detectAndExtendColumn,
  autoResize,
  autoResizeAll,
  renderMarkdown,
  isEditableTarget,
  AUTOSCROLL_EDGE_PX,
  AUTOSCROLL_MAX_STEP_PX,
  clampToScrollerRect,
  parseScenesPositional,
  csvToScenes,
  tsvEscapeCell,
  buildScriptTSV,
  buildSubsetTSV,
  mapClipRowsToDestination,
  buildScriptHTML,
  parseScenesFromHTML,
  downloadTemplate,
} from './scriptHelpers';
import { API_BASE } from '../../lib/apiBase';


// The script editor (everything under ?script=<id>). Mounted for the whole life of /scripts, as the old
// single component was, so its unmount flush, the [location.key] back-to-list effect and the auto-open
// wait on the list fetch behave exactly as before. ScriptList shows while no script is open.
//   listLoad            the list fetch is in flight (the auto-open waits for it, as before)
//   onOpenChange(bool)  a script is open (the page hides the list)
//   loadScripts / setScripts / setShowNew / setNewScriptDefaults / setSaveTplPrompt: the list-side
//                       state the old component shared, forwarded to ScriptList
// ref: { selectScript }
const ScriptEditor = forwardRef(function ScriptEditor({
  listLoad, onOpenChange, loadScripts, setScripts, setShowNew, setNewScriptDefaults, setSaveTplPrompt,
}, ref) {
  const { user, hasRole } = useAuth();
  const toast = useToast();
  const confirm = useConfirm();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();


  // Editor state
  const [selected,      setSelected]      = useState(null);
  const [draftBody,     setDraftBody]     = useState(null);
  const [draftNotes,    setDraftNotes]    = useState('');
  const [draftTitle,    setDraftTitle]    = useState('');
  const [saving,        setSaving]        = useState(false);
  const [lastSaved,     setLastSaved]     = useState(null);
  // Surfaces a failed save so silent data loss can't recur. null = healthy.
  const [saveError,     setSaveError]     = useState(null); // null | { tooLarge } | { largeDeletion } | { retrying }
  // True when the draft has edits not yet CONFIRMED saved on the server. Drives a
  // persistent "Unsaved" indicator so a save that never landed can't masquerade as saved.
  const [isDirty,       setIsDirty]       = useState(false);
  // P2: same-cell collisions reported by the last op-save (another editor changed a
  // cell you also changed). Non-blocking; surfaced in a dismissible banner. LWW already
  // applied server-side — this just makes the rare silent same-cell loss visible.
  const [cellConflicts, setCellConflicts] = useState([]);

  // Live word counter — words-per-minute is a per-user personal preference
  // (follows the user across every script), persisted in localStorage. `wpm`
  // only ever holds a valid, clamped value so it can safely divide; `wpmInput`
  // is the raw text in the box while typing (may be transiently empty/invalid).
  const [wpm, setWpm] = useState(() => {
    const stored = parseInt(localStorage.getItem(WPM_STORAGE_KEY), 10);
    return Number.isFinite(stored) ? clampWpm(stored) : WPM_DEFAULT;
  });
  const [wpmInput, setWpmInput] = useState(() => String(wpm));
  const wpmPersistTimer = useRef(null);

  // Capture openScriptId before setSearchParams on mount can clear location.state
  const pendingAutoOpenRef = useRef(location.state?.openScriptId || null);
  // Return ticket handed over by the Production Calendar's Script List: the
  // editor's back button navigates to backTo.path (with backTo.state) instead
  // of the Scripts list. Captured at mount for the same reason as above.
  const backToRef = useRef(location.state?.backTo || null);

  // Refs so beforeunload and goBackToList always read the latest values
  const selectedRef   = useRef(null);
  const draftBodyRef  = useRef(null);
  const draftNotesRef = useRef('');

  // The exact body/notes references handed to setDraftBody/setDraftNotes at load
  // time. The debounced autosave compares against these and skips while they're
  // still untouched, so merely *opening* a script never echoes its (possibly
  // stale or empty-fallback) body straight back to the server. Without this, a
  // hard refresh could race the GET ahead of the beforeunload flush, load a
  // stale/null body, then autosave it 2s later and clobber the real script.
  const loadedBodyRef  = useRef(null);
  const loadedNotesRef = useRef('');

  // The last body/notes references CONFIRMED saved on the server (200 returned).
  // dirty = current draft differs from these. Updated on load and on every confirmed
  // save; used by the Unsaved indicator and the unmount flush so unconfirmed edits
  // are never silently treated as durable.
  const savedBodyRef   = useRef(null);
  const savedNotesRef  = useRef('');
  // P2: the server revision (`rev`) the current savedBodyRef is based on. Seeded from
  // the loaded script, advanced on every confirmed op-save and every poll that pulls
  // newer content. Sent as `baseRev` and used by the /changes poll as `sinceRev`.
  const revRef         = useRef(0);
  // P2: guards so the 5s poll never overlaps itself or races an in-flight save.
  const pollingRef     = useRef(false);
  const savingRef      = useRef(false);
  // Auto-retry bookkeeping for transient (network / 5xx) save failures.
  const saveRetryRef   = useRef(0);
  const saveRetryTimer = useRef(null);

  // Undo stack — stores draftBody snapshots, max 50 steps
  const undoStack        = useRef([]);
  const lastFocusBodyRef = useRef(null);
  const handleUndoRef    = useRef(null);
  const handleRedoRef    = useRef(null);
  const redoStack        = useRef([]);

  // SceneRow memoization scaffolding:
  // 1) refs that handlers inside the memoized row read for latest state
  // 2) handlersLatestRef updated each render with current functions
  // 3) rowHandlers stable wrapper object (useMemo with empty deps) that delegates
  //    through handlersLatestRef. Passing this to <SceneRow> means the row's memo
  //    shallow-compare sees handlers as referentially equal across renders.
  const selectedRowsSizeRef = useRef(0);
  const selectAllActiveRef  = useRef(false);
  const handlersLatestRef   = useRef({});
  // Latest app-clipboard handlers/state for the capture-phase keydown listener,
  // so it never reads a stale closure.
  const appClipKbdRef       = useRef({});

  // Version history state
  const [versionsOpen,    setVersionsOpen]    = useState(false);
  const [versions,        setVersions]        = useState([]);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [transitioning, setTransit]       = useState(false);
  const [errorNote,     setErrorNote]     = useState(null); // { what, message }: a failed status change or rename
  useEffect(() => { setErrorNote(null); }, [selected?.id]);
  const [hoveredRow,      setHoveredRow]      = useState(null);
  const [colorPickerRow,  setColorPickerRow]  = useState(null);
  const [colorPickerCell, setColorPickerCell] = useState(null); // { rowIdx, colKey }
  const [autocomplete,    setAutocomplete]    = useState(null); // { rowIdx, colKey, suggestions, selIdx }
  const [selectedRows,       setSelectedRows]       = useState(new Set());
  const [multiDeleteConfirm, setMultiDeleteConfirm] = useState(false);
  // Anchor row for Shift+click range selection. Updated by plain click and Ctrl/Cmd+click.
  const [selectionAnchor,    setSelectionAnchor]    = useState(null);
  // Drag-to-select state. Plain ref (not state) so mousemove doesn't trigger re-renders.
  // { active: bool, anchorIdx: number, startX, startY, suppressClick: bool }
  const dragSelectRef = useRef({ active: false, anchorIdx: null, startX: 0, startY: 0, suppressClick: false });
  // Phase 3 drag-to-reposition state.
  // dropTargetIdx is the gap index (0..scenes.length) where moved rows will land.
  const [dropTargetIdx, setDropTargetIdx] = useState(null);
  // Floating preview chip that follows the cursor during a reposition drag.
  const [dragPreview, setDragPreview] = useState(null); // null | { x, y, count }
  const repositionRef = useRef({ active: false, startX: 0, startY: 0 });

  // Phase 4 row clipboard (internal — does not touch navigator.clipboard).
  // null | { rows: [sceneObj…], wasCut: bool, cutIndices: number[] (only if wasCut) }
  const rowClipboardRef = useRef(null);
  // Rows currently marked as "cut" — rendered dimmed until paste or Escape.
  const [cutRows, setCutRows] = useState(new Set());

  // Phase 5: right-click context menu on the gutter.
  // null | { x, y, targetRows: number[], clickedCell: { rowIdx, colKey } | null }
  const [contextMenu, setContextMenu] = useState(null);
  const ctxMenuRef = useRef(null); // measured for viewport clamping (see useLayoutEffect)
  const [linkPopover,        setLinkPopover]        = useState(null); // { rowIdx, colKey }
  const [linkDraft,          setLinkDraft]          = useState({ text: '', url: '' });
  const [activityLog,        setActivityLog]        = useState([]);
  const [activityOpen,       setActivityOpen]       = useState(false);
  const [activityLoading,    setActivityLoading]    = useState(false);
  // The writing surface's text size (the stepper): user geometry, set inline; starts at t-body's 14 (DESIGN §2.2).
  const [editorFontSize,     setEditorFontSize]     = useState(14);
  const [focusedCell,        setFocusedCell]        = useState(null); // { rowIdx, colKey }
  // Phase B1 (spec 1.1/1.2/1.3): two-state cell model. editingCell is set only
  // when a textarea is actively mounted for that cell; idle cells render as
  // <div>s. mode controls how the textarea's caret/selection is set on mount.
  // originalValue is captured at edit-start so Escape can revert.
  const [editingCell,        setEditingCell]        = useState(null);
  const editingCellRef       = useRef(null);
  useEffect(() => { editingCellRef.current = editingCell; }, [editingCell]);
  // Phase B2 (spec 1.5): rectangular selection range across cells.
  // null = no range; otherwise { startRow, startCol, endRow, endCol } with
  // startCol/endCol as column indices into `cols`. A 1x1 range counts as
  // "no visual highlight" — the active border alone handles the single-cell case.
  const [cellSelection,      setCellSelection]      = useState(null);
  // Non-contiguous cell selection: Set of "rowIdx:colKey" strings. Built via
  // Ctrl/Cmd+click. Coexists with cellSelection — when Ctrl+click happens with
  // an active rect range, the rect's cells are frozen into this set first so
  // they're not lost.
  const [cellMultiSelection, setCellMultiSelection] = useState(() => new Set());
  // Single source of truth for copy/paste: which selection the user last made.
  // 'cells' (drag/click a cell) or 'rows' (click the gutter). getActiveSelection
  // reads THIS rather than guessing from which selection state is non-empty —
  // that guessing is what made copy/paste flip between cells and rows.
  const [selectionMode, setSelectionMode] = useState(null); // 'cells' | 'rows' | null
  const draggingRef          = useRef(false);
  // Last "row:col" a live cell drag extended to — dedupes the document-level
  // mousemove and the auto-scroll rAF loop so they don't setState per event.
  const cellDragLastRef      = useRef(null);
  // Phase C1 (spec 4.1): move-drag in progress.
  // { sourceRow, sourceCol (colIdx), targetRow, targetCol (colIdx) } | null
  const [moveDrag,           setMoveDrag]           = useState(null);
  const moveDragRef          = useRef(null);
  useEffect(() => { moveDragRef.current = moveDrag; }, [moveDrag]);
  // Phase C2 (spec 5.2): autofill-drag in progress.
  // { sourceR1, sourceR2, sourceC1, sourceC2, targetEndRow } | null
  const [autofillDrag,       setAutofillDrag]       = useState(null);
  const autofillDragRef      = useRef(null);
  useEffect(() => { autofillDragRef.current = autofillDrag; }, [autofillDrag]);
  useEffect(() => {
    const onUp = () => {
      draggingRef.current = false;
      cellDragLastRef.current = null;
      handlersLatestRef.current.stopEdgeAutoScroll?.();
      if (moveDragRef.current)     handlersLatestRef.current.commitMoveDrag?.();
      if (autofillDragRef.current) handlersLatestRef.current.commitAutofillDrag?.();
    };
    // Spec 4.1/4.2/5.2: doc-level mousemove keeps drags tracking even when the
    // cursor crosses cell padding or drifts into adjacent columns where the
    // per-cell mouseenter handlers can miss. Uses elementFromPoint on the
    // current pointer coordinates to identify the cell under the cursor.
    const onMove = (e) => {
      // Live cell-range SELECTION drag: extend under the pointer (per-cell
      // mouseenter misses fast moves) and feed the edge auto-scroller.
      if (draggingRef.current) {
        handlersLatestRef.current.cellDragPointerMove?.(e.clientX, e.clientY);
      }
      const af = autofillDragRef.current;
      const md = moveDragRef.current;
      if (!af && !md) return;
      const el = document.elementFromPoint(e.clientX, e.clientY);
      if (!el) return;
      const td = el.closest?.('.scene-td');
      if (!td) return;
      const tr = td.parentElement;
      if (!tr?.dataset?.rowIdx) return;
      const rowIdx = Number(tr.dataset.rowIdx);
      if (!Number.isFinite(rowIdx)) return;
      if (af) handlersLatestRef.current.updateAutofillTarget(rowIdx);
      if (md) {
        const colInner = td.querySelector?.('[data-col]');
        const colKey = colInner?.dataset?.col;
        if (colKey) handlersLatestRef.current.updateMoveTarget(rowIdx, colKey);
      }
    };
    document.addEventListener('mouseup', onUp);
    document.addEventListener('mousemove', onMove);
    return () => {
      document.removeEventListener('mouseup', onUp);
      document.removeEventListener('mousemove', onMove);
      handlersLatestRef.current.stopEdgeAutoScroll?.(); // no loop survives unmount
    };
  }, []);
  const [shareOpen,          setShareOpen]          = useState(false);
  const csvInputRef = useRef(null);   // the footer's Import CSV button opens this hidden file input
  const [clioOpen,           setClioOpen]           = useState(false);
  const [findReplaceOpen,    setFindReplaceOpen]    = useState(false);

  // ── Spreadsheet clipboard sync ───────────────────────────────────
  // # cell select-all state — when active, Ctrl+C/V apply to the whole script.
  const [selectAllActive, setSelectAllActive] = useState(false);
  // Hidden focused textarea — catches paste/copy events when select-all is active so the
  // browser fires those events on an element we control (instead of a cell textarea or
  // dropping them entirely when nothing editable is focused).
  const pasteCatcherRef = useRef(null);


  // ── Cross-script app clipboard ───────────────────────────────────
  // Persistent sessionStorage-backed clipboard for copying a selection from one
  // script and pasting it into another (or elsewhere in the same script).
  // Distinct from the in-tab-only rowClipboardRef and the # select-all Google
  // Sheets clipboard — this one survives navigation and new same-session tabs.
  const {
    payload:        appClip,
    rowCount:       appClipCount,
    writeClipboard: writeAppClip,
    clearClipboard: clearAppClip,
  } = useScriptClipboard();
  // After an app-clipboard copy a toast says what was copied; the toolbar's "Clear clipboard" empties it (the old
  // toast carried that action, the system toast offers Undo only).

  // Resizable columns — editor (percentage-based). Last entry is always the Qual Notes column.
  // Skit:           character 13%, line 27%, notes 13%, scene 13%, props 13%, qualNotes 21%
  // TH:             narration 30%, aRoll 10%, bRoll 10%, vfx 10%, sfx 10%, props 10%, qualNotes 20%
  // AI:             narration 42%, visuals 42%, qualNotes 16%
  // Mockumentary:   character 11%, line 18%, notes 11%, aroll 8%, broll 8%, vfx 9%, props 9%, location 8%, qualNotes 18%
  const { pcts: skitEditorPcts,   startDrag: startSkitEditorDrag,   reset: resetSkitEditorCols   } = useResizableTableColumns('colWidths_editor_Skit_v2',         [13, 27, 13, 13, 13, 21]);
  const { pcts: thEditorPcts,     startDrag: startThEditorDrag,     reset: resetThEditorCols     } = useResizableTableColumns('colWidths_editor_TH_v2',           [30, 10, 10, 10, 10, 10, 20]);
  const { pcts: aiEditorPcts,     startDrag: startAiEditorDrag,     reset: resetAiEditorCols     } = useResizableTableColumns('colWidths_editor_AI_v2',           [42, 42, 16]);
  const { pcts: mockEditorPcts,   startDrag: startMockEditorDrag,   reset: resetMockEditorCols   } = useResizableTableColumns('colWidths_editor_Mockumentary_v2', [11, 18, 11, 8, 8, 9, 9, 8, 18]);
  const { pcts: recipeEditorPcts, startDrag: startRecipeEditorDrag, reset: resetRecipeEditorCols } = useResizableTableColumns('colWidths_editor_Recipe_v2',       [55, 30, 15]);
  // Longform: sceneNumber 8%, character 16%, line 44%, scene 16%, qualNotes 16%
  const { pcts: lfEditorPcts,     startDrag: startLfEditorDrag,     reset: resetLfEditorCols     } = useResizableTableColumns('colWidths_editor_Longform_v1',     [8, 16, 44, 16, 16]);
  // "With Production Notes" variants — used only when the Production Notes column
  // is visible (approved scripts). The column is included in-place so every drag
  // handle maps 1:1 to the on-screen columns. Order = displayCols order + qualNotes.
  // Skit:  character, line, PRODNOTES, notes, scene, props, qualNotes
  // TH:    narration, PRODNOTES, aRoll, bRoll, vfx, sfx, props, qualNotes
  // Mock:  character, line, PRODNOTES, notes, aroll, broll, vfx, props, location, qualNotes
  // Recipe: recipe, PRODNOTES, gestures, qualNotes
  const { pcts: skitEditorPctsP,   startDrag: startSkitEditorDragP,   reset: resetSkitEditorColsP   } = useResizableTableColumns('colWidths_editor_Skit_prod_v1',         [12, 24, 12, 12, 12, 12, 16]);
  const { pcts: thEditorPctsP,     startDrag: startThEditorDragP,     reset: resetThEditorColsP     } = useResizableTableColumns('colWidths_editor_TH_prod_v1',           [26, 12, 9, 9, 9, 9, 9, 17]);
  const { pcts: mockEditorPctsP,   startDrag: startMockEditorDragP,   reset: resetMockEditorColsP   } = useResizableTableColumns('colWidths_editor_Mockumentary_prod_v1', [10, 16, 10, 10, 7, 7, 8, 8, 8, 16]);
  const { pcts: recipeEditorPctsP, startDrag: startRecipeEditorDragP, reset: resetRecipeEditorColsP } = useResizableTableColumns('colWidths_editor_Recipe_prod_v1',       [42, 22, 22, 14]);
  // Longform: sceneNumber, character, line, PRODNOTES, scene, qualNotes
  const { pcts: lfEditorPctsP,     startDrag: startLfEditorDragP,     reset: resetLfEditorColsP     } = useResizableTableColumns('colWidths_editor_Longform_prod_v1',     [7, 14, 38, 14, 14, 13]);


  // Keep refs in sync so beforeunload/goBackToList always see the latest values
  useEffect(() => { selectedRef.current   = selected;   }, [selected]);
  useEffect(() => { draftBodyRef.current  = draftBody;  }, [draftBody]);
  useEffect(() => { draftNotesRef.current = draftNotes; }, [draftNotes]);
  useEffect(() => { savingRef.current     = saving;     }, [saving]);
  // Synced unconditionally each render (cheap), so SceneRow's stable handlers always
  // see the latest selection size + select-all flag without taking those as props
  // (which would break memoization).
  selectedRowsSizeRef.current = selectedRows.size;
  selectAllActiveRef.current  = selectAllActive;

  // Clear undo + redo stacks when a different script is opened
  useEffect(() => {
    undoStack.current = []; redoStack.current = []; lastFocusBodyRef.current = null;
  }, [selected?.id]);

  // Ctrl+Z / Ctrl+Shift+Z / Ctrl+Y — undo/redo when focus is outside a textarea
  // (textareas use browser-native undo for intra-cell typing, per spec 2.2).
  // Ctrl+H always opens Find & Replace, even inside a cell — spec 6.1.
  // Ctrl+X / Ctrl+V / Ctrl+D — selection actions (spec 2.3, 2.4) when in idle
  // mode. Guarded by NOT being in a textarea so they don't interfere with
  // native textarea cut/paste during editing.
  useEffect(() => {
    if (!selected) return;
    const onKeyDown = (e) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      if (e.key === 'h' || e.key === 'H') {
        e.preventDefault();
        setFindReplaceOpen(true);
        return;
      }
      if (document.activeElement?.tagName === 'TEXTAREA') return;
      // Normalize case: with Shift held, the Z key reports as 'Z', so the redo
      // chord (Ctrl+Shift+Z) must match uppercase too — otherwise only Ctrl+Y
      // ever triggered redo.
      const k = e.key.toLowerCase();
      if (k === 'z' && !e.shiftKey) {
        e.preventDefault(); handleUndoRef.current?.();
      } else if ((k === 'z' && e.shiftKey) || k === 'y') {
        e.preventDefault(); handleRedoRef.current?.();
      } else if (e.key === 'x' || e.key === 'X') {
        // Only act if focus is on a scene cell — otherwise let browser handle.
        if (document.activeElement?.classList?.contains('scene-cell-idle')) {
          e.preventDefault();
          handlersLatestRef.current.cutActiveCells();
        }
      } else if (e.key === 'v' || e.key === 'V') {
        // While a row cut is pending, Ctrl+V is a paste-to-MOVE and belongs to the
        // row-clipboard handler below — yield so the move still works even if a
        // scene cell happens to be focused.
        if (cutRows.size > 0) return;
        if (document.activeElement?.classList?.contains('scene-cell-idle')) {
          e.preventDefault();
          // Claim the paste so the legacy row-clipboard handler (below) can't
          // ALSO fire on the same keypress — that double-fire is what pasted
          // every line twice when the app-clipboard capture handler declined.
          e.stopImmediatePropagation();
          handlersLatestRef.current.pasteIntoActiveCells();
        }
      } else if (e.key === 'd' || e.key === 'D') {
        if (document.activeElement?.classList?.contains('scene-cell-idle')) {
          e.preventDefault();
          handlersLatestRef.current.fillDownActiveCells();
        }
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [selected, cutRows]); // cutRows: V yields to the row-move handler while a cut is pending

  // Spreadsheet clipboard sync: deactivate select-all whenever the open script changes.
  useEffect(() => {
    setSelectAllActive(false);
  }, [selected?.id]);

  // Escape deactivates # select-all (when no cell is being edited).
  useEffect(() => {
    if (!selectAllActive) return;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      const tag = document.activeElement?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      setSelectAllActive(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [selectAllActive]);

  // Click outside the script table or its clipboard toolbar deactivates select-all.
  useEffect(() => {
    if (!selectAllActive) return;
    const onMouseDown = (e) => {
      if (e.button !== 0) return;
      if (!(e.target instanceof Element)) return;
      if (e.target.closest('.scene-table')) return;
      if (e.target.closest('[data-clipboard-toolbar]')) return;
      setSelectAllActive(false);
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [selectAllActive]);

  // When select-all turns on, focus the hidden paste-catcher so that the next Ctrl+C/V
  // fires the paste/copy events on an element we own (the catcher's own onPaste/onCopy
  // handlers process the events synchronously via e.clipboardData).
  useEffect(() => {
    if (!selectAllActive) return;
    const id = requestAnimationFrame(() => pasteCatcherRef.current?.focus());
    return () => cancelAnimationFrame(id);
  }, [selectAllActive]);

  // Belt-and-suspenders: any Ctrl+C/V at the document level while select-all is active
  // refocuses the catcher first, so the event fires on it even if focus drifted.
  useEffect(() => {
    if (!selectAllActive) return;
    const onKeyDown = (e) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      if (e.key === 'c' || e.key === 'C' || e.key === 'v' || e.key === 'V') {
        const a = document.activeElement;
        if (a !== pasteCatcherRef.current) {
          pasteCatcherRef.current?.focus();
        }
      }
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [selectAllActive]);

  // Phase 4: row clipboard keyboard shortcuts (Ctrl/Cmd+C/X/V/D, Escape).
  // - C/X/V defer to native browser clipboard when the user is editing inside a textarea or input.
  // - D is always intercepted (suppresses the browser "Add bookmark" dialog).
  // - Escape cancels an in-progress cut.
  useEffect(() => {
    if (!selected) return;
    const onKeyDown = (e) => {
      // When # select-all is active, the dedicated spreadsheet-sync handler owns C/V.
      if (selectAllActive) return;
      const ctrl = e.ctrlKey || e.metaKey;
      const tag = document.activeElement?.tagName;
      const inTextarea = tag === 'TEXTAREA';
      const inInput = tag === 'INPUT';

      if (ctrl && (e.key === 'd' || e.key === 'D') && !isReadOnly) {
        e.preventDefault();
        if (!inTextarea && !inInput && selectedRows.size > 0) duplicateSelectedRows();
        return;
      }
      if (inTextarea || inInput) return; // C/X/V → let native clipboard handle text editing

      if (ctrl && (e.key === 'c' || e.key === 'C') && selectedRows.size > 0) {
        e.preventDefault();
        copySelectedRows();
        return;
      }
      if (ctrl && (e.key === 'x' || e.key === 'X') && selectedRows.size > 0 && !isReadOnly) {
        e.preventDefault();
        cutSelectedRows();
        return;
      }
      if (ctrl && (e.key === 'v' || e.key === 'V') && rowClipboardRef.current && !isReadOnly) {
        e.preventDefault();
        // Single-owner guard: if any other Ctrl+V handler already acted this
        // keypress it stopped propagation before us, so reaching here means we
        // own the paste. Stop here too so no third handler can double it.
        e.stopImmediatePropagation();
        pasteRows();
        return;
      }
      if (e.key === 'Escape' && cutRows.size > 0) {
        setCutRows(new Set());
        rowClipboardRef.current = null;
        return;
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [selected, selectedRows, cutRows, selectAllActive]); // eslint-disable-line react-hooks/exhaustive-deps

  // Flush pending changes when the tab/browser is closed
  // Best-effort durable flush of the latest UNCONFIRMED draft. Used for both full
  // page unload (beforeunload) and in-app unmount (navigating away from the editor),
  // which the debounced timer would otherwise drop. Reads refs so it always sends the
  // newest values, and no-ops when there's nothing unsaved. keepalive (not sendBeacon)
  // because it must carry the Authorization header. A destructive flush is still caught
  // by the server shrink guard (409, not applied).
  const flushUnsaved = () => {
    const base0 = savedBodyRef.current;
    const draft = draftBodyRef.current;
    if (!selectedRef.current || !draft) return;
    const notesChanged = draftNotesRef.current !== savedNotesRef.current;
    if (draft === base0 && !notesChanged) return;
    const authHeader = api.defaults.headers.common['Authorization'];
    if (!authHeader) return;
    const apiBase = API_BASE;
    const headers = { 'Content-Type': 'application/json', Authorization: authHeader };
    // P2: flush as ops so a last-second unmount/unload still MERGES rather than clobbers
    // a concurrent editor. Whole-body fallback only when there's no baseline or the format
    // changed (which ops can't express). keepalive (not sendBeacon) so the auth header rides.
    const useLegacy = !base0 || base0.format !== draft.format;
    if (useLegacy) {
      fetch(`${apiBase}/api/scripts/${selectedRef.current.id}`, {
        method: 'PATCH', headers,
        body: JSON.stringify({ scriptBody: draft, qualNotes: draftNotesRef.current }),
        keepalive: true,
      });
      return;
    }
    const ops = diffToOps(base0, draft);
    if (ops.length === 0 && !notesChanged) return;
    fetch(`${apiBase}/api/scripts/${selectedRef.current.id}/ops`, {
      method: 'PATCH', headers,
      body: JSON.stringify({ baseRev: revRef.current, ops,
        ...(notesChanged && { qualNotes: draftNotesRef.current }), saveType: 'autosave' }),
      keepalive: true,
    });
  };

  useEffect(() => {
    const handleBeforeUnload = () => flushUnsaved();
    window.addEventListener('beforeunload', handleBeforeUnload);
    // Cleanup also fires on component unmount (in-app navigation away from the editor),
    // flushing any pending edit the 2s debounce hadn't saved yet. This closes the
    // silent-loss path where leaving the page mid-edit dropped the queued autosave.
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      flushUnsaved();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep the Unsaved indicator in sync: dirty whenever the draft differs from the
  // last confirmed-saved server state.
  useEffect(() => {
    setIsDirty(!!selected && (draftBody !== savedBodyRef.current || draftNotes !== savedNotesRef.current));
  }, [draftBody, draftNotes, selected]);

  // Debounced autosave — fires 2s after last change
  useEffect(() => {
    if (!selected || !draftBody) return;
    // Skip the echo-save right after a script loads: only autosave once the user
    // has actually edited the body or notes. Every edit handler replaces draftBody
    // with a new object (and draftNotes with a new string), so the first real edit
    // breaks reference-equality and re-enables saving. This prevents a hard refresh
    // from writing a stale/empty just-loaded body back over good server data.
    if (draftBody === loadedBodyRef.current && draftNotes === loadedNotesRef.current) return;
    const timer = setTimeout(saveBody, 2000); // eslint-disable-line react-hooks/exhaustive-deps
    return () => clearTimeout(timer);
  }, [draftBody, draftNotes]); // saveBody intentionally omitted — reads latest closure

  // ── P2: background change poll ───────────────────────────────────────────────
  // While the editor is open and the tab is visible, poll every 5s for changes other
  // editors have saved (GET /:id/changes?sinceRev=rev). Bounds the convergence window
  // to ≤5s even for a passive viewer who never saves — exactly the reviewer-with-a-stale-
  // copy pattern that caused the data loss. Pulls once immediately on re-focus; pauses
  // while hidden (no point polling a backgrounded tab) and skips while a save is in flight
  // (the save response already reconciles). Foreign changes are folded in via
  // adoptServerBody, which preserves the user's un-acked local edits.
  useEffect(() => {
    if (!selected) return;
    const pull = async () => {
      if (pollingRef.current || savingRef.current) return;
      // Defer pulling while the user is actively editing a cell: adopting a foreign change
      // mid-keystroke would call setDraftBody and disrupt the cursor / in-progress text.
      // They catch up within 5s of blurring (and saves reconcile via the save response).
      if (editingCellRef.current) return;
      if (document.visibilityState !== 'visible') return;
      pollingRef.current = true;
      try {
        const res = await api.get(`/api/scripts/${selected.id}/changes`, { params: { sinceRev: revRef.current } });
        if (res.data?.changed) {
          // Only adopt (and let adoptServerBody advance revRef) when it's safe to. If a
          // save is in flight or state isn't ready, leave revRef untouched so the NEXT
          // poll retries — never advance past an unadopted change (that would drop it).
          if (res.data.scriptBody && savedBodyRef.current && !savingRef.current && draftBodyRef.current) {
            adoptServerBody(res.data.scriptBody, savedBodyRef.current, res.data.rev,
                            draftNotesRef.current !== savedNotesRef.current);
          }
        } else if (typeof res.data?.rev === 'number') {
          revRef.current = res.data.rev; // caught up — safe to advance
        }
      } catch { /* transient — next tick retries */ }
      finally { pollingRef.current = false; }
    };
    const interval = setInterval(pull, 5000);
    const onVisible = () => { if (document.visibilityState === 'visible') pull(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => { clearInterval(interval); document.removeEventListener('visibilitychange', onVisible); };
  }, [selected?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Resize all scene textareas when a script loads.
  // Double-RAF ensures the browser has resolved tableLayout:fixed column widths
  // before we measure scrollHeight — setTimeout(0) fires too early.
  useEffect(() => {
    if (!selected) return;
    let raf2;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        autoResizeAll();
      });
    });
    return () => { cancelAnimationFrame(raf1); cancelAnimationFrame(raf2); };
  }, [selected?.id]);

  // Keep textarea heights in sync after draftBody mutations.
  // Walks the prev→current diff regardless of length difference and resizes only the
  // cells that actually moved. Per-keystroke that's 1 cell. addRow-at-end is 5 cells
  // (the new row only — existing rows already have correct heights). Insert/delete in
  // the middle is N cells (everything from the splice point on, since indices shifted).
  // Falls back to chunked autoResizeAll only when many cells differ (paste, undo).
  // Also handles "focus the new row after addRow" without a setTimeout — adders set
  // pendingFocusRef, we read+clear it here so focus lands in the same commit as the row.
  const lastResizeBodyRef = useRef(null);
  const pendingFocusRef = useRef(null);
  // Longform scene flow (D-118): armed by "Add scene" BEFORE the script reloads; consumed by
  // selectScript when the reloaded body lands, so the scene's first row is appended onto the
  // SERVER body (never a stale draft) — { scriptId, sceneNumber, description }.
  const pendingSceneAppendRef = useRef(null);
  // Spec 1.4: remembers the column the user started tabbing from, so Enter
  // after Tab returns to that column on the next row. Cleared on Enter,
  // arrow-nav, mousedown, or Escape.
  const tabOriginColRef = useRef(null);
  useLayoutEffect(() => {
    if (!draftBody) return;
    const prev = lastResizeBodyRef.current;
    lastResizeBodyRef.current = draftBody;
    const scenes = draftBody.scenes || [];
    if (!prev) {
      autoResizeAll();
    } else {
      const prevScenes = prev.scenes || [];
      const changedEls = [];
      const maxLen = Math.max(scenes.length, prevScenes.length);
      for (let i = 0; i < maxLen; i++) {
        const row = scenes[i];
        const prevRow = prevScenes[i];
        if (!row) continue; // row was deleted at this index — nothing to resize
        if (row === prevRow) continue; // reference preserved → height already correct
        for (const key in row) {
          if (key.charCodeAt(0) === 95) continue; // skip metadata keys (_rowColor, _links, etc.)
          if (!prevRow || row[key] !== prevRow[key]) {
            const el = document.querySelector(`[data-row="${i}"][data-col="${key}"]`);
            if (el) changedEls.push(el);
          }
        }
      }
      if (changedEls.length > 200) {
        // Paste / undo-restore: chunk the resize so we don't block the main thread.
        autoResizeAll();
      } else {
        for (let i = 0; i < changedEls.length; i++) autoResize(changedEls[i]);
      }
    }
    // Same-flush focus for newly-added rows (Enter / Tab at end). No setTimeout — focus
    // happens before paint so the user never sees the new row briefly without a cursor.
    if (pendingFocusRef.current) {
      const { rowIdx, colKey, scroll } = pendingFocusRef.current;
      pendingFocusRef.current = null;
      const el = document.querySelector(`[data-row="${rowIdx}"][data-col="${colKey}"]`);
      if (el) { el.focus(); autoResize(el); }
      // Longform scene flow (D-118) only: the series panel above the grid may re-lay out after
      // the reload, so bring the appended row into view after paint. Skit adders never set this.
      // Twice: once after paint, once after the panel's own async fetches (episodes, cast) have
      // settled the layout — the second nudge is a no-op when the row is already centred.
      if (el && scroll) { const nudge = () => { try { el.scrollIntoView({ block: 'center' }); } catch { /* no-op */ } }; requestAnimationFrame(nudge); setTimeout(nudge, 450); }
    }
  }, [draftBody]);

  // Phase 2: click anywhere outside the gutter (and outside the multi-row action
  // footer / multi-delete modal) clears the row selection. Right-clicks are
  // intentionally ignored here — they trigger the row context menu without
  // changing selection.
  useEffect(() => {
    if (selectedRows.size === 0) return;
    const handler = (e) => {
      if (e.button !== 0) return; // left-click only
      const t = e.target;
      if (!(t instanceof Element)) return;
      // Clicks on the gutter are handled by handleGutterMouseDown — don't clear here.
      if (t.closest('td.scene-num')) return;
      // Preserve selection while the user interacts with the multi-row footer or its modal.
      if (t.closest('[data-keep-row-selection]')) return;
      // While the gutter context menu is open, clicks on its items shouldn't clear yet.
      if (t.closest('[data-gutter-context-menu]')) return;
      setSelectedRows(new Set());
      setSelectionAnchor(null);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [selectedRows.size]);

  // Phase 5: suppress right-click mousedown default inside scene rows. Without this,
  // a right-click on a textarea focuses the cell, whose onFocus clears the multi-row
  // selection BEFORE the contextmenu event reads it — so the menu would only see the
  // single right-clicked row.
  useEffect(() => {
    if (!selected) return;
    const onMouseDown = (e) => {
      if (e.button !== 2) return;
      if (!(e.target instanceof Element)) return;
      // Editable target → the browser owns this right-click (it needs the
      // default mousedown to place the caret on the misspelled word before
      // its native menu opens). Our menu won't open for it anyway.
      if (isEditableTarget(e.target)) return;
      if (e.target.closest('td.scene-num')) return;
      if (!e.target.closest('tr.scene-row')) return;
      e.preventDefault();
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [selected]);

  // Phase 5: row context menu — bound at document level so a single listener
  // catches every right-click that lands in a scene row, regardless of how
  // deeply nested the click target is.
  useEffect(() => {
    if (!selected) return;
    const onContextMenu = (e) => {
      if (!(e.target instanceof Element)) return;
      // Right-click inside an in-edit cell textarea / qual-notes textarea /
      // link-popover input → let the browser's native menu open (spellcheck
      // suggestions live there). No preventDefault, no app menu, no selection
      // change.
      if (isEditableTarget(e.target)) return;
      // Right-click anywhere in a scene row — including the gutter (td.scene-num) —
      // opens the context menu. (Gutter right-click previously fell through to the
      // browser's native menu; now it targets the clicked row like a cell does.)
      const tr = e.target.closest('tr.scene-row');
      if (!tr) return; // Not inside a scene row — let native menu show.
      const rowIdxStr = tr.getAttribute('data-row-idx');
      const idx = rowIdxStr != null ? Number(rowIdxStr) : null;
      if (idx == null || Number.isNaN(idx)) return;
      // canEditBody is computed later in the render — read isReadOnly via DOM check instead
      // by looking for a readonly attribute on the textareas. Simpler: bail if no draftBody.
      if (!draftBody) return;
      e.preventDefault();
      const target = selectedRows.has(idx) && selectedRows.size > 0
        ? [...selectedRows].sort((a, b) => a - b)
        : [idx];
      // Capture the clicked cell (if any) so the menu can offer cell coloring.
      const cellEl = e.target.closest('td.scene-td');
      const colKey = cellEl?.querySelector('[data-col]')?.getAttribute('data-col') || null;
      const clickedCell = (colKey && cellEl && !cellEl.classList.contains('scene-num'))
        ? { rowIdx: idx, colKey }
        : null;
      setContextMenu({ x: e.clientX, y: e.clientY, targetRows: target, clickedCell });
    };
    document.addEventListener('contextmenu', onContextMenu);
    return () => document.removeEventListener('contextmenu', onContextMenu);
  }, [selected, selectedRows, draftBody]);

  // Phase 5: close the gutter context menu on outside-click or Escape.
  useEffect(() => {
    if (!contextMenu) return;
    const onMouseDown = (e) => {
      if (!(e.target instanceof Element)) return;
      if (e.target.closest('[data-gutter-context-menu]')) return;
      setContextMenu(null);
    };
    const onKey = (e) => { if (e.key === 'Escape') setContextMenu(null); };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [contextMenu]);

  // Keep the context menu fully on-screen regardless of where the click landed.
  // The menu's height varies (color swatches, optional cell-color section), so we
  // measure the real rendered box and clamp — running in a layout effect so the
  // correction happens before paint (no visible flicker). Falls back to scrolling
  // if the menu is somehow taller than the viewport (maxHeight is set in style).
  useLayoutEffect(() => {
    if (!contextMenu) return;
    const el = ctxMenuRef.current;
    if (!el) return;
    const m = 8; // viewport margin
    const r = el.getBoundingClientRect();
    let left = contextMenu.x;
    let top  = contextMenu.y;
    if (left + r.width  > window.innerWidth  - m) left = window.innerWidth  - r.width  - m;
    if (top  + r.height > window.innerHeight - m) top  = window.innerHeight - r.height - m;
    el.style.left = `${Math.max(m, left)}px`;
    el.style.top  = `${Math.max(m, top)}px`;
  }, [contextMenu]);

  // Re-resize when column widths change (so text rewraps after column drag)
  useEffect(() => {
    if (!selected) return;
    let raf2;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        autoResizeAll();
      });
    });
    return () => { cancelAnimationFrame(raf1); cancelAnimationFrame(raf2); };
  }, [skitEditorPcts, thEditorPcts, aiEditorPcts, mockEditorPcts, recipeEditorPcts]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-resize when font size changes so rows expand/contract to fit the new line height
  useEffect(() => {
    if (!selected) return;
    let raf2;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        autoResizeAll();
      });
    });
    return () => { cancelAnimationFrame(raf1); cancelAnimationFrame(raf2); };
  }, [editorFontSize]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-resize when the browser window/viewport changes size (zoom, resize)
  useEffect(() => {
    if (!selected) return;
    const handleResize = () => {
      requestAnimationFrame(() => {
        autoResizeAll();
      });
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [selected]);

  // Auto-open a specific script when navigated from a Video Card or ?script= URL param.
  // pendingAutoOpenRef captures openScriptId at mount time — setSearchParams({ replace: true })
  // fires on mount and clears location.state before listLoad toggles.
  useEffect(() => {
    const id = pendingAutoOpenRef.current || searchParams.get('script');
    if (!listLoad && id && !selected) {
      pendingAutoOpenRef.current = null;
      // Keep ?script=<id> in the URL so refreshing stays on this script
      // (selectScript ensures the param is present for in-app/state navigation too).
      selectScript(id, { fromAutoOpen: true });
    }
  }, [listLoad]); // eslint-disable-line react-hooks/exhaustive-deps

  // Share links: ShareLinkManager (kind "script") loads them when its dialog opens, as this effect did.

  // Sidebar "Scripts" click always re-navigates to /scripts (changing location.key).
  // If we're in the editor when that happens, return to list view.
  useEffect(() => {
    if (selected) goBackToList();
  }, [location.key]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close color pickers and link popover on any outside click
  useEffect(() => {
    if (colorPickerRow === null && !colorPickerCell && !linkPopover) return;
    const close = () => { setColorPickerRow(null); setColorPickerCell(null); setLinkPopover(null); };
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [colorPickerRow, colorPickerCell, linkPopover]);


  // ── Handlers ─────────────────────────────────────────────────────

  const selectScript = async (id, { fromAutoOpen = false } = {}) => {
    // For regular list clicks: push a history entry (browser back → returns to list).
    // Skip for auto-open cases (VideoCard state nav / ?script= URL) — those don't
    // need it and the extra navigate was clearing location.state before setSelected fired.
    // Reflect the open script in the URL *before* the async load so a refresh
    // restores it. This MUST happen while `selected` is still null: the
    // [location.key] effect below calls goBackToList() on any navigation while a
    // script is open, so changing the URL after setSelected would bounce us to the list.
    if (!fromAutoOpen) {
      const params = new URLSearchParams(location.search);
      params.set('script', id);
      navigate('/scripts?' + params.toString()); // push → browser-back returns to the list
    } else if (searchParams.get('script') !== id) {
      // Opened via in-app state nav (e.g. a Video Card) with no ?script= param —
      // add it without a history push so a refresh still restores it.
      const params = new URLSearchParams(location.search);
      params.set('script', id);
      navigate('/scripts?' + params.toString(), { replace: true });
    }
    try {
      const res = await api.get(`/api/scripts/${id}`);
      const s = res.data;
      setSelected(s);
      const fmt = s.scriptBody?.format || s.client?.allowedScriptFormats?.find(f => f !== 'None') || null;
      const rawBody = s.scriptBody || (fmt ? { format: fmt, scenes: [emptyRow(fmt)] } : null);
      const idBody = rawBody?.scenes
        ? normalizeSceneIds({ ...rawBody, scenes: rawBody.scenes.map(sc =>
            sc._propsItems?.length && !sc.props
              ? { ...sc, props: sc._propsItems.join(', ') }
              : sc
          )})
        : rawBody;
      // FIX-2: a Longform body stored with raw scene numbers is renumbered here, into the baseline, so
      // the D-130 effect below finds nothing to change and opening the script is not an edit (it used to
      // autosave a whole-script renumber, 525 ops on PGE-test-V1-MA). The server applies the same
      // normalization to the whole body on the next real save, so the stored body converges then.
      const migratedBody = idBody?.format === 'Longform' && Array.isArray(idBody.scenes)
        ? { ...idBody, scenes: lfe.normalizeSceneNumbers(idBody.scenes).rows }
        : idBody;
      // D-118: append the just-confirmed scene's first dialogue row onto the freshly loaded
      // body and land in its Character cell. loadedBodyRef / savedBodyRef below keep the
      // loaded body (without the appended row), so the appended row is a real edit — autosave (and D-110 sync) fire.
      const pendingAppend = pendingSceneAppendRef.current; pendingSceneAppendRef.current = null;
      let openingBody = migratedBody;
      if (pendingAppend && pendingAppend.scriptId === s.id && migratedBody?.format === 'Longform') {
        const scenes = migratedBody.scenes || [];
        const row = { ...emptyRow('Longform'), sceneNumber: String(pendingAppend.sceneNumber), scene: String(pendingAppend.description || '').trim() };
        openingBody = { ...migratedBody, scenes: [...scenes, row] };
        pendingFocusRef.current = { rowIdx: scenes.length, colKey: 'character', scroll: true };
      }
      setDraftBody(openingBody);
      setDraftNotes(s.qualNotes || '');
      // Clear any selection carried over from a previously-open script so a paste
      // into this one doesn't anchor on a stale cell from the source script.
      setCellSelection(null);
      setCellMultiSelection(new Set());
      setSelectedRows(new Set());
      setSelectionAnchor(null);
      setFocusedCell(null);
      setSelectionMode(null);
      // Mark this freshly-loaded state as "not yet edited" so the debounced
      // autosave skips it until the user actually changes something.
      loadedBodyRef.current  = migratedBody;
      loadedNotesRef.current = s.qualNotes || '';
      // Freshly loaded == confirmed-saved server state, so the draft starts clean.
      savedBodyRef.current   = migratedBody;
      savedNotesRef.current  = s.qualNotes || '';
      // P2: seed the revision the draft is based on (drives op-save baseRev + the poll).
      revRef.current         = s.rev || 0;
      setIsDirty(false);
      setCellConflicts([]);
      setSaveError(null);
      saveRetryRef.current   = 0;
      if (saveRetryTimer.current) { clearTimeout(saveRetryTimer.current); saveRetryTimer.current = null; }
      setDraftTitle(s.scriptName || '');
      setLastSaved(null);
      setActivityLog([]);
      setActivityOpen(false);
      const storedSize = parseInt(localStorage.getItem(`fontSize_script_${s.id}`), 10);
      setEditorFontSize(storedSize >= 8 && storedSize <= 24 ? storedSize : 14);
    } catch (err) {
      console.error('Failed to load script:', err);
    }
  };

  const goBackToList = () => {
    saveBody(); // flush any pending changes before clearing state
    setSelected(null);
    setDraftBody(null);
    setDraftNotes('');
    // Drop ?script= so the list (not the editor) is what a refresh restores now.
    setSearchParams(prev => { const p = new URLSearchParams(prev); p.delete('script'); return p; }, { replace: true });
    loadScripts();
  };

  const doTransition = async (newStatus) => {
    setTransit(true);
    try {
      const payload = { status: newStatus, qualNotes: draftNotes };
      const res = await api.patch(`/api/scripts/${selected.id}/status`, payload);
      // the status route's script carries no runtime fields (OUT-3 returns them on GET and PATCH /:id): keep the loaded ones
      setSelected(prev => ({ runtime_minutes: prev?.runtime_minutes ?? null, runtime_estimate: prev?.runtime_estimate, ...res.data }));
      // D-126: leaving Approved deleted a Longform series' episode cards; approving minted them — re-read them.
      if ((res.data?.scriptBody?.format || format) === 'Longform') { setLfEpisodesVersion(v => v + 1); loadLfEpisodes(selected.id); }
      setDraftNotes(res.data.qualNotes || '');
    } catch (err) {
      showError('The status did not change', err.response?.data?.error || 'Try again.');
    } finally {
      setTransit(false);
    }
  };
  const doTransitionRef = useRef(doTransition);
  doTransitionRef.current = doTransition;

  // Was an error dialog; now an alert strip under the header (the server's reason can be long, a toast is one line).
  // The hint for "not approved" refusals is kept.
  const showError = (what, message) => {
    const m = clean(message) || '';
    const hint = m.toLowerCase().includes('approved') && m.toLowerCase().includes('production')
      ? ' Scripts must be Approved before they can go into Live or AI production. Approve the script first, then try again.' : '';
    setErrorNote({ what, message: `${m}${hint}` });
  };

  const handleTransition = async (newStatus) => {
    if (!selected || transitioning) return;
    if (selected.qualStatus === 'Approved') {
      // Leaving Approved deletes the production cards: a two-step destructive confirm (was a warning dialog).
      const lf = format === 'Longform';
      const ok = await confirm({
        title: 'Move this script out of Approved?',
        body: (lf
          ? `Moving ${clean(selected.scriptName)} out of Approved deletes its ${lfEpisodes.length} episode card${lfEpisodes.length === 1 ? '' : 's'}: their stage, shoot plans, talent bookings, cut lists and posting slots. The episodes, every line and the cast stay in the script; approving it again recreates the cards from the script.`
          : `Moving ${clean(selected.scriptName)} out of Approved deletes its production cards (Live and AI), any shoot days scheduled on them, and any posting slots.`)
          + (newStatus === 'Needs Review' ? ' The script returns to Needs review for writing and its title can be edited again. It has to be approved again to go back into production.' : '')
          + ' This cannot be undone.',
        confirmLabel: 'Move out of Approved',
        destructive: true,
      });
      if (!ok) return;
      doTransitionRef.current(newStatus);
      return;
    }
    doTransition(newStatus);
  };

  // Shared save-failure handling for both the op-save and legacy paths.
  const handleSaveError = (err, manual) => {
    console.error('Save failed:', err);
    const status = err?.response?.status;
    // Shrink guard: server refused a save that would wipe most of the script (usually
    // a stale copy). Surface loudly; the server has already backed up the old version.
    if (status === 409 && err.response.data?.error === 'large_deletion') {
      setSaveError({ largeDeletion: err.response.data });
    } else if (status === 413) {
      setSaveError({ tooLarge: true });
    } else if (saveRetryRef.current < 3) {
      // Transient (network blip / 5xx): auto-retry with backoff before giving up, so a
      // momentary failure doesn't strand unsaved work. The draft stays dirty throughout.
      saveRetryRef.current += 1;
      setSaveError({ retrying: true });
      saveRetryTimer.current = setTimeout(() => saveBody(manual), 4000 * saveRetryRef.current);
    } else {
      // Exhausted retries — surface a hard failure with a manual Retry affordance.
      setSaveError({ failed: true });
    }
  };

  // P2: fold a merged/pulled server body into the editor, preserving the user's own
  // un-acked edits (those made relative to `baseBody`). `serverBody` is authoritative for
  // everyone else's changes; the user's in-flight edits are re-applied as ops on top so
  // typing during a save/poll round-trip is never lost. Advances the confirmed baseline
  // (savedBodyRef) and the rev. Reference-equality is preserved in the common no-change
  // case so the Unsaved indicator stays accurate and the cursor isn't disturbed.
  const adoptServerBody = (serverBody, baseBody, newRev, notesDirty) => {
    const pendingOps = diffToOps(baseBody, draftBodyRef.current);
    // Did anyone else change content? Compare by id + cell value (NOT JSON.stringify —
    // Postgres JSONB doesn't preserve key order, so a stringify compare false-positives
    // on every save). An empty diff means the server body matches what we based our edits
    // on, so we can keep the current draft reference (clean) instead of rebuilding it.
    const foreign = diffToOps(baseBody, serverBody).length > 0;
    if (foreign) {
      // Someone else changed cells/rows: rebuild the draft from the server body with our
      // pending edits replayed. With no pending edits we adopt the server body verbatim
      // (same reference → clean), otherwise reconcile.
      setDraftBody(pendingOps.length ? applyOps(serverBody, pendingOps) : serverBody);
      savedBodyRef.current = serverBody;
    } else {
      // Server matched what we based our edits on. Keep the draft reference; only the
      // baseline advances. If the user kept typing, those edits stay dirty.
      savedBodyRef.current = pendingOps.length ? serverBody : draftBodyRef.current;
    }
    if (typeof newRev === 'number') revRef.current = newRev;
    setIsDirty(pendingOps.length > 0 || !!notesDirty);
  };

  // P2: saves a *diff* of operations (setCell/insert/delete/move by stable row id) against
  // the last-confirmed body, so concurrent edits to different cells/rows merge instead of
  // clobbering. The server returns the merged body, which we reconcile via adoptServerBody.
  // Format changes (and the first save before a baseline exists) can't be expressed as ops
  // — they fall back to the legacy whole-body PATCH, which the server still accepts during
  // the cutover grace period.
  const saveBody = async (manual = false) => {
    if (!selected || !draftBody) return;
    // Permission MUST mirror canEditBody (and the backend gate): any Writer may save any
    // script (team policy), not only its owner. The previous guard omitted plain 'Writer',
    // so a non-owner writer could edit the grid but every save silently no-op'd — a silent
    // data-loss path. Keep this in sync with canEditBody.
    const isShootLeadEditing = location.state?.isShootLeadForScript && (hasRole('Producer'));
    const mayEdit = hasRole('Admin', 'Manager', 'Power User', 'Qual Reviewer', 'Writer')
                 || selected?.writer?.id === user?.id || isShootLeadEditing;
    if (!mayEdit) return;
    if (saveRetryTimer.current) { clearTimeout(saveRetryTimer.current); saveRetryTimer.current = null; }
    // Snapshot exactly what we're sending so the "confirmed" mark is for THIS content,
    // even if the user keeps typing while the request is in flight.
    const bodyAtSave   = draftBody;
    const notesAtSave  = draftNotes;
    const baseBody     = savedBodyRef.current;
    const notesChanged = notesAtSave !== savedNotesRef.current;
    const useLegacy    = !baseBody || baseBody.format !== bodyAtSave.format;

    if (!useLegacy) {
      const ops = diffToOps(baseBody, bodyAtSave);
      if (ops.length === 0 && !notesChanged) return; // nothing to persist
      setSaving(true);
      try {
        const res = await api.patch(`/api/scripts/${selected.id}/ops`, {
          baseRev: revRef.current,
          ops,
          ...(notesChanged && { qualNotes: notesAtSave }),
          saveType: manual ? 'manual' : 'autosave',
        });
        setLastSaved(new Date());
        setSaveError(null);
        saveRetryRef.current = 0;
        if (notesChanged) savedNotesRef.current = notesAtSave;
        adoptServerBody(res.data.scriptBody || bodyAtSave, bodyAtSave, res.data.rev,
                        draftNotesRef.current !== savedNotesRef.current);
        if (bodyAtSave?.format === 'Longform') setLfEpisodesVersion(v => v + 1);
        // Same-cell collisions: another editor changed a cell you also changed. LWW is
        // already applied; surface it so the rare silent same-cell loss is visible.
        const sameCell = (res.data.conflicts || []).filter(c => c.type === 'sameCell');
        if (sameCell.length) setCellConflicts(prev => [...prev.slice(-19), ...sameCell]);
        if (manual && activityOpen) {
          const logRes = await api.get(`/api/audit/script/${selected.id}`);
          setActivityLog(logRes.data);
        }
      } catch (err) { handleSaveError(err, manual); }
      finally { setSaving(false); }
      return;
    }

    // Legacy whole-body save (format change / no baseline yet).
    setSaving(true);
    try {
      const res = await api.patch(`/api/scripts/${selected.id}`, {
        scriptBody: bodyAtSave,
        qualNotes:  notesAtSave,
        ...(manual && { saveType: 'manual' }),
      });
      setLastSaved(new Date());
      setSaveError(null);
      saveRetryRef.current  = 0;
      savedBodyRef.current  = bodyAtSave;
      savedNotesRef.current = notesAtSave;
      if (typeof res.data?.rev === 'number') revRef.current = res.data.rev;
      setIsDirty(draftBodyRef.current !== bodyAtSave || draftNotesRef.current !== notesAtSave);
      if (manual && activityOpen) {
        const logRes = await api.get(`/api/audit/script/${selected.id}`);
        setActivityLog(logRes.data);
      }
    } catch (err) { handleSaveError(err, manual); }
    finally { setSaving(false); }
  };

  // Force-apply a save the shrink guard blocked. Only reachable from an explicit
  // user click on "Save anyway", so confirmShrink is intentional.
  const saveAnyway = async () => {
    if (!selected || !draftBody) return;
    const bodyAtSave   = draftBody;
    const notesAtSave  = draftNotes;
    const baseBody     = savedBodyRef.current;
    const notesChanged = notesAtSave !== savedNotesRef.current;
    const useLegacy    = !baseBody || baseBody.format !== bodyAtSave.format;
    setSaving(true);
    try {
      if (useLegacy) {
        const res = await api.patch(`/api/scripts/${selected.id}`, {
          scriptBody: bodyAtSave, qualNotes: notesAtSave, saveType: 'manual', confirmShrink: true,
        });
        savedBodyRef.current  = bodyAtSave;
        savedNotesRef.current = notesAtSave;
        if (typeof res.data?.rev === 'number') revRef.current = res.data.rev;
        setIsDirty(draftBodyRef.current !== bodyAtSave || draftNotesRef.current !== notesAtSave);
      } else {
        const ops = diffToOps(baseBody, bodyAtSave);
        const res = await api.patch(`/api/scripts/${selected.id}/ops`, {
          baseRev: revRef.current, ops,
          ...(notesChanged && { qualNotes: notesAtSave }),
          saveType: 'manual', confirmShrink: true,
        });
        if (notesChanged) savedNotesRef.current = notesAtSave;
        adoptServerBody(res.data.scriptBody || bodyAtSave, bodyAtSave, res.data.rev,
                        draftNotesRef.current !== savedNotesRef.current);
      }
      if (bodyAtSave?.format === 'Longform') setLfEpisodesVersion(v => v + 1);
      setLastSaved(new Date());
      setSaveError(null);
    } catch (err) {
      console.error('Save failed:', err);
      setSaveError({ tooLarge: err?.response?.status === 413 });
    } finally {
      setSaving(false);
    }
  };

  const pushUndo = (body) => {
    if (!body) return;
    const stack = undoStack.current;
    // setDraftBody always creates a new reference, so referential equality is the
    // correct dedup signal. (Previous version JSON.stringify'd both bodies — for a
    // 500-row script that's two ~250 KB serializations per call, called on every
    // addRow / paste / drag-reposition.)
    if (stack.length > 0 && stack[stack.length - 1] === body) return;
    undoStack.current = [...stack.slice(-49), body];
    // Any new mutation invalidates the redo branch — standard editor semantics.
    if (redoStack.current.length) redoStack.current = [];
  };

  // Keep refs current so the document keydown handler always calls the latest version.
  handleUndoRef.current = () => {
    if (undoStack.current.length === 0) return;
    const prev = undoStack.current[undoStack.current.length - 1];
    undoStack.current = undoStack.current.slice(0, -1);
    if (draftBody) redoStack.current = [...redoStack.current.slice(-49), draftBody];
    setDraftBody(prev);
  };
  handleRedoRef.current = () => {
    if (redoStack.current.length === 0) return;
    const next = redoStack.current[redoStack.current.length - 1];
    redoStack.current = redoStack.current.slice(0, -1);
    if (draftBody) undoStack.current = [...undoStack.current.slice(-49), draftBody];
    setDraftBody(next);
  };

  const updateScene = (idx, key, value) => {
    const scenes = [...(draftBody?.scenes || [])];
    scenes[idx] = { ...scenes[idx], [key]: value };
    setDraftBody({ ...draftBody, scenes });
  };

  // Phase B1: cell edit-mode helpers. Always set focusedCell too so the active
  // border (spec 1.2) tracks the editing cell. `mode` is read by the textarea
  // on mount to decide selection placement: 'cursor-end' (default), 'select-all'
  // (next keystroke overwrites), 'cursor-start'.
  const enterEdit = (rowIdx, colKey, mode = 'cursor-end') => {
    const cur = draftBodyRef.current?.scenes?.[rowIdx]?.[colKey] || '';
    setFocusedCell({ rowIdx, colKey });
    setEditingCell({ rowIdx, colKey, mode, originalValue: cur });
    // Entering edit on a single cell ends any multi-cell range — the row's
    // selectedColRange will recompute to '' next render.
    setCellSelection(null);
    if (cellMultiSelection.size) setCellMultiSelection(new Set());
  };
  const exitEdit = () => { setEditingCell(null); };

  // Find/Replace navigation helper: brings the matched cell into edit mode
  // (so it becomes a textarea where text can actually be selected) and sets
  // the selection range to the match. If the cell is already being edited,
  // just updates the selection — doesn't re-capture originalValue and stomp
  // on prior replacements.
  const focusFindMatch = (rowIdx, colKey, start, end) => {
    const cur = editingCellRef.current;
    if (!cur || cur.rowIdx !== rowIdx || cur.colKey !== colKey) {
      enterEdit(rowIdx, colKey, 'cursor-end');
    }
    requestAnimationFrame(() => {
      const el = document.querySelector(`tr[data-row-idx="${rowIdx}"] textarea[data-col="${colKey}"]`);
      if (!el) return;
      try { el.setSelectionRange(start, end); } catch { /* not selectable */ }
    });
  };
  const revertEdit = () => {
    const e = editingCellRef.current;
    if (!e) return;
    const body = draftBodyRef.current;
    if (body) {
      const scenes = [...(body.scenes || [])];
      scenes[e.rowIdx] = { ...scenes[e.rowIdx], [e.colKey]: e.originalValue };
      setDraftBody({ ...body, scenes });
    }
    setEditingCell(null);
  };

  // Longform line flow (D-117 → D-124, 2026-09-18): the row appended by Enter / Ctrl+Enter / Tab at
  // the end of the grid (the Skit editor's row-advance, unchanged) inherits the scene number of the
  // nearest numbered row above — mid-episode. From an EPISODE BREAK row the advance STARTS THE NEXT
  // EPISODE: the new dialogue row is seeded with scene 1 (scene numbers restart per episode, D-123).
  // The footer "Add scene row" and Tab-append go through the same addRow, so they inherit alike.
  // Every other format gets the plain empty row exactly as before.
  const longformInheritedScene = (scenes) => lfe.inheritedSceneForAppend(scenes);
  const addRow = () => {
    pushUndo(draftBody);
    const scenes = draftBody?.scenes || [];
    const row = draftBody?.format === 'Longform'
      ? { ...emptyRow('Longform'), sceneNumber: longformInheritedScene(scenes) }
      : emptyRow(draftBody?.format);
    setDraftBody({ ...draftBody, scenes: [...scenes, row] });
  };
  // Longform scene flow (D-118): append a scene's FIRST dialogue row after the last row —
  // scene # given (or auto-next), its description in Scene Description — and land in the
  // Character cell in the same commit (pendingFocusRef, exactly like Enter-at-end). The
  // row goes through the normal draft → autosave path, so D-110 character sync sees it.
  // D-123: "next scene" = the CURRENT episode's max scene + 1 (the rows after the last break), or 1.
  const longformNextSceneNumber = (scenes) => lfe.nextSceneNumber(scenes);
  const appendSceneRow = (sceneNumber, description = '') => {
    const body = draftBodyRef.current || draftBody;
    if (!body) return;
    pushUndo(body);
    const scenes = body.scenes || [];
    const n = Number.isFinite(Number(sceneNumber)) && Number(sceneNumber) > 0 ? String(Number(sceneNumber)) : String(longformNextSceneNumber(scenes));
    const row = { ...emptyRow('Longform'), sceneNumber: n, scene: String(description || '').trim() };
    pendingFocusRef.current = { rowIdx: scenes.length, colKey: 'character', scroll: true };
    setDraftBody({ ...body, format: 'Longform', scenes: [...scenes, row] });
  };
  // D-126 (2026-09-18): episodes are AUTHORED IN THE SCRIPT. "＋ Add episode" appends an episode header
  // row ("EPISODE n", a direction/open-marker row the parser and lib/longformEpisodes.js both read) and
  // the episode's first row seeded with scene 1, cursor in Character — on a blank series this is
  // Episode 1 ("Start writing"). The episode's card is minted from the script by the server sync
  // (approval / body save while Approved), never by this button.
  const addEpisodeToScript = (title = '') => {
    const body = draftBodyRef.current || draftBody;
    if (!body) return;
    pushUndo(body);
    const scenes = body.scenes || [];
    const n = lfe.episodeBlocks(scenes).length + 1;
    const header = { ...emptyRow('Longform'), kind: 'direction', character: '', line: lfe.episodeHeaderLine(n, title), sceneNumber: '' };
    const first = { ...emptyRow('Longform'), sceneNumber: '1' };
    pendingFocusRef.current = { rowIdx: scenes.length + 1, colKey: 'character', scroll: true };
    setDraftBody({ ...body, format: 'Longform', scenes: [...scenes, header, first] });
  };
  const startWritingScene1 = () => addEpisodeToScript('');
  // Rename an episode IN THE SCRIPT: rewrite its header row ("EPISODE k — Title"), or insert one at the
  // top of its block when the episode was opened by a plain EPISODE BREAK. The card's title follows on save.
  const setEpisodeTitleInScript = (k, title) => {
    const body = draftBodyRef.current || draftBody;
    if (!body) return;
    const scenes = [...(body.scenes || [])];
    const b = lfe.episodeBlocks(scenes)[k - 1];
    if (!b) return;
    pushUndo(body);
    const line = lfe.episodeHeaderLine(k, title);
    if (lfe.isOpenMarker(scenes[b.start])) scenes[b.start] = { ...scenes[b.start], line };
    else scenes.splice(b.start, 0, { ...emptyRow('Longform'), kind: 'direction', character: '', line, sceneNumber: '' });
    setDraftBody({ ...body, format: 'Longform', scenes });
  };
  // "Add scene" in the Scene breakdown saved its breakdown rows server-side and reloaded the
  // script; once the reload has committed, append the scene's first dialogue row.
  const handleLongformSceneAdded = (sceneNumber, description) => {
    const n = Number(sceneNumber);
    pendingSceneAppendRef.current = { scriptId: selected?.id, sceneNumber: Number.isFinite(n) && n > 0 ? n : longformNextSceneNumber(draftBodyRef.current?.scenes), description };
  };



  const moveRow = (idx, dir, colKey) => {
    const scenes = [...(draftBody?.scenes || [])];
    const target = idx + dir;
    if (target < 0 || target >= scenes.length) return;
    pushUndo(draftBody);
    [scenes[idx], scenes[target]] = [scenes[target], scenes[idx]];
    setDraftBody({ ...draftBody, scenes });
    setHoveredRow(target);
    // Phase 2.7: clear selection after any row move.
    setSelectedRows(new Set());
    setSelectionAnchor(null);
    // After React re-renders, re-focus the same column in the row's new position
    if (colKey) {
      setTimeout(() => {
        const el = document.querySelector(`[data-row="${target}"][data-col="${colKey}"]`);
        if (el) el.focus();
      }, 0);
    }
  };

  const handlePrint = () => {
    const printCols = displayCols(format, showProdNotesCol);
    const allScenes = draftBody?.scenes || [];
    const scenes    = allScenes.filter(row => printCols.some(c => (row[c.key] || '').trim()));
    const esc = s => (s || '').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');

    const headerCells = `<th style="width:32px">#</th>` +
      printCols.map((c, ci) => `<th style="width:${renderedColPcts[ci]}%">${esc(c.label)}</th>`).join('');

    const bodyRows = scenes.map((row, i) => {
      const rowBg = row._rowColor ? `background:${row._rowColor};` : '';
      const cells = printCols.map(c => {
        const cellBg = row._cellColors?.[c.key] ? `background:${row._cellColors[c.key]};` : '';
        const val  = renderMarkdown(esc(row[c.key] || '')).replace(/\n/g, '<br>');
        const link = row._links?.[c.key];
        const linkHtml = link?.url ? `<br><a href="${link.url}" style="color:inherit;text-decoration:underline;font-size:0.85em">${esc(link.text || link.url)}</a>` : '';
        return `<td style="border:1px solid #ddd;padding:6px 8px;vertical-align:top;line-height:1.5;word-break:break-word;overflow-wrap:break-word;${cellBg}">${val}${linkHtml}</td>`;
      }).join('');
      return `<tr style="break-inside:avoid;${rowBg}">
        <td style="border:1px solid #ddd;padding:6px 8px;text-align:center;color:#888;font-size:0.85em;background:#f8f8f8;width:32px;">${i + 1}</td>
        ${cells}
      </tr>`;
    }).join('');

    const html = `<!DOCTYPE html><html><head><meta charset="utf-8">
<title>${esc(selected.scriptName)}</title>
<style>
  *{box-sizing:border-box;margin:0;padding:0}
  body{font-family:Arial,sans-serif;font-size:${editorFontSize}px;color:#111;background:#fff;padding:28px 32px}
  h1{font-size:1.4em;font-weight:700;color:#111;padding-bottom:10px;margin-bottom:16px;border-bottom:2px solid #222;letter-spacing:0.01em}
  *{-webkit-print-color-adjust:exact;print-color-adjust:exact}
  table{width:100%;border-collapse:collapse;table-layout:fixed}
  thead tr{background:#f0f0f0}
  th{background:#f0f0f0;color:#333;border:1px solid #ccc;padding:6px 8px;font-size:0.9em;font-weight:600;text-align:left;word-break:break-word;overflow-wrap:break-word}
  td,th{word-break:break-word;overflow-wrap:break-word}
  #print-bar{position:fixed;top:0;left:0;right:0;background:#f5f5f5;border-bottom:1px solid #ddd;padding:8px 20px;display:flex;align-items:center;gap:14px;font-family:Arial,sans-serif;font-size:12px;z-index:999}
  #print-bar button{background:#333;color:#fff;border:none;padding:6px 14px;border-radius:4px;font-size:12px;cursor:pointer}
  #print-bar span{color:#888}
  #content{margin-top:48px}
  @media print{#print-bar{display:none!important}#content{margin-top:0}}
</style>
</head><body>
<div id="print-bar">
  <button onclick="window.print()">&#9113; Print / Save as PDF</button>
  <span>or press Ctrl+P</span>
</div>
<div id="content">
<h1>${esc(selected.scriptName)}</h1>
${printCols.length > 0 ? `<table><thead><tr>${headerCells}</tr></thead><tbody>${bodyRows}</tbody></table>` : '<p style="color:#666;font-size:12px">No scene table for this script format.</p>'}
</div>
</body></html>`;

    const w = window.open('', '_blank', 'width=900,height=700');
    if (!w) { toast('The print window was blocked. Allow pop-ups for this site and try again.', { tone: 'danger' }); return; }
    w.document.write(html);
    w.document.close();
  };

  // Picks the row targets for a color operation: if the clicked row is part
  // of the active multi-row selection, color every selected row; otherwise
  // just the clicked row.
  const rowColorTargets = (idx) =>
    (selectedRows.has(idx) && selectedRows.size > 1) ? [...selectedRows] : [idx];

  const applyRowColorToIndices = (indices, color) => {
    if (!indices.length) return;
    pushUndo(draftBody);
    const scenes = [...(draftBody?.scenes || [])];
    for (const i of indices) {
      if (scenes[i]) scenes[i] = { ...scenes[i], _rowColor: color };
    }
    setDraftBody({ ...draftBody, scenes });
    setColorPickerRow(null);
  };

  const updateRowColor = (idx, color) => {
    applyRowColorToIndices(rowColorTargets(idx), color);
  };

  // Picks the cell targets for a color operation:
  //   - If the clicked cell is part of the non-contiguous cellMultiSelection,
  //     color every cell in that set.
  //   - Else if the active rectangular cellSelection (Phase B2) covers the
  //     clicked cell and isn't a 1x1, color every cell in the rectangle.
  //   - Else just the clicked cell.
  const cellColorTargets = (idx, colKey) => {
    const colIdx = cols.findIndex(c => c.key === colKey);
    const clickedId = `${idx}:${colKey}`;
    if (cellMultiSelection.size > 0 && cellMultiSelection.has(clickedId)) {
      return [...cellMultiSelection].map(id => {
        const sep = id.indexOf(':');
        return { rowIdx: Number(id.slice(0, sep)), colKey: id.slice(sep + 1) };
      });
    }
    const cs = cellSelection;
    if (cs && (cs.startRow !== cs.endRow || cs.startCol !== cs.endCol)) {
      const r1 = Math.min(cs.startRow, cs.endRow);
      const r2 = Math.max(cs.startRow, cs.endRow);
      const c1 = Math.min(cs.startCol, cs.endCol);
      const c2 = Math.max(cs.startCol, cs.endCol);
      if (idx >= r1 && idx <= r2 && colIdx >= c1 && colIdx <= c2) {
        const out = [];
        for (let r = r1; r <= r2; r++) {
          for (let c = c1; c <= c2; c++) out.push({ rowIdx: r, colKey: cols[c].key });
        }
        return out;
      }
    }
    return [{ rowIdx: idx, colKey }];
  };

  const applyCellColorToTargets = (targets, color) => {
    if (!targets.length) return;
    pushUndo(draftBody);
    const scenes = [...(draftBody?.scenes || [])];
    const byRow = new Map();
    for (const t of targets) {
      if (!byRow.has(t.rowIdx)) byRow.set(t.rowIdx, []);
      byRow.get(t.rowIdx).push(t.colKey);
    }
    for (const [rowIdx, keys] of byRow) {
      if (!scenes[rowIdx]) continue;
      const cc = { ...(scenes[rowIdx]._cellColors || {}) };
      for (const k of keys) cc[k] = color;
      scenes[rowIdx] = { ...scenes[rowIdx], _cellColors: cc };
    }
    setDraftBody({ ...draftBody, scenes });
    setColorPickerCell(null);
  };

  const updateCellColor = (idx, colKey, color) => {
    applyCellColorToTargets(cellColorTargets(idx, colKey), color);
  };


  // Phase 2 selection helpers — gutter-driven row selection (Google-Sheets style).

  const selectRowOnly = (idx) => {
    setSelectedRows(new Set([idx]));
    setSelectionAnchor(idx);
  };

  const selectRowRange = (anchorIdx, idx) => {
    if (anchorIdx == null) { selectRowOnly(idx); return; }
    const [lo, hi] = anchorIdx < idx ? [anchorIdx, idx] : [idx, anchorIdx];
    const next = new Set();
    for (let i = lo; i <= hi; i++) next.add(i);
    setSelectedRows(next);
    // Anchor stays at original anchor — matches Sheets shift-click behavior
  };

  const toggleRowInSelection = (idx) => {
    setSelectedRows(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return next;
    });
    setSelectionAnchor(idx);
  };

  // Phase 4: row clipboard helpers (copy / cut / paste / duplicate).
  const cloneRows = (indices) =>
    indices.map(i => JSON.parse(JSON.stringify(draftBody.scenes[i])));

  // Short confirmations of a copy / cut / paste (was a floating chip; the system toast now).
  const flashCopyFeedback = (msg, ms = 1500) => {
    toast(msg, { tone: 'info', duration: Math.max(ms, 2500) });
  };

  const copySelectedRows = () => {
    if (selectedRows.size === 0) return;
    const sorted = [...selectedRows].sort((a, b) => a - b);
    rowClipboardRef.current = { rows: cloneRows(sorted), wasCut: false };
    setCutRows(new Set());
    writeRowsToAppClip(sorted);   // keep the cross-script app clipboard in sync
    flashCopyFeedback(`Copied ${sorted.length} row${sorted.length !== 1 ? 's' : ''}`);
  };

  const cutSelectedRows = () => {
    if (selectedRows.size === 0) return;
    const sorted = [...selectedRows].sort((a, b) => a - b);
    rowClipboardRef.current = { rows: cloneRows(sorted), wasCut: true, cutIndices: sorted };
    setCutRows(new Set(sorted));
    // Clear the selection so the user can pick a paste destination.
    setSelectedRows(new Set());
    setSelectionAnchor(null);
    flashCopyFeedback(`Cut ${sorted.length} row${sorted.length !== 1 ? 's' : ''}. Paste to move ${sorted.length !== 1 ? 'them' : 'it'}.`, 2200);
  };

  const pasteRows = () => {
    const clip = rowClipboardRef.current;
    if (!clip || !clip.rows.length) return;
    pushUndo(draftBody);
    const scenes = [...(draftBody?.scenes || [])];
    // Destination: above the first selected row, or end of script if no selection.
    let insertIdx = selectedRows.size > 0 ? Math.min(...selectedRows) : scenes.length;

    if (clip.wasCut && Array.isArray(clip.cutIndices)) {
      // Remove cut rows from their original positions (highest first to avoid index shift).
      const cutSorted = [...clip.cutIndices].sort((a, b) => b - a);
      for (const i of cutSorted) scenes.splice(i, 1);
      // Adjust insertIdx for any cut rows that were before the destination.
      const cutsBefore = clip.cutIndices.filter(i => i < insertIdx).length;
      insertIdx -= cutsBefore;
      if (insertIdx < 0) insertIdx = 0;
      if (insertIdx > scenes.length) insertIdx = scenes.length;
    }

    // Clone again so repeated pastes (copy mode) don't share references.
    const fresh = clip.rows.map(r => JSON.parse(JSON.stringify(r)));
    scenes.splice(insertIdx, 0, ...fresh);
    setDraftBody({ ...draftBody, scenes });

    // Newly inserted rows become the selection so the user can see where they landed.
    const newSelection = new Set();
    for (let i = 0; i < fresh.length; i++) newSelection.add(insertIdx + i);
    setSelectedRows(newSelection);
    setSelectionAnchor(insertIdx);

    if (clip.wasCut) {
      rowClipboardRef.current = null;
      setCutRows(new Set());
    }
  };

  const duplicateSelectedRows = () => {
    if (selectedRows.size === 0) return;
    pushUndo(draftBody);
    const sorted = [...selectedRows].sort((a, b) => a - b);
    const dupes = cloneRows(sorted);
    const insertIdx = sorted[sorted.length - 1] + 1;
    const scenes = [...(draftBody?.scenes || [])];
    scenes.splice(insertIdx, 0, ...dupes);
    setDraftBody({ ...draftBody, scenes });
    const newSelection = new Set();
    for (let i = 0; i < dupes.length; i++) newSelection.add(insertIdx + i);
    setSelectedRows(newSelection);
    setSelectionAnchor(insertIdx);
  };

  // Phase 5 helpers — context-menu actions operate on explicit indices instead of
  // current selection state (avoids stale-state issues when right-clicking outside
  // the existing selection).
  // D-094 (Longform): tag rows as DIRECTION (gold; never a character; Scene Scrub
  // treats them as non-speaking) or back to dialogue. A direction row keeps its
  // text in Line and drops the Character.
  const setRowsKind = (indices, kind) => {
    pushUndo(draftBody);
    const scenes = [...(draftBody?.scenes || [])];
    for (const i of indices) {
      if (!scenes[i]) continue;
      const { kind: _k, ...rest } = scenes[i];
      scenes[i] = kind ? { ...rest, kind, character: '' } : rest;
    }
    setDraftBody({ ...draftBody, scenes });
  };
  const insertRowAt = (idx, count = 1) => {
    pushUndo(draftBody);
    const scenes = [...(draftBody?.scenes || [])];
    const safeIdx = Math.max(0, Math.min(idx, scenes.length));
    const blanks = Array.from({ length: Math.max(1, count) }, () => emptyRow(draftBody?.format));
    scenes.splice(safeIdx, 0, ...blanks);
    setDraftBody({ ...draftBody, scenes });
    setSelectedRows(new Set());
    setSelectionAnchor(null);
  };
  // D-112 (Longform): insert an EPISODE BREAK direction row at `idx` (end of the
  // script when idx is past the end). The row is exactly what the PDF/paste parser
  // emits and what /episodes/from-breaks recognises: kind 'direction', line
  // "EPISODE BREAK", scene number = the scene it closes (the row just above).
  const insertEpisodeBreakAt = (idx) => {
    pushUndo(draftBody);
    const scenes = [...(draftBody?.scenes || [])];
    const safeIdx = Math.max(0, Math.min(idx == null ? scenes.length : idx, scenes.length));
    const above = [...scenes.slice(0, safeIdx)].reverse().find(r => String(r?.sceneNumber ?? '').trim() !== '');
    const row = { ...emptyRow(draftBody?.format), kind: 'direction', character: '', line: 'EPISODE BREAK', sceneNumber: above ? String(above.sceneNumber).trim() : '' };
    scenes.splice(safeIdx, 0, row);
    setDraftBody({ ...draftBody, scenes });
    setSelectedRows(new Set());
    setSelectionAnchor(null);
  };

  const deleteRowsByIndices = (indices) => {
    if (!indices.length) return;
    pushUndo(draftBody);
    const scenes = [...(draftBody?.scenes || [])];
    const sortedDesc = [...indices].sort((a, b) => b - a);
    for (const i of sortedDesc) scenes.splice(i, 1);
    // Keep at least one row so the editor remains usable.
    if (scenes.length === 0) scenes.push(emptyRow(draftBody?.format));
    setDraftBody({ ...draftBody, scenes });
    setSelectedRows(new Set());
    setSelectionAnchor(null);
    setHoveredRow(null);
    setCutRows(new Set());
  };

  const duplicateRowsByIndices = (indices) => {
    if (!indices.length) return;
    pushUndo(draftBody);
    const sorted = [...indices].sort((a, b) => a - b);
    const dupes = sorted.map(i => JSON.parse(JSON.stringify(draftBody.scenes[i])));
    const insertIdx = sorted[sorted.length - 1] + 1;
    const scenes = [...(draftBody?.scenes || [])];
    scenes.splice(insertIdx, 0, ...dupes);
    setDraftBody({ ...draftBody, scenes });
    const newSelection = new Set();
    for (let i = 0; i < dupes.length; i++) newSelection.add(insertIdx + i);
    setSelectedRows(newSelection);
    setSelectionAnchor(insertIdx);
  };

  const copyRowsByIndices = (indices) => {
    if (!indices.length) return;
    const sorted = [...indices].sort((a, b) => a - b);
    rowClipboardRef.current = {
      rows: sorted.map(i => JSON.parse(JSON.stringify(draftBody.scenes[i]))),
      wasCut: false,
    };
    setCutRows(new Set());
    writeRowsToAppClip(sorted);   // keep the cross-script app clipboard in sync
    flashCopyFeedback(`Copied ${sorted.length} row${sorted.length !== 1 ? 's' : ''}`);
  };

  const cutRowsByIndices = (indices) => {
    if (!indices.length) return;
    const sorted = [...indices].sort((a, b) => a - b);
    rowClipboardRef.current = {
      rows: sorted.map(i => JSON.parse(JSON.stringify(draftBody.scenes[i]))),
      wasCut: true,
      cutIndices: sorted,
    };
    setCutRows(new Set(sorted));
    setSelectedRows(new Set());
    setSelectionAnchor(null);
    flashCopyFeedback(`Cut ${sorted.length} row${sorted.length !== 1 ? 's' : ''}. Paste to move ${sorted.length !== 1 ? 'them' : 'it'}.`, 2200);
  };

  // Paste from the right-click menu: insert above the highest-priority target row.
  const pasteAboveRow = (idx) => {
    const clip = rowClipboardRef.current;
    if (!clip || !clip.rows.length) return;
    pushUndo(draftBody);
    const scenes = [...(draftBody?.scenes || [])];
    let insertIdx = idx;
    if (clip.wasCut && Array.isArray(clip.cutIndices)) {
      const cutSorted = [...clip.cutIndices].sort((a, b) => b - a);
      for (const i of cutSorted) scenes.splice(i, 1);
      const cutsBefore = clip.cutIndices.filter(i => i < insertIdx).length;
      insertIdx -= cutsBefore;
      if (insertIdx < 0) insertIdx = 0;
      if (insertIdx > scenes.length) insertIdx = scenes.length;
    }
    const fresh = clip.rows.map(r => JSON.parse(JSON.stringify(r)));
    scenes.splice(insertIdx, 0, ...fresh);
    setDraftBody({ ...draftBody, scenes });
    const newSelection = new Set();
    for (let i = 0; i < fresh.length; i++) newSelection.add(insertIdx + i);
    setSelectedRows(newSelection);
    setSelectionAnchor(insertIdx);
    if (clip.wasCut) {
      rowClipboardRef.current = null;
      setCutRows(new Set());
    }
  };

  // Phase 3: move the currently selected rows to a new position.
  // targetGapIdx is a value in [0..scenes.length]; rows are inserted at that gap,
  // becoming contiguous regardless of whether sources were contiguous.
  const repositionSelectedTo = (targetGapIdx) => {
    if (selectedRows.size === 0) return;
    const scenes = [...(draftBody?.scenes || [])];
    const sortedSources = [...selectedRows].sort((a, b) => a - b);
    // No-op if dropping into the gap that's already where the (contiguous) block sits.
    const firstSrc = sortedSources[0];
    const lastSrc  = sortedSources[sortedSources.length - 1];
    const contiguous = lastSrc - firstSrc + 1 === sortedSources.length;
    if (contiguous && (targetGapIdx === firstSrc || targetGapIdx === lastSrc + 1)) {
      setSelectedRows(new Set());
      setSelectionAnchor(null);
      return;
    }
    pushUndo(draftBody);
    // Snapshot the source rows in their current order, then remove from highest idx down.
    const moved = sortedSources.map(i => scenes[i]);
    for (let i = sortedSources.length - 1; i >= 0; i--) {
      scenes.splice(sortedSources[i], 1);
    }
    // Shift the target left by however many source rows were before it.
    const sourcesBefore = sortedSources.filter(i => i < targetGapIdx).length;
    const adjustedTarget = targetGapIdx - sourcesBefore;
    scenes.splice(adjustedTarget, 0, ...moved);
    setDraftBody({ ...draftBody, scenes });
    // Spec 2.6 / 2.7: selection clears immediately after the drop.
    setSelectedRows(new Set());
    setSelectionAnchor(null);
  };

  // ── Drag-select edge auto-scroll ─────────────────────────────────────────
  // ONE shared implementation used by BOTH selection drags (gutter row drag
  // and cell range drag). While a drag is live, every mousemove feeds the last
  // pointer position here; a rAF loop scrolls `.script-scroll-body` whenever
  // that position sits in an edge hot zone, then re-resolves the row/cell now
  // under the stationary pointer and extends the selection (mousemove does NOT
  // fire when only the page scrolls, so the loop must do the re-resolving).
  // Move/fill drags (reposition, autofill, move-drag) never call this.
  const autoScrollRef = useRef(null); // { raf, lastX, lastY, horizontal, resolveAt, teardown } | null

  const stopEdgeAutoScroll = () => {
    const s = autoScrollRef.current;
    if (!s) return;
    autoScrollRef.current = null;
    if (s.raf != null) cancelAnimationFrame(s.raf);
    s.teardown();
  };

  // Called from a selection drag's mousemove. Starts the rAF loop on first call
  // and refreshes the stored pointer position on every subsequent one. Stopping
  // is handled by mouseup (in each drag's own teardown) plus Escape, window
  // blur, and the pointer leaving the document; a later in-document mousemove
  // restarts the loop if the drag is still live.
  const ensureEdgeAutoScroll = (x, y, { horizontal = false, resolveAt }) => {
    const existing = autoScrollRef.current;
    if (existing) {
      existing.lastX = x; existing.lastY = y; existing.resolveAt = resolveAt;
      return;
    }
    const scroller = document.querySelector('.script-scroll-body');
    if (!scroller) return;
    const state = { raf: null, lastX: x, lastY: y, horizontal, resolveAt, teardown: () => {} };

    const onKey = (e) => { if (e.key === 'Escape') stopEdgeAutoScroll(); };
    const onBlur = () => stopEdgeAutoScroll();
    const onDocLeave = () => stopEdgeAutoScroll();
    document.addEventListener('keydown', onKey, true);
    window.addEventListener('blur', onBlur);
    document.documentElement.addEventListener('mouseleave', onDocLeave);
    state.teardown = () => {
      document.removeEventListener('keydown', onKey, true);
      window.removeEventListener('blur', onBlur);
      document.documentElement.removeEventListener('mouseleave', onDocLeave);
    };

    const step = () => {
      if (autoScrollRef.current !== state) return; // stopped or replaced
      const rect = scroller.getBoundingClientRect();
      // The sticky column header overlays the top of the scroll area — the top
      // zone starts below it, over rows the user can actually see.
      const headH = scroller.querySelector('.scene-head')?.getBoundingClientRect().height || 0;
      const topEdge = rect.top + headH;
      let dy = 0, dx = 0;
      if (state.lastY > rect.bottom - AUTOSCROLL_EDGE_PX) {
        dy = Math.min(AUTOSCROLL_MAX_STEP_PX,
          ((state.lastY - (rect.bottom - AUTOSCROLL_EDGE_PX)) / AUTOSCROLL_EDGE_PX) * AUTOSCROLL_MAX_STEP_PX);
      } else if (state.lastY < topEdge + AUTOSCROLL_EDGE_PX) {
        dy = -Math.min(AUTOSCROLL_MAX_STEP_PX,
          (((topEdge + AUTOSCROLL_EDGE_PX) - state.lastY) / AUTOSCROLL_EDGE_PX) * AUTOSCROLL_MAX_STEP_PX);
      }
      // Horizontal zones: cell-range drags only, and only when the container
      // actually scrolls sideways (today it does not — columns are %-width).
      if (state.horizontal && scroller.scrollWidth > scroller.clientWidth) {
        if (state.lastX > rect.right - AUTOSCROLL_EDGE_PX) {
          dx = Math.min(AUTOSCROLL_MAX_STEP_PX,
            ((state.lastX - (rect.right - AUTOSCROLL_EDGE_PX)) / AUTOSCROLL_EDGE_PX) * AUTOSCROLL_MAX_STEP_PX);
        } else if (state.lastX < rect.left + AUTOSCROLL_EDGE_PX) {
          dx = -Math.min(AUTOSCROLL_MAX_STEP_PX,
            (((rect.left + AUTOSCROLL_EDGE_PX) - state.lastX) / AUTOSCROLL_EDGE_PX) * AUTOSCROLL_MAX_STEP_PX);
        }
      }
      if (dy || dx) {
        const beforeTop = scroller.scrollTop, beforeLeft = scroller.scrollLeft;
        if (dy) scroller.scrollTop = beforeTop + dy;
        if (dx) scroller.scrollLeft = beforeLeft + dx;
        // Re-resolve only when the view actually moved (not clamped at either
        // end) — that is when different rows sit under the stationary pointer.
        if (scroller.scrollTop !== beforeTop || scroller.scrollLeft !== beforeLeft) {
          state.resolveAt(state.lastX, state.lastY, { rect, topEdge });
        }
      }
      state.raf = requestAnimationFrame(step);
    };
    autoScrollRef.current = state;
    state.raf = requestAnimationFrame(step);
  };

  // Resolve + extend for a live GUTTER row drag. From the rAF loop the point is
  // clamped into the scroller and aimed at the gutter column's x (in the edge
  // zones the pointer may sit over cells or outside the table entirely; the
  // drag extends by row, so only the y position matters). Guarded by lastHover
  // so a 400-row script doesn't re-render once per frame.
  const resolveGutterRowAt = (x, y, geo) => {
    const ds = dragSelectRef.current;
    if (!ds.active) return;
    let px = x, py = y;
    if (geo) {
      ({ x: px, y: py } = clampToScrollerRect(x, y, geo.rect, geo.topEdge));
      const gutterTd = document.querySelector('.script-scroll-body td.scene-num');
      if (gutterTd) {
        const gr = gutterTd.getBoundingClientRect();
        px = gr.left + gr.width / 2;
      }
    }
    const el = document.elementFromPoint(px, py);
    const tr = el?.closest('tr.scene-row');
    const rowIdxStr = tr?.getAttribute('data-row-idx');
    if (rowIdxStr == null) return;
    const hover = Number(rowIdxStr);
    if (Number.isNaN(hover) || hover === ds.lastHover) return;
    ds.lastHover = hover;
    selectRowRange(ds.anchorIdx, hover);
  };

  // Resolve + extend for a live CELL range drag (also called from the permanent
  // document-level mousemove so fast moves and out-of-table tracking work).
  // Guarded by cellDragLastRef against per-frame re-renders.
  const resolveCellRangeAt = (x, y, geo) => {
    if (!draggingRef.current) return;
    let px = x, py = y;
    if (geo) ({ x: px, y: py } = clampToScrollerRect(x, y, geo.rect, geo.topEdge));
    const el = document.elementFromPoint(px, py);
    const td = el?.closest?.('td.scene-td');
    if (!td || td.classList.contains('scene-num')) return;
    const rowIdx = Number(td.parentElement?.dataset?.rowIdx);
    if (!Number.isFinite(rowIdx)) return;
    const colKey = td.querySelector?.('[data-col]')?.dataset?.col;
    if (!colKey || colKeyToIdx[colKey] == null) return;
    const key = `${rowIdx}:${colKey}`;
    if (cellDragLastRef.current === key) return;
    cellDragLastRef.current = key;
    updateCellDrag(rowIdx, colKey);
  };

  // Document-level mousemove entry point for cell drags (wired in the permanent
  // drag-tracking effect via handlersLatestRef).
  const cellDragPointerMove = (x, y) => {
    resolveCellRangeAt(x, y, null);
    ensureEdgeAutoScroll(x, y, { horizontal: true, resolveAt: resolveCellRangeAt });
  };

  // mousedown on a gutter cell. Decides immediately for shift/ctrl, defers drag detection to mousemove.
  const handleGutterMouseDown = (idx, e) => {
    // Let the existing inline buttons (+/×/↑/↓ etc.) handle their own events.
    // Their handlers stopPropagation, so this fires only on clicks on the gutter background.
    if (isReadOnly) return;
    if (e.button !== 0) return; // left-click only

    // Mutually exclusive with cell selection — clear any active cell range so a
    // row copy/paste reads the selected rows, not a leftover cell selection.
    setSelectionMode('rows');
    if (cellSelection) setCellSelection(null);
    if (cellMultiSelection.size) setCellMultiSelection(new Set());

    if (e.shiftKey) {
      e.preventDefault();
      selectRowRange(selectionAnchor, idx);
      return;
    }
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      toggleRowInSelection(idx);
      return;
    }
    // Phase 3: mousedown on a row that's already selected starts a drag-to-reposition
    // gesture (past a 5px threshold). A plain click without drag-movement reduces the
    // selection to this single row (standard spreadsheet behavior).
    if (selectedRows.has(idx) && selectedRows.size > 0) {
      e.preventDefault(); // prevent text-selection drag in adjacent textareas
      repositionRef.current = { active: false, startX: e.clientX, startY: e.clientY };
      let currentDrop = null;

      const onMove = (ev) => {
        const rs = repositionRef.current;
        if (!rs.active) {
          const dx = ev.clientX - rs.startX;
          const dy = ev.clientY - rs.startY;
          if (dx * dx + dy * dy < 25) return; // 5px threshold
          rs.active = true;
          setDragPreview({ x: ev.clientX, y: ev.clientY, count: selectedRows.size });
        } else {
          setDragPreview(prev => prev ? { ...prev, x: ev.clientX, y: ev.clientY } : prev);
        }
        // Figure out which gap the cursor is closest to.
        // elementFromPoint finds the row under the cursor; cursor-Y vs row mid-line
        // decides above-vs-below.
        const el = document.elementFromPoint(ev.clientX, ev.clientY);
        const tr = el?.closest('tr.scene-row');
        if (tr) {
          const rect = tr.getBoundingClientRect();
          const rowIdxStr = tr.getAttribute('data-row-idx');
          const rowIdx = rowIdxStr != null ? Number(rowIdxStr) : null;
          if (rowIdx != null && !Number.isNaN(rowIdx)) {
            const mid = rect.top + rect.height / 2;
            const gap = ev.clientY < mid ? rowIdx : rowIdx + 1;
            currentDrop = gap;
            setDropTargetIdx(gap);
          }
        }
      };

      const onUp = () => {
        const rs = repositionRef.current;
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
        if (rs.active && currentDrop != null) {
          repositionSelectedTo(currentDrop);
        } else if (!rs.active) {
          // No drag movement — reduce multi-selection to this single row.
          selectRowOnly(idx);
        }
        setDropTargetIdx(null);
        setDragPreview(null);
        repositionRef.current = { active: false, startX: 0, startY: 0 };
      };

      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
      return;
    }

    // Plain mousedown — prepare for either a click-select or a drag-select.
    // The drag is only initiated past a movement threshold (handled in mousemove below).
    dragSelectRef.current = {
      active: false,
      anchorIdx: idx,
      startX: e.clientX,
      startY: e.clientY,
      suppressClick: false,
      lastHover: idx,
    };

    const onMove = (ev) => {
      const ds = dragSelectRef.current;
      if (ds.anchorIdx == null) return;
      if (!ds.active) {
        const dx = ev.clientX - ds.startX;
        const dy = ev.clientY - ds.startY;
        if (dx * dx + dy * dy < 25) return; // 5px threshold
        ds.active = true;
        // Begin drag selection — anchor becomes the only selected row initially.
        setSelectedRows(new Set([ds.anchorIdx]));
        setSelectionAnchor(ds.anchorIdx);
      }
      // Resolve the element under the cursor; if it's a gutter cell, extend the range.
      const el = document.elementFromPoint(ev.clientX, ev.clientY);
      const td = el?.closest('td.scene-num');
      const tr = td?.closest('tr.scene-row');
      const rowIdxStr = tr?.getAttribute('data-row-idx');
      if (rowIdxStr != null) {
        const hover = Number(rowIdxStr);
        if (!Number.isNaN(hover) && hover !== ds.lastHover) {
          ds.lastHover = hover;
          selectRowRange(ds.anchorIdx, hover);
        }
      }
      // Edge auto-scroll: scrolls when the pointer sits in a top/bottom hot
      // zone and keeps extending the selection while the view moves.
      ensureEdgeAutoScroll(ev.clientX, ev.clientY, { horizontal: false, resolveAt: resolveGutterRowAt });
    };

    const onUp = () => {
      stopEdgeAutoScroll();
      const ds = dragSelectRef.current;
      if (ds.active) {
        ds.suppressClick = true;
      } else {
        // No drag — treat as a plain click: select this row only.
        selectRowOnly(ds.anchorIdx);
      }
      ds.active = false;
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  const deleteSelectedRows = () => {
    pushUndo(draftBody);
    const indices = [...selectedRows].sort((a, b) => b - a);
    const scenes = [...(draftBody?.scenes || [])];
    indices.forEach(i => scenes.splice(i, 1));
    setDraftBody({ ...draftBody, scenes });
    setSelectedRows(new Set());
    setMultiDeleteConfirm(false);
    setHoveredRow(null);
  };

  const moveSelectedBlock = (dir) => {
    if (selectedRows.size === 0) return;
    const sorted = [...selectedRows].sort((a, b) => a - b);
    const scenes = [...(draftBody?.scenes || [])];
    const n = scenes.length;

    // Build contiguous groups from the sorted selection
    const groups = [];
    let gs = sorted[0], ge = sorted[0];
    for (let i = 1; i < sorted.length; i++) {
      if (sorted[i] === ge + 1) { ge = sorted[i]; }
      else { groups.push([gs, ge]); gs = sorted[i]; ge = sorted[i]; }
    }
    groups.push([gs, ge]);

    if (dir === -1) {
      if (sorted[0] === 0) return;
      pushUndo(draftBody);
      // Process groups top→bottom: move the element above each group to below it
      for (const [start, end] of groups) {
        if (start === 0) continue;
        const displaced = scenes.splice(start - 1, 1)[0];
        scenes.splice(end, 0, displaced);
      }
    } else {
      if (sorted[sorted.length - 1] === n - 1) return;
      pushUndo(draftBody);
      // Process groups bottom→top: move the element below each group to above it
      for (let g = groups.length - 1; g >= 0; g--) {
        const [start, end] = groups[g];
        if (end === n - 1) continue;
        const displaced = scenes.splice(end + 1, 1)[0];
        scenes.splice(start, 0, displaced);
      }
    }
    setDraftBody({ ...draftBody, scenes });
    // Phase 2.7: clear selection after any move (single or block).
    setSelectedRows(new Set());
    setSelectionAnchor(null);
  };

  const saveLink = (rowIdx, colKey, text, url) => {
    if (!url.trim()) return;
    const scenes = [...(draftBody?.scenes || [])];
    const _links = { ...(scenes[rowIdx]._links || {}), [colKey]: { text: text.trim() || url.trim(), url: url.trim() } };
    scenes[rowIdx] = { ...scenes[rowIdx], _links };
    setDraftBody({ ...draftBody, scenes });
    setLinkPopover(null);
  };

  const removeLink = (rowIdx, colKey) => {
    const scenes = [...(draftBody?.scenes || [])];
    const _links = { ...(scenes[rowIdx]._links || {}) };
    delete _links[colKey];
    scenes[rowIdx] = { ...scenes[rowIdx], _links };
    setDraftBody({ ...draftBody, scenes });
  };

  const handleExportDocx = () => {
    const printCols = displayCols(format, showProdNotesCol);
    const allScenes = draftBody?.scenes || [];
    const scenes    = allScenes.filter(row => printCols.some(c => (row[c.key] || '').trim()));
    const esc = s => (s || '').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');

    const headerCells = `<th style="width:40px;background:#f0f0f0;border:1px solid #ccc;padding:6px 8px;font-size:10pt;font-weight:bold;text-align:center">#</th>` +
      printCols.map((c, ci) => `<th style="width:${renderedColPcts[ci]}%;background:#f0f0f0;border:1px solid #ccc;padding:6px 8px;font-size:10pt;font-weight:bold;text-align:left">${esc(c.label)}</th>`).join('');

    const bodyRows = scenes.map((row, i) => {
      const cells = printCols.map(c => {
        let val = renderMarkdown(esc(row[c.key] || '')).replace(/\n/g, '<br>');
        const link = row._links?.[c.key];
        if (link?.url) val += `${val ? '<br>' : ''}<a href="${esc(link.url)}" style="color:inherit;text-decoration:underline">${esc(link.text || link.url)}</a>`;
        return `<td style="border:1px solid #ccc;padding:6px 8px;font-size:10pt;vertical-align:top">${val}</td>`;
      }).join('');
      return `<tr><td style="border:1px solid #ccc;padding:6px 8px;text-align:center;color:#888;font-size:9pt">${i + 1}</td>${cells}</tr>`;
    }).join('');

    const html = `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word" xmlns="http://www.w3.org/TR/REC-html40">
<head><meta charset="utf-8"><title>${esc(selected.scriptName)}</title>
<style>body{font-family:Arial,sans-serif;font-size:10pt;margin:1in}h1{font-size:16pt;font-weight:bold;margin-bottom:4pt}.sub{font-size:10pt;color:#555;margin-bottom:16pt}table{border-collapse:collapse;width:100%;table-layout:fixed}</style>
</head><body>
<h1>${esc(selected.scriptName)}</h1>
<div class="sub">${esc(selected.client?.name || '')} · ${esc(selected.writer?.name || '')}</div>
<table><thead><tr>${headerCells}</tr></thead><tbody>${bodyRows}</tbody></table>
</body></html>`;

    const blob = new Blob(['\ufeff', html], { type: 'application/msword' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `${(selected.scriptName || 'script').replace(/[^a-z0-9\s-]/gi, '').trim()}.doc`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const applyMarkdown = (marker, rowIdx, colKey) => {
    const el = document.querySelector(`[data-row="${rowIdx}"][data-col="${colKey}"]`);
    if (!el) return;
    const start = el.selectionStart;
    const end   = el.selectionEnd;
    const val   = el.value;
    const sel   = val.slice(start, end);
    let newVal, newStart, newEnd;
    if (start === end) {
      newVal   = val.slice(0, start) + marker + marker + val.slice(end);
      newStart = newEnd = start + marker.length;
    } else if (sel.startsWith(marker) && sel.endsWith(marker) && sel.length > marker.length * 2) {
      const inner = sel.slice(marker.length, -marker.length);
      newVal   = val.slice(0, start) + inner + val.slice(end);
      newStart = start; newEnd = start + inner.length;
    } else {
      newVal   = val.slice(0, start) + marker + sel + marker + val.slice(end);
      newStart = start; newEnd = end + marker.length * 2;
    }
    updateScene(rowIdx, colKey, newVal);
    setTimeout(() => {
      const el2 = document.querySelector(`[data-row="${rowIdx}"][data-col="${colKey}"]`);
      if (el2) el2.setSelectionRange(newStart, newEnd);
    }, 0);
  };

  // Phase B2: format/cols/B2 helpers need to be defined BEFORE handlersLatestRef
  // since `const` is temporally dead until declaration — otherwise the assignment
  // below throws ReferenceError and the page renders blank.
  const format = draftBody?.format;
  // Production Notes column: visible to anyone viewing the script once it has
  // ever been approved (derived server-side as selected.hasBeenApproved), and
  // it stays visible if the script later leaves Approved. Defined here (not with
  // the other gates below) because `cols` must be built before handlersLatestRef.
  const showProdNotesCol = !!selected?.hasBeenApproved;
  // useMemo keeps `cols` referentially stable (displayCols returns a fresh array
  // when prodNotes is spliced in) so SceneRow memoization isn't defeated.
  const cols   = useMemo(() => displayCols(format, showProdNotesCol), [format, showProdNotesCol]);

  // ── Longform hierarchy (D-121, 2026-09-17): GROUPED RENDERING of the flat body — rendering only.
  // The body stays the flat Skit-shaped array; band <tr>s are emitted around the rows:
  //   • a SCENE band before the first row of each contiguous run of one sceneNumber (Scene N + its
  //     Scene Description, row count, chevron);
  //   • an EPISODE band before each EPISODE BREAK direction row ("Episode break", or "Ep N — title"
  //     once an episode whose range starts right after the break exists), plus a leading band for the
  //     first episode when episodes exist.
  // Collapse/expand is UI state only (scene number / break row id); collapsed rows are simply not
  // emitted, so data-row indices, keyboard flow (D-117/D-118), selection and autosave are untouched.
  const [lfCollapsedScenes, setLfCollapsedScenes] = useState(() => new Set());
  const [lfCollapsedEpisodes, setLfCollapsedEpisodes] = useState(() => new Set());
  const [lfEpisodes, setLfEpisodes] = useState([]);
  const lfEpisodesScriptRef = useRef(null);
  // D-126: every successful body save may have minted / renamed / removed episode cards server-side —
  // bump so the series panel (and the bands) re-read the cards.
  const [lfEpisodesVersion, setLfEpisodesVersion] = useState(0);
  const loadLfEpisodes = useCallback(async (scriptId) => {
    // The longform API refuses external accounts outright (404 on every route), so they never ask.
    if (!scriptId || user?.isExternal) { setLfEpisodes([]); return; }
    try { const r = await api.get(`/api/longform/scripts/${scriptId}/episodes`); if (lfEpisodesScriptRef.current === scriptId) setLfEpisodes(r.data.episodes || []); } catch { /* bands fall back to "Episode break" */ }
  }, [user?.isExternal]);
  useEffect(() => {
    const isLf = format === 'Longform' && selected?.id;
    lfEpisodesScriptRef.current = isLf ? selected.id : null;
    if (isLf) loadLfEpisodes(selected.id); else setLfEpisodes([]);
    setLfCollapsedScenes(new Set()); setLfCollapsedEpisodes(new Set());
  }, [selected?.id, format, loadLfEpisodes]);
  // D-123 (2026-09-18): bands are POSITIONAL. lib/longformEpisodes.js splits the flat body into blocks
  // at the EPISODE BREAK / EPISODE n markers (Ep N ≡ the N-th block); the episode band before a marker
  // row is labelled with the episode that block maps to — "Ep N — title · scenes a–b[ · Planned]" with
  // the range read from the block's OWN rows (scene numbers restart at 1 per episode) — or "Episode
  // break" when no such episode exists yet. Scene bands show the row's own (per-episode) scene number;
  // their collapse key includes the block so "Scene 1" of Ep 2 never folds with "Scene 1" of Ep 1.
  const lfGroupPlan = useMemo(() => {
    if (format !== 'Longform') return null;
    const scenes = draftBody?.scenes || [];
    const plan = new Array(scenes.length);
    const blocks = lfe.episodeBlocks(scenes);
    const blockOfRow = new Array(scenes.length).fill(null);
    for (const b of blocks) for (const j of b.rowIndexes) blockOfRow[j] = b;
    const epFor = (blockIndex) => lfEpisodes.find(e => e.episodeNumber === blockIndex) || null;
    // D-129: the band IS the episode break — one band per block, above its first row; the marker rows
    // themselves (EPISODE BREAK / EPISODE n headers) are not emitted. Label: "Ep N · title|untitled ·
    // scenes a–b · Planned | <stage> | no card yet".
    const bandFor = (b) => {
      const ep = epFor(b.index); const title = lfe.blockTitle(scenes, b);
      const range = b.sceneFrom == null ? `${b.rowCount} row${b.rowCount === 1 ? '' : 's'}` : (b.sceneFrom === b.sceneTo ? `scene ${b.sceneFrom}` : `scenes ${b.sceneFrom}–${b.sceneTo}`);
      const state = !ep ? 'no card yet' : (ep.productionActive === false ? 'Planned' : ep.status);
      return { key: `e:${b.index}`, episodeNumber: b.index, title, untitled: !title, range, state, cardId: ep?.id || null, label: `Ep ${b.index} · ${title || 'untitled'} · ${range} · ${state}` };
    };
    let prevScene = null, curEpKey = 'e:0', epCollapsed = false, sceneCollapsed = false;
    for (let i = 0; i < scenes.length; i++) {
      const r = scenes[i]; const p = { episodeBand: null, sceneBand: null, hidden: false, marker: false };
      const b = blockOfRow[i];
      if (b && i === b.start) { const band = bandFor(b); curEpKey = band.key; p.episodeBand = band; epCollapsed = lfCollapsedEpisodes.has(curEpKey); prevScene = null; }
      if (lfe.isBreakMarker(r)) { p.marker = true; p.hidden = true; plan[i] = p; continue; }
      const sk = String(r?.sceneNumber ?? '').trim();
      if (sk !== prevScene) {
        let n = 1; for (let j = i + 1; j < scenes.length && String(scenes[j]?.sceneNumber ?? '').trim() === sk && !lfe.isBreakMarker(scenes[j]); j++) n++;
        const key = `s:${curEpKey}:${sk}`;
        p.sceneBand = { key, sceneNumber: sk, description: String(r?.scene || '').trim(), rows: n, episodeNumber: b?.index ?? null };
        sceneCollapsed = lfCollapsedScenes.has(key); prevScene = sk;
      }
      // A collapsed episode shows only its band — its rows and scene bands are not emitted (the band now sits
      // on the episode's first content row, which also opens a scene, so the scene band must go too).
      if (epCollapsed) { p.hidden = true; p.sceneBand = null; }
      else if (sceneCollapsed) p.hidden = true;
      plan[i] = p;
    }
    return plan;
  }, [format, draftBody, lfEpisodes, lfCollapsedScenes, lfCollapsedEpisodes]);
  // D-129 band actions: rename (writes the header row, D-127) and remove the break that opens episode k
  // (merges it into the episode above: drops its header row and the close marker right before it).
  const [lfBandEditing, setLfBandEditing] = useState(null); // { k, value }
  const removeEpisodeBoundary = (k) => {
    const body = draftBodyRef.current || draftBody;
    if (!body) return;
    const scenes = [...(body.scenes || [])];
    const b = lfe.episodeBlocks(scenes)[k - 1];
    if (!b || k < 2) return;
    pushUndo(body);
    const drop = new Set();
    if (lfe.isOpenMarker(scenes[b.start])) drop.add(b.start);
    for (let j = b.start - 1; j >= 0 && lfe.isBreakMarker(scenes[j]); j--) drop.add(j);
    const next = scenes.filter((_, j) => !drop.has(j));
    setDraftBody({ ...body, format: 'Longform', scenes: next });
    setSelectedRows(new Set()); setSelectionAnchor(null);
  };
  // D-130 (2026-09-18): scene numbers are forced into succession inside each episode. Whatever a row
  // carries only decides "same scene as the row above" or "next scene"; the draft is re-numbered 1, 2, 3…
  // per episode after every change (and on load — a legacy body is corrected and autosaved). Idempotent,
  // so the effect settles in one extra render; row indices never move, only the Scene # strings.
  useEffect(() => {
    if (format !== 'Longform' || !draftBody?.scenes) return;
    const { rows, changed } = lfe.normalizeSceneNumbers(draftBody.scenes);
    if (changed) setDraftBody({ ...draftBody, scenes: rows });
  }, [draftBody, format]); // eslint-disable-line react-hooks/exhaustive-deps
  const lfToggleScene = (key) => setLfCollapsedScenes(prev => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n; });
  const lfToggleEpisode = (key) => setLfCollapsedEpisodes(prev => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n; });

  // Run 2: the per-client character registry — feeds the Character cell picker
  // and the registry modal opened from the Character column header. Skit and
  // Mockumentary only (the formats with a character column, DEF-083).
  const isCharacterFormat  = format === 'Skit' || format === 'Mockumentary' || format === 'Longform'; // Longform joined 2026-09-15 (D-089)
  const registryClientId   = selected?.client?.id || null;
  const [registryChars, setRegistryChars]         = useState(null);
  const [registryModalOpen, setRegistryModalOpen] = useState(false);
  const loadRegistry = useCallback(async () => {
    if (!registryClientId) return;
    try {
      const r = await api.get(`/api/client-characters?clientId=${registryClientId}`);
      setRegistryChars(r.data.characters || []);
    } catch { setRegistryChars([]); }
  }, [registryClientId]);
  useEffect(() => {
    setRegistryChars(null);
    setRegistryModalOpen(false);
    if (registryClientId && isCharacterFormat) loadRegistry();
  }, [registryClientId, isCharacterFormat, loadRegistry]);
  // Live names only — the picker never offers an archived character.
  const registryNames = useMemo(
    () => (isCharacterFormat && registryChars) ? registryChars.filter(r => !r.isArchived).map(r => r.name) : null,
    [isCharacterFormat, registryChars]);
  // Mirrors the server gates: list curation follows the script-edit roles,
  // talent mapping follows canEditTal (Producer / Power User / Admin).
  const canEditRegistry = hasRole('Admin', 'Manager', 'Power User', 'Qual Reviewer', 'Writer');
  const canMapTalent    = hasRole('Admin', 'Power User', 'Producer');
  const addCharacterToRegistry = async (name) => {
    if (!registryClientId || !name) return;
    try { await api.post('/api/client-characters', { clientId: registryClientId, name }); }
    catch { /* 409 already-present etc. — the refetch below shows the truth */ }
    await loadRegistry();
  };
  // Live spoken-word count (null spokenKey -> None/Riff/unknown -> counter hidden).
  const spokenKey = SPOKEN_COL[format] || null;
  const spokenWordCount = useSpokenWordCount(draftBody, spokenKey);

  // OUT-4 (docs/OUTPUT-C5-v1.md §2, ruling D): the script's Minutes, how long the finished video will run. Blank uses the
  // estimate (spoken words ÷ 250), shown as the placeholder; whatever the field holds when the script is approved is what
  // Scripted and Filmed credit. Saved on blur or Enter through PATCH /api/scripts/:id. The API's gate: the script's writer,
  // a Qual Reviewer, a Manager, a Power User or an Admin; a Posted script is read-only.
  const [runtimeText, setRuntimeText] = useState('');
  useEffect(() => { setRuntimeText(minutesText(selected?.runtime_minutes)); }, [selected?.id, selected?.runtime_minutes]);
  const canEditRuntime = !!selected && !user?.isExternal && selected.qualStatus !== 'Posted'
    && (hasRole('Admin', 'Manager', 'Power User', 'Qual Reviewer') || selected.writer?.id === user?.id);
  const saveRuntime = async () => {
    if (!selected) return;
    const current = selected.runtime_minutes ?? null;
    const parsed = parseMinutes(runtimeText);
    if (parsed.error) { setRuntimeText(minutesText(current)); toast(parsed.error, { tone: 'warn' }); return; }
    if (parsed.value === current) { setRuntimeText(minutesText(current)); return; }
    try {
      const res = await api.patch(`/api/scripts/${selected.id}`, { runtime_minutes: parsed.value });
      const saved = res.data?.runtime_minutes !== undefined ? res.data.runtime_minutes : parsed.value;
      setSelected(prev => (prev && prev.id === selected.id ? { ...prev, runtime_minutes: saved } : prev));
      setRuntimeText(minutesText(saved));
    } catch (err) {
      setRuntimeText(minutesText(current));
      showError('The minutes were not saved', err.response?.data?.error || 'Try again.');
    }
  };

  // WPM input: accept the keystroke into `wpmInput`, but only promote a valid,
  // clamped number into `wpm` (which drives the division). Non-numeric / empty
  // input is silently ignored — `wpm` holds its last good value and reverts the
  // display on blur. Persist (debounced) becomes the user's new default.
  const handleWpmChange = e => {
    const raw = e.target.value;
    setWpmInput(raw);
    if (raw.trim() === '') return;
    const n = parseInt(raw, 10);
    if (!Number.isFinite(n)) return;
    const clamped = clampWpm(n);
    setWpm(clamped);
    if (wpmPersistTimer.current) clearTimeout(wpmPersistTimer.current);
    wpmPersistTimer.current = setTimeout(() => {
      try { localStorage.setItem(WPM_STORAGE_KEY, String(clamped)); } catch { /* ignore */ }
    }, 400);
  };
  const handleWpmBlur = () => {
    // Snap the display back to the last good clamped value (drops empty/invalid/
    // out-of-range text the user may have left in the box).
    setWpmInput(String(wpm));
    if (wpmPersistTimer.current) { clearTimeout(wpmPersistTimer.current); wpmPersistTimer.current = null; }
    try { localStorage.setItem(WPM_STORAGE_KEY, String(wpm)); } catch { /* ignore */ }
  };
  const colKeyToIdx = useMemo(() => {
    const m = {};
    cols.forEach((c, i) => { m[c.key] = i; });
    return m;
  }, [cols]);
  const startCellDrag = (rowIdx, colKey, shift, ctrl) => {
    const colIdx = colKeyToIdx[colKey];
    if (colIdx == null) return;
    // Cell and row selection are mutually exclusive — starting a cell selection
    // clears any gutter row selection so a copy reads the cells, not stale rows.
    setSelectionMode('cells');
    if (selectedRows.size) { setSelectedRows(new Set()); setSelectionAnchor(null); }
    if (ctrl) {
      // Ctrl/Cmd+click → toggle the cell in the non-contiguous set. If an
      // active rectangular range was being shown, freeze its cells into the
      // set first so the user doesn't lose them when they extend with Ctrl.
      setCellMultiSelection(prev => {
        const next = new Set(prev);
        if (cellSelection &&
            (cellSelection.startRow !== cellSelection.endRow ||
             cellSelection.startCol !== cellSelection.endCol)) {
          const r1 = Math.min(cellSelection.startRow, cellSelection.endRow);
          const r2 = Math.max(cellSelection.startRow, cellSelection.endRow);
          const c1 = Math.min(cellSelection.startCol, cellSelection.endCol);
          const c2 = Math.max(cellSelection.startCol, cellSelection.endCol);
          for (let r = r1; r <= r2; r++) {
            for (let c = c1; c <= c2; c++) next.add(`${r}:${cols[c].key}`);
          }
        }
        const id = `${rowIdx}:${colKey}`;
        if (next.has(id)) next.delete(id); else next.add(id);
        return next;
      });
      // Collapse the rect range to the clicked cell so it doesn't keep the
      // big rectangle highlighted while the user is building a non-contiguous set.
      setCellSelection({ startRow: rowIdx, startCol: colIdx, endRow: rowIdx, endCol: colIdx });
      return;
    }
    draggingRef.current = true;
    cellDragLastRef.current = `${rowIdx}:${colKey}`;
    // Plain or shift click — drop any non-contiguous selection.
    if (cellMultiSelection.size) setCellMultiSelection(new Set());
    setCellSelection(prev => {
      if (shift && prev) return { ...prev, endRow: rowIdx, endCol: colIdx };
      return { startRow: rowIdx, startCol: colIdx, endRow: rowIdx, endCol: colIdx };
    });
  };
  const updateCellDrag = (rowIdx, colKey) => {
    if (!draggingRef.current) return;
    const colIdx = colKeyToIdx[colKey];
    if (colIdx == null) return;
    setCellSelection(prev => prev ? { ...prev, endRow: rowIdx, endCol: colIdx } : prev);
  };
  const clearCellSelection = () => {
    setCellSelection(null);
    setCellMultiSelection(new Set());
  };
  // Collapse an active multi-cell range/multi-selection down to a single cell when
  // the user navigates by keyboard (arrows / Tab / Enter). Google-Sheets behavior:
  // a plain arrow after a range moves to ONE cell rather than keeping the old range.
  // We drop a real rectangle (and any ctrl+click multi-set) so the destination cell
  // — tracked by focusedCell — becomes the active single cell, which is what copy,
  // delete, color, etc. then act on. A lone 1×1 (or null) is left untouched so we
  // don't trigger needless re-renders mid-navigation.
  const collapseCellSelection = () => {
    setCellSelection(prev =>
      (prev && (prev.startRow !== prev.endRow || prev.startCol !== prev.endCol)) ? null : prev);
    setCellMultiSelection(prev => (prev.size ? new Set() : prev));
  };

  // ── Cross-script app clipboard: selection reader, copy, paste ────────────
  const flashAppClipToast = (msg) => {
    toast(msg, { tone: 'info', duration: 5000 });
  };

  // Normalize the current selection into a descriptor used by copy and by paste
  // (as the paste anchor). Two kinds:
  //   'cells' — any cell selection (drag range, ctrl+click multi, or one focused
  //             cell). Copied/pasted as a rectangular OVERLAY block. anchorRow /
  //             anchorColIdx are the top-left cell.
  //   'rows'  — whole rows selected via the gutter. Copied/pasted as INSERTED rows.
  const cellsSel = (r1, r2, c1, c2) => {
    const rowIdxs = []; for (let r = r1; r <= r2; r++) rowIdxs.push(r);
    const colIdxs = []; for (let c = c1; c <= c2; c++) colIdxs.push(c);
    const colKeys = colIdxs.map(c => cols[c]?.key).filter(Boolean);
    return { kind: 'cells', rowIdxs, colIdxs, colKeys, anchorRow: r1, anchorColIdx: c1 };
  };
  const rowsSelection = () => {
    if (selectedRows.size === 0) return null;
    const sorted = [...selectedRows].sort((a, b) => a - b);
    return { kind: 'rows', rowIdxs: sorted, colKeys: cols.map(c => c.key), anchorRow: sorted[0], anchorColIdx: 0 };
  };
  const cellsSelection = () => {
    if (cellMultiSelection.size > 0) {
      // Non-contiguous ctrl+click set → bounding rectangle (like Sheets).
      let r1 = Infinity, r2 = -Infinity, c1 = Infinity, c2 = -Infinity;
      for (const id of cellMultiSelection) {
        const sep = id.indexOf(':');
        const r = Number(id.slice(0, sep));
        const ci = colKeyToIdx[id.slice(sep + 1)];
        if (!Number.isFinite(r) || ci == null) continue;
        r1 = Math.min(r1, r); r2 = Math.max(r2, r);
        c1 = Math.min(c1, ci); c2 = Math.max(c2, ci);
      }
      if (r1 !== Infinity) return cellsSel(r1, r2, c1, c2);
    }
    // A real multi-cell rectangle (drag / shift-range) is authoritative. A
    // COLLAPSED 1×1 cellSelection, however, carries no more information than the
    // focused cell — and keyboard navigation (arrows / Tab / Enter) moves
    // focusedCell WITHOUT updating cellSelection, so a stale 1×1 left over from an
    // earlier click would otherwise win and copy the WRONG (previously-clicked)
    // cell. Defer to focusedCell in that case, mirroring getActiveCellGrid's
    // precedence (the reader used by delete / color / move). Fall back to the 1×1
    // only when there's no usable focused cell.
    const cs = cellSelection;
    const csIsRange = cs && (cs.startRow !== cs.endRow || cs.startCol !== cs.endCol);
    if (csIsRange) {
      const r1 = Math.min(cs.startRow, cs.endRow), r2 = Math.max(cs.startRow, cs.endRow);
      const c1 = Math.min(cs.startCol, cs.endCol), c2 = Math.max(cs.startCol, cs.endCol);
      return cellsSel(r1, r2, c1, c2);
    }
    if (focusedCell && Number.isFinite(focusedCell.rowIdx)) {
      const ci = colKeyToIdx[focusedCell.colKey];
      if (ci != null) return cellsSel(focusedCell.rowIdx, focusedCell.rowIdx, ci, ci);
    }
    if (cs) {
      const r1 = Math.min(cs.startRow, cs.endRow), c1 = Math.min(cs.startCol, cs.endCol);
      return cellsSel(r1, r1, c1, c1);
    }
    return null;
  };
  // SINGLE decision point. `selectionMode` (set by the user's last action — cell
  // drag/click vs gutter click) is authoritative, so a row copy can never be
  // mistaken for a cell copy or vice-versa. Falls back to whichever exists only
  // when no mode is set yet.
  const getActiveSelection = () => {
    if (selectionMode === 'rows')  return rowsSelection();
    if (selectionMode === 'cells') return cellsSelection();
    return cellsSelection() || rowsSelection();
  };

  const copySelectionToAppClipboard = () => {
    if (!format || !draftBody) return;
    const sel = getActiveSelection();
    if (!sel) { flashCopyFeedback('Select cells or rows to copy first.', 2000); return; }
    const scenes = draftBody.scenes || [];
    const base = {
      sourceScriptId:   selected?.id,
      sourceScriptName: selected?.scriptName || 'Untitled',
      sourceFormat:     format,
      copiedAt:         Date.now(),
    };
    let payload, n;
    if (sel.kind === 'rows') {
      const rows = sel.rowIdxs.filter(i => scenes[i]).map(i => {
        const scene = scenes[i]; const out = {};
        for (const c of cols) out[c.key] = scene[c.key] ?? '';
        if (scene._rowColor)   out._rowColor   = scene._rowColor;
        if (scene._cellColors) out._cellColors = { ...scene._cellColors };
        if (scene._links)      out._links      = JSON.parse(JSON.stringify(scene._links));
        return out;
      });
      if (!rows.length) return;
      payload = { ...base, kind: 'rows', rows };
      n = rows.length;
    } else {
      // Rectangular cell block → 2D grid of { v, color?, link? }, preserving layout.
      const grid = sel.rowIdxs.map(r => {
        const scene = scenes[r] || {};
        return sel.colKeys.map(k => {
          const cell = { v: scene[k] ?? '' };
          const color = scene._cellColors?.[k];
          const link  = scene._links?.[k];
          if (color) cell.color = color;
          if (link)  cell.link  = link;
          return cell;
        });
      });
      if (!grid.length || !sel.colKeys.length) {
        flashAppClipToast(`Nothing was copied: the selection is ${grid.length} rows by ${sel.colKeys.length} columns.`);
        return;
      }
      payload = { ...base, kind: 'cells', colKeys: sel.colKeys, grid };
      n = grid.length;
    }
    writeAppClip(payload);
    // Best-effort system clipboard (TSV) so the same selection can also paste into
    // Google Sheets / Excel. sessionStorage is the source of truth; a denied
    // clipboard permission just means no external paste.
    try {
      const tsv = payload.kind === 'cells'
        ? payload.grid.map(row => row.map(c => tsvEscapeCell(c.v)).join('\t')).join('\n')
        : buildSubsetTSV(payload.rows, cols.map(c => c.key));
      if (tsv && navigator.clipboard?.writeText) navigator.clipboard.writeText(tsv).catch(() => {});
    } catch { /* best effort */ }
    if (payload.kind === 'cells') {
      const w = payload.colKeys.length;
      flashAppClipToast(`Copied a ${n} by ${w} cell block. Click a target cell in any script, then paste.`);
    } else {
      flashAppClipToast(`Copied ${n} row${n !== 1 ? 's' : ''}. Paste into any script.`);
    }
  };

  // Write whole rows (by index) to the app clipboard as a 'rows' payload. Called
  // by the legacy row-copy paths (gutter Ctrl+C and the right-click menu) so they
  // populate the SAME cross-script clipboard the paste reads — otherwise a copy
  // there leaves a stale app clipboard and paste uses old rows from another script.
  const writeRowsToAppClip = (indices) => {
    if (!format || !indices?.length) return;
    const scenes = draftBody?.scenes || [];
    const rows = indices.filter(i => scenes[i]).map(i => {
      const scene = scenes[i]; const out = {};
      for (const c of cols) out[c.key] = scene[c.key] ?? '';
      if (scene._rowColor)   out._rowColor   = scene._rowColor;
      if (scene._cellColors) out._cellColors = { ...scene._cellColors };
      if (scene._links)      out._links      = JSON.parse(JSON.stringify(scene._links));
      return out;
    });
    if (!rows.length) return;
    writeAppClip({
      kind: 'rows',
      sourceScriptId:   selected?.id,
      sourceScriptName: selected?.scriptName || 'Untitled',
      sourceFormat:     format,
      copiedAt:         Date.now(),
      rows,
    });
  };

  // Explicit save that tags the change as a paste so the API writes a paste
  // audit entry. Mirrors saveBody's permission guard; takes the freshly computed
  // body to avoid the async-setState race.
  const savePaste = async (bodyToSave, meta) => {
    if (!selected || !bodyToSave) return;
    const isShootLeadEditing = location.state?.isShootLeadForScript && (hasRole('Producer'));
    if (!hasRole('Admin', 'Manager', 'Power User', 'Qual Reviewer') && !isShootLeadEditing && selected?.writer?.id !== user?.id) return;
    setSaving(true);
    const notesAtSave = draftNotesRef.current;
    try {
      const res = await api.patch(`/api/scripts/${selected.id}`, {
        scriptBody: bodyToSave,
        qualNotes:  notesAtSave,
        saveType:   'paste',
        pasteMeta:  meta,
      });
      // HF-FIX-PASTE-1: this save is the new base. Without it the next autosave diffed against the pre-paste body and
      // sent every pasted row again as an insert.
      savedNotesRef.current = notesAtSave;
      adoptServerBody(res.data?.scriptBody || bodyToSave, bodyToSave, res.data?.rev, draftNotesRef.current !== notesAtSave);
      setLastSaved(new Date());
      setSaveError(null);
      if (activityOpen) {
        const logRes = await api.get(`/api/audit/script/${selected.id}`);
        setActivityLog(logRes.data);
      }
    } catch (err) {
      console.error('Paste save failed:', err);
      setSaveError({ tooLarge: err?.response?.status === 413 });
    } finally {
      setSaving(false);
    }
  };

  const pasteFromAppClipboard = async () => {
    if (isReadOnly || !format || !draftBody) return;
    const clip = appClip;
    if (!clip || (clip.kind === 'cells' ? !clip.grid?.length : !clip.rows?.length)) {
      flashCopyFeedback('Clipboard is empty.', 2000);
      return;
    }

    const cur = [...(draftBody.scenes || [])];
    const beforeCount = cur.length;
    let next, selectStart, pastedRowCount;

    if (clip.kind === 'cells') {
      // Spreadsheet overlay: drop the grid into cells starting at the anchor,
      // overwriting existing values and growing the script with new rows when the
      // block runs past the bottom. Columns past the rightmost are ignored.
      const grid = clip.grid;
      const sel = getActiveSelection();
      // Anchor at the active target cell if one is selected in THIS script;
      // otherwise top-left. Never append at the bottom — that turned a cell paste
      // into "a bunch of rows". An out-of-bounds (stale, cross-script) anchor also
      // falls back to top-left.
      const inBounds = sel && Number.isFinite(sel.anchorRow) && sel.anchorRow >= 0 && sel.anchorRow < cur.length;
      const anchorRow = inBounds ? sel.anchorRow : 0;
      const anchorCol = inBounds ? sel.anchorColIdx : 0;
      next = cur.map(s => ({ ...s }));
      for (let r = 0; r < grid.length; r++) {
        const targetRow = anchorRow + r;
        while (next.length <= targetRow) next.push(emptyRow(format));
        const scene = { ...next[targetRow] };
        const gridRow = grid[r];
        for (let c = 0; c < gridRow.length; c++) {
          const destCol = cols[anchorCol + c];
          if (!destCol) continue;                            // past the last column → ignore overflow
          const cell = gridRow[c];
          scene[destCol.key] = cell.v ?? '';
          if (cell.color) scene._cellColors = { ...(scene._cellColors || {}), [destCol.key]: cell.color };
          if (cell.link)  scene._links      = { ...(scene._links      || {}), [destCol.key]: cell.link };
        }
        next[targetRow] = scene;
      }
      selectStart = anchorRow;
      pastedRowCount = grid.length;
    } else {
      // Whole rows → insert. Positional cross-format mapping (with a warning).
      if (clip.sourceFormat !== format) {
        const ok = await confirm({
          title: 'Different script format',
          body: `The copied rows are from a ${clip.sourceFormat} script. This script uses the ${format} format. Columns paste by position: extra source columns are dropped and destination columns with no match stay blank.`,
          confirmLabel: 'Paste anyway',
        });
        if (!ok) return;
      }
      const { scenes: incoming } = mapClipRowsToDestination(
        { rows: clip.rows, sourceFormat: clip.sourceFormat, selectionType: 'full_rows' },
        format, { columnMode: 'full' });
      if (!incoming.length) return;
      const sel = getActiveSelection();
      const at = sel ? sel.anchorRow : cur.length;           // insert above active row, else append
      next = [...cur.slice(0, at), ...incoming, ...cur.slice(at)];
      selectStart = at;
      pastedRowCount = incoming.length;
    }

    pushUndo(draftBody);
    if (next.length === 0) next = [emptyRow(format)];
    const newBody = { ...draftBody, scenes: next };
    setDraftBody(newBody);
    clearCellSelection();
    setSelectAllActive(false);
    const newSelection = new Set();
    for (let k = 0; k < pastedRowCount; k++) newSelection.add(selectStart + k);
    setSelectedRows(newSelection);
    setSelectionAnchor(selectStart);
    setSelectionMode('rows');   // result is highlighted as rows; keep the mode consistent
    const whatPasted = clip.kind === 'cells'
      ? `a ${pastedRowCount} by ${(clip.colKeys || []).length} cell block`
      : `${pastedRowCount} row${pastedRowCount !== 1 ? 's' : ''}`;
    flashCopyFeedback(
      `Pasted ${whatPasted}${clip.sourceScriptName ? ` from ${clean(clip.sourceScriptName)}` : ''}.`,
      2800
    );

    // Audit the paste (pass the freshly computed body — setDraftBody is async).
    savePaste(newBody, {
      beforeCount,
      afterCount:       next.length,
      pastedCount:      pastedRowCount,
      sourceScriptName: clip.sourceScriptName,
      sourceFormat:     clip.sourceFormat,
    });
  };

  // Keep the capture-phase keydown listener reading current state without
  // re-subscribing on every render.
  appClipKbdRef.current = {
    getActiveSelection,
    copy:       copySelectionToAppClipboard,
    paste:      pasteFromAppClipboard,
    hasClip:    appClipCount > 0,   // true for both cell-grid and whole-row payloads
    // A pending legacy row "cut" (Ctrl+X → paste-to-move) must keep Ctrl+V, so
    // the app clipboard yields V while a cut is in flight.
    cutPending: cutRows.size > 0,
  };

  // Cross-script clipboard keyboard ownership (capture phase, runs before the
  // legacy bubble-phase row-clipboard handler). Claims Ctrl/Cmd+C only when a
  // selection exists, and Ctrl/Cmd+V only when the app clipboard is populated
  // and no legacy cut is pending — and only when NOT editing text and NOT in #
  // select-all (those keep their existing native / Google-Sheets behavior). On a
  // claim it stopImmediatePropagation()s so the legacy handler doesn't double-fire;
  // otherwise it leaves the event completely untouched.
  useEffect(() => {
    if (!selected) return;
    const onKeyDownCapture = (e) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      const tag = document.activeElement?.tagName;
      if (tag === 'TEXTAREA' || tag === 'INPUT') return; // editing a cell → native
      if (selectAllActive) return;                       // # select-all → Google Sheets clipboard
      const isC = e.key === 'c' || e.key === 'C';
      const isV = e.key === 'v' || e.key === 'V';
      if (!isC && !isV) return;
      const h = appClipKbdRef.current;
      if (isC) {
        if (h.getActiveSelection && h.getActiveSelection()) {
          e.preventDefault();
          e.stopImmediatePropagation();
          h.copy();
        }
        return;
      }
      // isV
      if (h.hasClip && !h.cutPending) {
        e.preventDefault();
        e.stopImmediatePropagation();
        h.paste();
      }
    };
    document.addEventListener('keydown', onKeyDownCapture, true);
    return () => document.removeEventListener('keydown', onKeyDownCapture, true);
  }, [selected, selectAllActive]);

  // Phase C1 (spec 4.1 / 4.2): move-drag for single cell OR range. The anchor
  // is the cell whose move-handle the user grabbed; the source range is the
  // current cellSelection (or just the anchor cell if no multi-cell range).
  // The whole range moves by the (target - anchor) offset on drop.
  const startMoveDrag = (anchorRow, anchorCol) => {
    let r1 = anchorRow, r2 = anchorRow, c1 = anchorCol, c2 = anchorCol;
    if (cellSelection && (cellSelection.startRow !== cellSelection.endRow || cellSelection.startCol !== cellSelection.endCol)) {
      r1 = Math.min(cellSelection.startRow, cellSelection.endRow);
      r2 = Math.max(cellSelection.startRow, cellSelection.endRow);
      c1 = Math.min(cellSelection.startCol, cellSelection.endCol);
      c2 = Math.max(cellSelection.startCol, cellSelection.endCol);
    }
    const md = { anchorRow, anchorCol, r1, r2, c1, c2, targetRow: anchorRow, targetCol: anchorCol };
    moveDragRef.current = md;
    setMoveDrag(md);
  };
  const updateMoveTarget = (targetRow, colKey) => {
    const cur = moveDragRef.current;
    if (!cur) return;
    const targetCol = colKeyToIdx[colKey];
    if (targetCol == null) return;
    const next = { ...cur, targetRow, targetCol };
    moveDragRef.current = next;
    setMoveDrag(next);
  };
  const commitMoveDrag = () => {
    const md = moveDragRef.current;
    if (!md) return;
    setMoveDrag(null);
    const offsetR = md.targetRow - md.anchorRow;
    const offsetC = md.targetCol - md.anchorCol;
    if (offsetR === 0 && offsetC === 0) return;
    const body = draftBodyRef.current;
    if (!body) return;
    pushUndo(body);

    // Build per-row update bundle. Source clears come first; target sets
    // override in the overlap so cells inside both source AND target end up
    // with their target value rather than empty.
    const updates = new Map(); // rowIdx -> { colKey: value }
    const setUpdate = (r, key, v) => {
      if (!updates.has(r)) updates.set(r, {});
      updates.get(r)[key] = v;
    };

    // 1) Capture source values and queue source clears.
    const captured = [];
    for (let r = md.r1; r <= md.r2; r++) {
      for (let c = md.c1; c <= md.c2; c++) {
        const key = cols[c]?.key;
        if (!key) continue;
        const v = body.scenes[r]?.[key] ?? '';
        captured.push({ dr: r - md.r1, dc: c - md.c1, value: v });
        setUpdate(r, key, '');
      }
    }
    // 2) Apply captured values at the target position.
    for (const { dr, dc, value } of captured) {
      const tr = md.r1 + offsetR + dr;
      const tc = md.c1 + offsetC + dc;
      if (tr < 0 || tr >= body.scenes.length) continue;
      const key = cols[tc]?.key;
      if (!key) continue;
      setUpdate(tr, key, value);
    }

    const scenes = body.scenes.map((row, idx) => {
      const u = updates.get(idx);
      if (!u) return row;
      return { ...row, ...u };
    });
    setDraftBody({ ...body, scenes });

    // Move the selection + focus to the new location so the user sees where
    // their range landed and can immediately keep working on it.
    const isRange = md.r1 !== md.r2 || md.c1 !== md.c2;
    if (isRange) {
      setCellSelection({
        startRow: md.r1 + offsetR, startCol: md.c1 + offsetC,
        endRow:   md.r2 + offsetR, endCol:   md.c2 + offsetC,
      });
    } else {
      setCellSelection(null);
    }
    const newAnchorCol = cols[md.anchorCol + offsetC]?.key;
    if (newAnchorCol) setFocusedCell({ rowIdx: md.anchorRow + offsetR, colKey: newAnchorCol });
  };

  // Phase C2 (spec 5.2): autofill drag from the bottom-right handle. Both the
  // ref and the state are updated synchronously — the ref so the very next
  // mouseenter (which can fire before the useEffect sync) sees a live drag,
  // and the state so the preview tint renders.
  const startAutofillDrag = () => {
    let r1, r2, c1, c2;
    if (cellSelection && (cellSelection.startRow !== cellSelection.endRow || cellSelection.startCol !== cellSelection.endCol)) {
      r1 = Math.min(cellSelection.startRow, cellSelection.endRow);
      r2 = Math.max(cellSelection.startRow, cellSelection.endRow);
      c1 = Math.min(cellSelection.startCol, cellSelection.endCol);
      c2 = Math.max(cellSelection.startCol, cellSelection.endCol);
    } else if (focusedCell) {
      const colIdx = colKeyToIdx[focusedCell.colKey];
      if (colIdx == null) return;
      r1 = r2 = focusedCell.rowIdx;
      c1 = c2 = colIdx;
    } else return;
    const af = { sourceR1: r1, sourceR2: r2, sourceC1: c1, sourceC2: c2, targetEndRow: r2 };
    autofillDragRef.current = af;
    setAutofillDrag(af);
  };
  const updateAutofillTarget = (rowIdx) => {
    const cur = autofillDragRef.current;
    if (!cur) return;
    const next = { ...cur, targetEndRow: Math.max(cur.sourceR2, rowIdx) };
    autofillDragRef.current = next;
    setAutofillDrag(next);
  };
  const commitAutofillDrag = () => {
    const af = autofillDragRef.current;
    if (!af) return;
    setAutofillDrag(null);
    if (af.targetEndRow <= af.sourceR2) return;
    const body = draftBodyRef.current;
    if (!body) return;
    pushUndo(body);
    const fillCount = af.targetEndRow - af.sourceR2;
    const scenes = [...body.scenes];
    for (let c = af.sourceC1; c <= af.sourceC2; c++) {
      const key = cols[c]?.key;
      if (!key) continue;
      const sourceValues = [];
      for (let r = af.sourceR1; r <= af.sourceR2; r++) {
        sourceValues.push(body.scenes[r]?.[key] ?? '');
      }
      const fillValues = detectAndExtendColumn(sourceValues, fillCount);
      for (let i = 0; i < fillCount; i++) {
        const targetR = af.sourceR2 + 1 + i;
        if (targetR >= scenes.length) break;
        scenes[targetR] = { ...scenes[targetR], [key]: fillValues[i] };
      }
    }
    setDraftBody({ ...body, scenes });
    // Extend the selection to cover source + fill range so the user sees what landed.
    setCellSelection({
      startRow: af.sourceR1, startCol: af.sourceC1,
      endRow:   Math.min(af.targetEndRow, scenes.length - 1), endCol: af.sourceC2,
    });
  };

  // Phase B3: resolve the currently "active" cell grid. If a multi-cell range
  // is active, that's the grid. Otherwise the focused single cell. Returns
  // null when there's nothing to act on. Reads cellSelection/focusedCell from
  // the parent's render closure — each render redefines this with latest state,
  // and callers reach it via handlersLatestRef so they always see the latest.
  const getActiveCellGrid = () => {
    if (cellSelection && (cellSelection.startRow !== cellSelection.endRow || cellSelection.startCol !== cellSelection.endCol)) {
      const r1 = Math.min(cellSelection.startRow, cellSelection.endRow);
      const r2 = Math.max(cellSelection.startRow, cellSelection.endRow);
      const c1 = Math.min(cellSelection.startCol, cellSelection.endCol);
      const c2 = Math.max(cellSelection.startCol, cellSelection.endCol);
      const grid = [];
      for (let r = r1; r <= r2; r++) {
        const row = [];
        for (let c = c1; c <= c2; c++) {
          if (cols[c]) row.push({ rowIdx: r, colKey: cols[c].key, colIdx: c });
        }
        grid.push(row);
      }
      return grid;
    }
    if (focusedCell) {
      const colIdx = colKeyToIdx[focusedCell.colKey];
      if (colIdx == null) return null;
      return [[{ rowIdx: focusedCell.rowIdx, colKey: focusedCell.colKey, colIdx }]];
    }
    return null;
  };

  // Spec 2.1: clear every cell in the active grid. One undo per action.
  const clearActiveCells = () => {
    const grid = getActiveCellGrid();
    if (!grid || grid.length === 0) return;
    const body = draftBodyRef.current;
    if (!body) return;
    pushUndo(body);
    const targets = new Map(); // rowIdx -> Set<colKey>
    grid.flat().forEach(c => {
      if (!targets.has(c.rowIdx)) targets.set(c.rowIdx, new Set());
      targets.get(c.rowIdx).add(c.colKey);
    });
    const scenes = body.scenes.map((row, idx) => {
      const keys = targets.get(idx);
      if (!keys) return row;
      const next = { ...row };
      keys.forEach(k => { next[k] = ''; });
      return next;
    });
    setDraftBody({ ...body, scenes });
  };

  // Spec 2.3: cut = copy active grid to clipboard as TSV, then clear source.
  // TSV is the format spreadsheets use, so the user can paste into Sheets/Excel.
  const cutActiveCells = async () => {
    const grid = getActiveCellGrid();
    if (!grid || grid.length === 0) return;
    const body = draftBodyRef.current;
    if (!body) return;
    const tsv = grid.map(row =>
      row.map(c => {
        const v = body.scenes[c.rowIdx]?.[c.colKey];
        return String(v || '').replace(/\t/g, ' ').replace(/\r?\n/g, ' ');
      }).join('\t')
    ).join('\n');
    try { await navigator.clipboard.writeText(tsv); }
    catch { /* clipboard denied — still clear so the cut intent is honored */ }
    clearActiveCells();
  };

  // Spec 2.3: paste TSV from clipboard into cells starting at focused cell.
  // Distributes values row-by-row, column-by-column. Extras (beyond grid edge)
  // are dropped — we don't auto-add rows or columns.
  const pasteIntoActiveCells = async () => {
    if (!focusedCell) return;
    let text;
    try { text = await navigator.clipboard.readText(); }
    catch { return; }
    if (!text) return;
    const startCol = colKeyToIdx[focusedCell.colKey];
    if (startCol == null) return;
    const startRow = focusedCell.rowIdx;
    const lines = text.split(/\r?\n/);
    if (lines.length > 1 && lines[lines.length - 1] === '') lines.pop();
    const grid = lines.map(line => line.split('\t'));
    const body = draftBodyRef.current;
    if (!body) return;
    pushUndo(body);
    const scenes = [...body.scenes];
    for (let r = 0; r < grid.length; r++) {
      const targetRow = startRow + r;
      if (targetRow >= scenes.length) break;
      const next = { ...scenes[targetRow] };
      for (let c = 0; c < grid[r].length; c++) {
        const targetCol = cols[startCol + c];
        if (!targetCol) break;
        next[targetCol.key] = grid[r][c];
      }
      scenes[targetRow] = next;
    }
    setDraftBody({ ...body, scenes });
  };

  // Spec 2.4: fill down — single-cell only (range form belongs to autofill 5.2).
  // Copies the value from the cell directly above into the focused cell.
  const fillDownActiveCells = () => {
    if (!focusedCell || focusedCell.rowIdx <= 0) return;
    const body = draftBodyRef.current;
    if (!body) return;
    const above = body.scenes[focusedCell.rowIdx - 1]?.[focusedCell.colKey];
    if (above == null) return;
    pushUndo(body);
    const scenes = [...body.scenes];
    scenes[focusedCell.rowIdx] = { ...scenes[focusedCell.rowIdx], [focusedCell.colKey]: above };
    setDraftBody({ ...body, scenes });
  };

  // SceneRow handlers: keep the ref pointing at the latest functions every render,
  // and expose a *stable* rowHandlers object whose methods delegate through the ref.
  // Stability of rowHandlers is what lets React.memo on SceneRow actually skip work
  // on unchanged rows.
  handlersLatestRef.current = {
    updateScene, applyMarkdown, addRow, moveRow,
    handleGutterMouseDown, updateRowColor, updateCellColor,
    removeLink, saveLink, pushUndo, saveBody,
    enterEdit, exitEdit, revertEdit,
    startCellDrag, updateCellDrag, clearCellSelection, collapseCellSelection,
    clearActiveCells, cutActiveCells, pasteIntoActiveCells, fillDownActiveCells,
    startMoveDrag, updateMoveTarget, commitMoveDrag,
    startAutofillDrag, updateAutofillTarget, commitAutofillDrag,
    cellDragPointerMove, stopEdgeAutoScroll,
    addCharacterToRegistry,
  };
  const rowHandlers = useMemo(() => ({
    updateScene:           (...a) => handlersLatestRef.current.updateScene(...a),
    applyMarkdown:         (...a) => handlersLatestRef.current.applyMarkdown(...a),
    addRow:                (...a) => handlersLatestRef.current.addRow(...a),
    moveRow:               (...a) => handlersLatestRef.current.moveRow(...a),
    handleGutterMouseDown: (...a) => handlersLatestRef.current.handleGutterMouseDown(...a),
    updateRowColor:        (...a) => handlersLatestRef.current.updateRowColor(...a),
    updateCellColor:       (...a) => handlersLatestRef.current.updateCellColor(...a),
    removeLink:            (...a) => handlersLatestRef.current.removeLink(...a),
    saveLink:              (...a) => handlersLatestRef.current.saveLink(...a),
    pushUndo:              (...a) => handlersLatestRef.current.pushUndo(...a),
    saveBody:              (...a) => handlersLatestRef.current.saveBody(...a),
    // Set a focus target the layout effect will honor right after React commits.
    // Used when addRow is called from Enter / Tab at the bottom so cursor moves to the
    // new row in the same paint as the row appearing.
    queueFocus: (rowIdx, colKey) => { pendingFocusRef.current = { rowIdx, colKey }; },
    getTabOrigin:   ()  => tabOriginColRef.current,
    setTabOrigin:   (k) => { tabOriginColRef.current = k; },
    clearTabOrigin: ()  => { tabOriginColRef.current = null; },
    enterEdit:      (...a) => handlersLatestRef.current.enterEdit(...a),
    exitEdit:       (...a) => handlersLatestRef.current.exitEdit(...a),
    revertEdit:     (...a) => handlersLatestRef.current.revertEdit(...a),
    startCellDrag:       (...a) => handlersLatestRef.current.startCellDrag(...a),
    updateCellDrag:      (...a) => handlersLatestRef.current.updateCellDrag(...a),
    clearCellSelection:  ()     => handlersLatestRef.current.clearCellSelection(),
    collapseCellSelection: ()   => handlersLatestRef.current.collapseCellSelection(),
    clearActiveCells:    ()     => handlersLatestRef.current.clearActiveCells(),
    startMoveDrag:       (...a) => handlersLatestRef.current.startMoveDrag(...a),
    updateMoveTarget:    (...a) => handlersLatestRef.current.updateMoveTarget(...a),
    startAutofillDrag:   ()     => handlersLatestRef.current.startAutofillDrag(),
    updateAutofillTarget:(...a) => handlersLatestRef.current.updateAutofillTarget(...a),
    addCharacterToRegistry: (...a) => handlersLatestRef.current.addCharacterToRegistry(...a),
  }), []); // stable for the component's lifetime

  const handleExportSceneCSV = () => {
    if (!selected || !draftBody) return;
    exportSceneCSV({ scriptName: selected.scriptName, scriptBody: draftBody, hasBeenApproved: selected.hasBeenApproved });
  };

  const handleImportCSV = (e) => {
    const file = e.target.files[0];
    if (!file || !format) return;
    e.target.value = '';
    const reader = new FileReader();
    reader.onload = async (ev) => {
      const scenes = csvToScenes(ev.target.result, format);
      if (scenes.length === 0) {
        toast('No rows were imported. Check that the CSV headers match the downloaded template.', { tone: 'danger' });
        return;
      }
      const proceed = !draftBody?.scenes?.some(r => Object.values(r).some(v => v))
        || await confirm({
          title: 'Replace every row?',
          body: `The ${draftBody.scenes.length} rows in this script are replaced by the ${scenes.length} rows in the CSV.`,
          confirmLabel: 'Replace rows',
          destructive: true,
        });
      if (proceed) setDraftBody({ ...draftBody, scenes });
    };
    reader.readAsText(file);
  };

  // ── Spreadsheet clipboard sync handlers ──────────────────────────
  const hasRealScriptContent = (scenes) =>
    Array.isArray(scenes) && scenes.some(r =>
      Object.entries(r).some(([k, v]) => !k.startsWith('_') && typeof v === 'string' && v.trim())
    );

  const copyScriptToClipboard = async () => {
    if (!format || !draftBody?.scenes?.length || !hasRealScriptContent(draftBody.scenes)) {
      flashCopyFeedback('Nothing to copy: the script is empty.', 2000);
      return;
    }
    const tsv = buildScriptTSV(draftBody.scenes, format);
    const html = buildScriptHTML(draftBody.scenes, format);
    try {
      // Prefer ClipboardItem so we can write both text/plain AND text/html in one go.
      // Heliconflow→Heliconflow paste prefers text/html (round-trips colors + links);
      // Sheets paste also reads text/html so colors and hyperlinks come through there too.
      if (navigator.clipboard?.write && typeof window.ClipboardItem !== 'undefined') {
        const item = new window.ClipboardItem({
          'text/html':  new Blob([html], { type: 'text/html'  }),
          'text/plain': new Blob([tsv],  { type: 'text/plain' }),
        });
        await navigator.clipboard.write([item]);
      } else if (navigator.clipboard?.writeText) {
        // Older browsers: TSV only (colors/links lost on round-trip).
        await navigator.clipboard.writeText(tsv);
      } else {
        const ta = document.createElement('textarea');
        ta.value = tsv;
        ta.style.position = 'fixed';
        ta.style.left = '-9999px';
        ta.setAttribute('readonly', '');
        document.body.appendChild(ta);
        ta.select();
        const ok = document.execCommand('copy');
        document.body.removeChild(ta);
        if (!ok) throw new Error('execCommand copy returned false');
      }
      flashCopyFeedback('Script copied. Paste it into any spreadsheet.', 2000);
    } catch {
      flashCopyFeedback('Could not copy to clipboard. Try Ctrl+C after clicking the # cell.', 2800);
    }
  };

  // Direct paste — no modal, no header matching. Tries text/html first (full fidelity for
  // colors + hyperlinks), then falls back to text/plain TSV positional parse. Ctrl+Z restores.
  const pasteFromClipboard = async () => {
    if (!format || isReadOnly || !draftBody) return;
    let newScenes = null;
    // 1) HTML path — works for Heliconflow→Heliconflow and Sheets→Heliconflow alike.
    try {
      if (navigator.clipboard?.read) {
        const items = await navigator.clipboard.read();
        for (const item of items) {
          if (item.types?.includes('text/html')) {
            const blob = await item.getType('text/html');
            const html = await blob.text();
            if (html && html.trim()) {
              const parsed = parseScenesFromHTML(html, format);
              if (parsed && parsed.length > 0) { newScenes = parsed; break; }
            }
          }
        }
      }
    } catch { /* fall through to TSV */ }
    // 2) Plain text TSV fallback.
    if (!newScenes) {
      try {
        if (navigator.clipboard?.readText) {
          const text = await navigator.clipboard.readText();
          if (text && text.trim()) newScenes = parseScenesPositional(text, format, '\t');
        }
      } catch { /* leave newScenes null */ }
    }
    if (!newScenes || newScenes.length === 0) {
      flashCopyFeedback('The clipboard could not be read. Copy a range in your spreadsheet, then try again.', 2800);
      return;
    }
    pushUndo(draftBody);
    setDraftBody({ ...draftBody, scenes: newScenes });
    setSelectAllActive(false);
    flashCopyFeedback(
      `${newScenes.length} row${newScenes.length !== 1 ? 's' : ''} pasted from clipboard.`,
      2500
    );
  };

  // Copy the current script into a standalone "Copy-<name>" script and open it in
  // a new browser tab (so the original and the copy can be edited concurrently).
  const handleDuplicate = async () => {
    if (!selected) return;
    const win = window.open('about:blank', '_blank'); // open within the click gesture
    try {
      const res = await api.post(`/api/scripts/${selected.id}/duplicate`);
      const id = res.data.id;
      await loadScripts();
      const url = `${window.location.origin}/scripts?script=${id}`;
      if (win) win.location = url; else window.open(url, '_blank');
    } catch (err) {
      if (win) win.close();
      toast(clean(err.response?.data?.error) || 'The script was not duplicated. Try again.', { tone: 'danger' });
    }
  };

  const handleDeleteScript = async () => {
    if (!selected) return;
    const ok = await confirm({
      title: 'Delete this script?',
      body: `${clean(selected.scriptName)} moves to Deleted with its version history and any production cards, where an admin can restore it.`,
      confirmLabel: 'Delete script',
      destructive: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/api/scripts/${selected.id}`);
      goBackToList();
    } catch (err) {
      toast(clean(err.response?.data?.error) || 'The script was not deleted. Try again.', { tone: 'danger' });
    }
  };

  const availableTransitions = getAvailableTransitions(selected, user);
  // Pick the right editor column percentages/drag fn for the current format
  // When the Production Notes column is actually rendered (approved + a format
  // that gets it — not AI), use the "with Production Notes" width arrays so the
  // saved widths and drag handles line up 1:1 with the on-screen columns.
  const useProdCols = showProdNotesCol && PROD_NOTES_INSERT_IDX[format] != null;
  const editorColPcts = useProdCols
    ? (format === 'TH' ? thEditorPctsP : format === 'Mockumentary' ? mockEditorPctsP : format === 'Recipe' ? recipeEditorPctsP : format === 'Longform' ? lfEditorPctsP : skitEditorPctsP)
    : (format === 'TH' ? thEditorPcts  : format === 'AI' ? aiEditorPcts  : format === 'Mockumentary' ? mockEditorPcts  : format === 'Recipe' ? recipeEditorPcts  : format === 'Longform' ? lfEditorPcts : skitEditorPcts);
  const startEditorColDrag = useProdCols
    ? (format === 'TH' ? startThEditorDragP : format === 'Mockumentary' ? startMockEditorDragP : format === 'Recipe' ? startRecipeEditorDragP : format === 'Longform' ? startLfEditorDragP : startSkitEditorDragP)
    : (format === 'TH' ? startThEditorDrag  : format === 'AI' ? startAiEditorDrag  : format === 'Mockumentary' ? startMockEditorDrag  : format === 'Recipe' ? startRecipeEditorDrag  : format === 'Longform' ? startLfEditorDrag : startSkitEditorDrag);
  const resetEditorCols = useProdCols
    ? (format === 'TH' ? resetThEditorColsP : format === 'Mockumentary' ? resetMockEditorColsP : format === 'Recipe' ? resetRecipeEditorColsP : format === 'Longform' ? resetLfEditorColsP : resetSkitEditorColsP)
    : (format === 'TH' ? resetThEditorCols  : format === 'AI' ? resetAiEditorCols  : format === 'Mockumentary' ? resetMockEditorCols  : format === 'Recipe' ? resetRecipeEditorCols  : format === 'Longform' ? resetLfEditorCols : resetSkitEditorCols);
  const isReviewerOrAdmin      = user?.roles?.some(r => r === 'Qual Reviewer' || r === 'Admin' || r === 'Power User');
  const canSeeQualNotesPanel   = user?.roles?.some(r => ['Admin','Power User','Qual Reviewer','Writer'].includes(r));
  // Per-scene qualNotes column: visible to reviewers always, and to writers only when the script needs revision.
  const showQualNotesCol       = canSeeQualNotesPanel && (isReviewerOrAdmin || selected?.qualStatus === 'Needs Revision');
  // The stored defaults reserve a slice for the Qual Notes column. When that column
  // isn't rendered (any non-Reviewer/Admin role on a non-revision script), the
  // content-column percentages sum to ~80%, leaving ~20% unaccounted — which
  // table-layout:fixed then redistributes onto the fixed-pixel # column, ballooning
  // it to 20-30% of table width. Rescale to 100% for the rendered subset so the
  // table lays out cleanly regardless of which columns are visible.
  // `editorColPcts` already matches the rendered content columns (the prod-notes
  // variant includes the Production Notes column in place), with the qualNotes
  // width as its trailing slot. Slice content vs qualNotes by the rendered column
  // count and rescale to 100% so table-layout:fixed doesn't dump leftover width
  // onto the fixed-pixel # column when qualNotes is hidden.
  const { renderedColPcts, qualNotesPct } = (() => {
    const raw        = editorColPcts;
    const contentLen = cols.length;            // rendered content cols (incl. prodNotes when shown)
    const contentRaw = raw.slice(0, contentLen);
    const qnRaw      = raw[contentLen];         // qualNotes slot
    const full = showQualNotesCol ? [...contentRaw, qnRaw] : contentRaw;
    const sum  = full.reduce((a, b) => a + (b || 0), 0);
    const scaled = sum > 0 ? full.map(p => ((p || 0) * 100) / sum) : full;
    return showQualNotesCol
      ? { renderedColPcts: scaled.slice(0, contentLen), qualNotesPct: scaled[contentLen] }
      : { renderedColPcts: scaled, qualNotesPct: null };
  })();
  // Writable by reviewers always; also by the script's own writer when status is Needs Revision (so they can address feedback).
  const canEditQualNotes       = selected?.qualStatus !== 'Posted' &&
                                 (isReviewerOrAdmin || (selected?.writer?.id === user?.id && selected?.qualStatus === 'Needs Revision'));
  const isShootLeadForScript   = location.state?.isShootLeadForScript;
  const fromVideoCard = location.state?.fromVideoCard;
  // Writers may edit any script's body regardless of owner (team policy), so
  // 'Writer' is included alongside the reviewer/admin roles. Read-only only if
  // the user has none of these roles, isn't the script's writer, and isn't a
  // Producer navigated from a Video Card. Mirrors the backend gate in scripts.js.
  // A9: a Posted script is a shipped artifact — read-only for EVERY role, Admins
  // included. This overrides all edit permissions. The escape hatch is the
  // un-post path (pull a card back from Posted → auto-reverts to Approved), not
  // an admin override. `is_archived` has NO effect on editability.
  const isPosted = selected?.qualStatus === 'Posted';
  const canEditBody = !isPosted && (
                      hasRole('Admin', 'Manager', 'Power User', 'Qual Reviewer', 'Writer') ||
                      selected?.writer?.id === user?.id ||
                      (isShootLeadForScript && (hasRole('Producer'))));
  const isReadOnly  = !!selected && !canEditBody;
  // Title can be edited by anyone who can edit the script body, but is locked
  // while the script is Approved/Posted or once production has started. The
  // server decides (titleLocked / titleLockReason from PATCH+GET) so the two
  // can't drift — a narrower role list here (added in cd81e40) silently hid the
  // editable input from Managers / Qual Reviewers / non-owner Writers.
  // Owner 2026-09-08: a past approval no longer locks the title forever —
  // pull the script back to Needs Review and it's editable again.
  // Fallback (a `selected` that didn't come from the detail fetch): assume
  // locked for Approved/Posted rather than offering an input the server refuses.
  const titleLocked = selected?.titleLocked
    ?? (selected?.qualStatus === 'Approved' || selected?.qualStatus === 'Posted');
  const canEditTitle = canEditBody && !titleLocked;
  // C4: Duplicate follows the ORIGINAL writer, so a pure Writer (Writer but not
  // Reviewer/Admin/Power User) duplicating someone else's script would create one
  // they can't see — hide the button (the endpoint also 403s). External client
  // users don't create scripts.
  const isPureWriter = hasRole('Writer') && !hasRole('Admin', 'Power User', 'Qual Reviewer');
  const canDuplicate = !!selected && !user?.isExternal &&
                       !(isPureWriter && selected?.writer?.id !== user?.id);

  // Template create/use is Writer, Qual Reviewer, Power User or Admin (mirrors
  // requireTemplateRole on the server). Deleting one is Admin/PU only. The
  // server enforces both independently — this only hides the controls.
  const canUseTemplates    = !user?.isExternal && hasRole('Writer', 'Qual Reviewer', 'Power User', 'Admin');


  const saveTitleIfChanged = async () => {
    const trimmed = draftTitle.trim();
    if (!trimmed || trimmed === selected?.scriptName) return;
    try {
      const res = await api.patch(`/api/scripts/${selected.id}`, { scriptName: trimmed });
      setSelected(prev => ({ ...prev, scriptName: res.data.scriptName }));
      setScripts(prev => prev.map(s => s.id === selected.id ? { ...s, scriptName: res.data.scriptName } : s));
    } catch (err) {
      console.error('Failed to rename script:', err);
      setDraftTitle(selected.scriptName);
      // A silent revert reads as a broken input; the server's reason (usually a
      // locked title) is the only thing that explains it.
      showError('The title was not changed', err.response?.data?.error || 'Try again.');
    }
  };

  // HD-7 split: the page reaches the editor through this handle, and hides the list while a script is open.
  useImperativeHandle(ref, () => ({ selectScript }));
  useLayoutEffect(() => { onOpenChange?.(!!selected); }, [selected]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Editor view ──────────────────────────────────────────────────

  if (selected) {
    // Stage and condition from the detail response (HD-8 A0 added `stage` / `stageCondition`, as the list); the
    // fallback covers a response from an older API.
    const stage = STAGES.includes(selected.stage) ? selected.stage
      : (selected.productionStarted && selected.qualStatus === 'Approved' ? 'Filming' : stageOf({ qualStatus: selected.qualStatus }));
    const condition = selected.stageCondition
      || conditionOf({ qualStatus: selected.qualStatus, cardStatus: IN_PRODUCTION_STAGES.includes(stage) && selected.qualStatus !== 'Posted' ? stage : undefined });
    const backTo = backToRef.current;
    const backLabel = backTo
      ? `Back to ${(String(backTo.label || '').replace(/^[\s←]+/, '') || 'the previous page').toLowerCase()}`
      : fromVideoCard ? 'Back to the production' : 'Back to scripts';
    const onBack = backTo
      ? () => { saveBody(); navigate(backTo.path, { state: backTo.state }); }
      : fromVideoCard ? () => navigate(-1) : goBackToList;
    const isLongform = format === 'Longform' || selected?.scriptBody?.format === 'Longform';
    const changeFontSize = next => {
      setEditorFontSize(next);
      if (selected?.id) localStorage.setItem(`fontSize_script_${selected.id}`, next);
    };
    const saveStatus = isReadOnly ? null
      : saveError?.retrying ? <Badge tone="warn">Reconnecting, retrying the save</Badge>
      : saveError ? null
      : saving ? <span className="ui-meta">Saving</span>
      : isDirty ? <Badge tone="warn" icon={Pencil}>Unsaved</Badge>
      : lastSaved ? <span className="ui-meta">Saved {lastSaved.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}</span>
      : null;
    const fmtName = format === 'TH' ? 'Talking head' : format;

    return (
      <div className="ui ui-page ui-page--flush">
        <EditorHeader
          title={canEditTitle ? (
            <input
              className="ui-titlefield"
              aria-label="Script title"
              size={Math.max(12, draftTitle.length + 2)}
              value={draftTitle}
              onChange={e => setDraftTitle(e.target.value)}
              onBlur={saveTitleIfChanged}
              onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
            />
          ) : (
            <span title={titleLocked
              ? (clean(selected?.titleLockReason) || 'The title is locked. Move this script back to Needs review to rename it.')
              : undefined}>{clean(selected.scriptName)}</span>
          )}
          meta={(
            <span className="ui-cluster">
              <StageChip stage={stage} />
              {condition && <Badge condition={condition} />}
              {selected.qualStatus === 'Backburner' && <BackburnerBadge />}
              {selected.client?.name && <span>{clean(selected.client.name)}</span>}
              {format && <span data-testid="script-format-badge"><Tag title={format === 'Longform' ? 'Longform series: episodes are production cards' : undefined}>{fmtName}</Tag></span>}
              <span className="ui-num">Version {selected.version}</span>
            </span>
          )}
          actions={(
            <>
              {saveStatus}
              {isReadOnly ? (
                <Tag>Read only</Tag>
              ) : (
                <>
                  {availableTransitions.filter(s => s !== 'Backburner').map(s => (
                    <Button key={s}
                      variant={s === 'Approved' || (s === 'Needs Review' && selected.qualStatus !== 'Approved') ? 'primary' : 'secondary'}
                      icon={s === 'Approved' ? Check : s === 'Needs Review' ? (selected.qualStatus === 'Approved' ? Undo2 : Send) : s === 'I/P' ? Undo2 : Pencil}
                      onClick={() => handleTransition(s)} disabled={transitioning}>
                      {transitionBtnLabel(s, selected)}
                    </Button>
                  ))}
                  {availableTransitions.includes('Backburner') && (
                    <Button variant="quiet" onClick={() => handleTransition('Backburner')} disabled={transitioning}>Backburner</Button>
                  )}
                  <Button variant="secondary" icon={Save} onClick={() => saveBody(true)}>Save</Button>
                </>
              )}
              {/* Export: Share / Print / Word / CSV. Word and CSV need a scene table, so they only appear when the
                  script has a format (the old per-button conditions). */}
              <EditorMenu
                label="Export"
                icon={Download}
                items={[
                  { label: 'Share', icon: Share2, onSelect: () => setShareOpen(true) },
                  { label: 'Print or save as PDF', icon: Printer, onSelect: handlePrint },
                  { label: 'Export Word', icon: FileDown, onSelect: format ? handleExportDocx : null },
                  { label: 'Export CSV', icon: FileDown, onSelect: format ? handleExportSceneCSV : null },
                ]}
              />
              {/* Actions: each item keeps the exact condition its old button had (a null handler hides it). Delete is
                  last, below a separator. */}
              <EditorMenu
                label="Actions"
                icon={Ellipsis}
                items={[
                  {
                    label: 'Copy link',
                    icon: Link2,
                    onSelect: () => {
                      navigator.clipboard.writeText(`${window.location.origin}/scripts?script=${selected.id}`)
                        .then(() => toast('Link to this script copied.'))
                        .catch(() => toast('The link was not copied. Copy it from the address bar.', { tone: 'danger' }));
                    },
                  },
                  { label: 'Find and replace', icon: Search, onSelect: (!isReadOnly && format) ? (() => setFindReplaceOpen(true)) : null },
                  { label: 'Duplicate in a new tab', icon: CopyPlus, onSelect: canDuplicate ? handleDuplicate : null },
                  {
                    label: 'Save as template',
                    icon: Copy,
                    onSelect: canUseTemplates
                      ? (() => setSaveTplPrompt({ scriptId: selected.id, defaultName: selected.scriptName || '' }))
                      : null,
                  },
                  {
                    label: 'Delete script',
                    icon: Trash2,
                    danger: true,
                    dividerBefore: true,
                    onSelect: (hasRole('Admin', 'Qual Reviewer', 'Power User') || selected.writer?.id === user?.id) ? handleDeleteScript : null,
                  },
                ]}
              />
              <Button
                variant="quiet"
                icon={Plus}
                title="Create another script for this client. It opens in a new tab so you can write both at once."
                onClick={() => { setNewScriptDefaults({ clientId: selected.client?.id || '', openInNewTab: true }); setShowNew(true); }}
              >
                New script
              </Button>
            </>
          )}
        />

        <div className="ui-inset ui-inset--bar ui-toolbar" role="toolbar" aria-label="Script tools">
          <Button variant="quiet" icon={ArrowLeft} onClick={onBack}>{backLabel}</Button>
          {format && (
            <span className="ui-btngroup" role="group" aria-label="Text style">
              {[['**', 'Bold', Bold, 'B'], ['*', 'Italic', Italic, 'I'], ['__', 'Underline', Underline, 'U']].map(([marker, name, glyph, key]) => (
                <Button
                  key={marker}
                  variant="quiet"
                  iconOnly
                  icon={glyph}
                  label={`${name} (Ctrl+${key})`}
                  disabled={!focusedCell}
                  onMouseDown={e => { e.preventDefault(); if (focusedCell) applyMarkdown(marker, focusedCell.rowIdx, focusedCell.colKey); }}
                />
              ))}
            </span>
          )}
          {format && (
            <span className="ui-btngroup" role="group" aria-label="Text size">
              <Button variant="quiet" iconOnly icon={Minus} label="Smaller text" onClick={() => changeFontSize(Math.max(8, editorFontSize - 1))} />
              <span className="ui-num" aria-live="polite" aria-label={`Text size ${editorFontSize}`}>{editorFontSize}</span>
              <Button variant="quiet" iconOnly icon={Plus} label="Larger text" onClick={() => changeFontSize(Math.min(24, editorFontSize + 1))} />
            </span>
          )}
          {format && (
            <Button variant="quiet" icon={Columns3} onClick={resetEditorCols}
              title="Reset the column widths to their defaults (use it if the row number column looks too wide)">
              Reset columns
            </Button>
          )}
          {/* Cross-script copy: shows whenever cells or rows are selected. */}
          {format && getActiveSelection() && (
            <Button variant="quiet" icon={Copy} onClick={copySelectionToAppClipboard}
              title="Copy the selection to paste into this or any other script (Ctrl+C)">
              Copy selection
            </Button>
          )}
          {/* Cross-script paste: the app clipboard persists across scripts, so it shows on any script while it holds rows. */}
          {format && !isReadOnly && appClipCount > 0 && (
            <Button variant="secondary" icon={ClipboardPaste} onClick={pasteFromAppClipboard} title="Paste the copied rows here (Ctrl+V)">
              Paste copied ({appClipCount})
            </Button>
          )}
          {appClipCount > 0 && (
            <Button variant="quiet" icon={X} onClick={() => { clearAppClip(); toast('Clipboard cleared.', { tone: 'info' }); }}>Clear clipboard</Button>
          )}
          {selected.sourceUrl && (
            <a className="ui ui-btn ui-btn--quiet" href={selected.sourceUrl} target="_blank" rel="noreferrer"
              title="Open the original source of this script in a new tab">
              <Icon icon={ExternalLink} />View original
            </a>
          )}
          <span className="ui-spacer" />
          {/* Live spoken-word count and read-aloud runtime. Hidden for formats with no spoken column (None / Riff). Not
              gated by isReadOnly: a reviewer needs the runtime too, and WPM is a personal preference. */}
          {spokenKey && (
            <span className="ui-cluster ui-cluster--nowrap" title="Spoken words and the estimated read-aloud runtime">
              <span className="ui-meta ui-num">{formatWordCount(spokenWordCount)} words, {formatRuntime(spokenWordCount, wpm)} at</span>
              <input
                className="ui-input ui-input--num"
                type="text"
                inputMode="numeric"
                value={wpmInput}
                onChange={handleWpmChange}
                onBlur={handleWpmBlur}
                onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                aria-label="Words per minute"
                title={`Words per minute (${WPM_MIN} to ${WPM_MAX})`}
              />
              <span className="ui-meta">words a minute</span>
            </span>
          )}
          {selected && (
            <label className="ui-cluster ui-cluster--nowrap" title="How long the finished video will run. Blank uses the estimate from the spoken words, 250 a minute. The value when the script is approved is the one that counts.">
              <span className="ui-meta">Minutes</span>
              <input
                className="ui-input ui-input--num"
                type="text"
                inputMode="decimal"
                data-testid="script-runtime"
                value={runtimeText}
                placeholder={estimateLabel(spokenWordCount) || 'Not set'}
                disabled={!canEditRuntime}
                onChange={e => setRuntimeText(e.target.value)}
                onBlur={saveRuntime}
                onKeyDown={e => {
                  if (e.key === 'Enter') e.currentTarget.blur();
                  if (e.key === 'Escape') { setRuntimeText(minutesText(selected.runtime_minutes)); }
                }}
                aria-label={`Minutes, blank uses the estimate${estimateLabel(spokenWordCount) ? ` (${estimateLabel(spokenWordCount).replace('≈ ', 'about ')})` : ''}`}
              />
            </label>
          )}
          <Button
            variant="quiet"
            iconOnly
            icon={ArrowDownToLine}
            label="Skip to the bottom of the script"
            onClick={() => {
              const scroller = document.querySelector('.script-scroll-body');
              if (scroller) scroller.scrollTo({ top: scroller.scrollHeight, behavior: 'smooth' });
            }}
          />
          {/* SEC-2: Clio answers from the whole database, so the API refuses client accounts; they get no Clio button. */}
          {!user?.isExternal && <Button variant="quiet" icon={MessageSquareText} aria-pressed={clioOpen} onClick={() => setClioOpen(o => !o)}>Clio</Button>}
        </div>

        {/* Save failures: a blocked large deletion, or a save that did not go through. */}
        {!isReadOnly && saveError && !saveError.retrying && (
          <div className="ui-inset ui-inset--bar ui-toolbar" role="alert">
            {saveError.largeDeletion ? (
              <>
                <Badge tone="danger">Large deletion blocked</Badge>
                <span className="ui-dense ui-text-2">{clean(saveError.largeDeletion.message) || 'This save would remove most of the script. Your copy may be out of date.'}</span>
                <Button variant="secondary" onClick={() => selectScript(selected.id)}>Reload latest</Button>
                <Button variant="danger" onClick={saveAnyway}>Save anyway</Button>
              </>
            ) : (
              <>
                <Badge tone="danger">Not saved</Badge>
                <span className="ui-dense ui-text-2">
                  {saveError.tooLarge
                    ? 'The script is too large to save. Tell an admin: the save size limit needs raising.'
                    : 'The last save did not go through. Your latest edits are not on the server yet.'}
                </span>
                <Button variant="secondary" onClick={() => saveBody(true)}>Retry</Button>
              </>
            )}
          </div>
        )}
        {errorNote && (
          <div className="ui-inset ui-inset--bar ui-toolbar" role="alert">
            <Badge tone="danger">{errorNote.what}</Badge>
            <span className="ui-dense ui-text-2">{errorNote.message}</span>
            <Button variant="quiet" onClick={() => setErrorNote(null)}>Dismiss</Button>
          </div>
        )}
        {/* P2: same-cell collision notice. Non-blocking: your edit was saved; this flags that someone else had also
            changed those cells. */}
        {!isReadOnly && cellConflicts.length > 0 && (
          <div className="ui-inset ui-inset--bar ui-toolbar" role="status">
            <Badge tone="warn">{cellConflicts.length} cell{cellConflicts.length === 1 ? '' : 's'} also edited elsewhere</Badge>
            <span className="ui-dense ui-text-2">Another editor changed {cellConflicts.length === 1 ? 'this cell' : 'these cells'} while you were editing. Your version was saved.</span>
            <Button variant="quiet" onClick={() => setCellConflicts([])}>Dismiss</Button>
          </div>
        )}

        {/* Editor column + Clio drawer */}
        <div className="ui-work">
          <div className="ui-workcol">
            {/* The scroller (the drag-select auto-scroll and Skip to bottom find it by its hook class) */}
            <div className="script-scroll-body ui-pane ui-stack">
              {/* In transcript: CTF clients only */}
              {selected.client?.serviceType === 'CTF' && (
                <Checkbox
                  label="In transcript"
                  checked={!!selected.inTranscript}
                  onChange={async e => {
                    try {
                      const res = await api.patch(`/api/scripts/${selected.id}`, { inTranscript: e.target.checked });
                      setSelected(res.data);
                    } catch (err) {
                      console.error('Failed to update inTranscript:', err);
                    }
                  }}
                />
              )}

              {/* Reviewer notes: visible to qual-note roles, editable by reviewers */}
              {canSeeQualNotesPanel && (
                <Textarea
                  label="Reviewer notes"
                  rows={2}
                  value={draftNotes}
                  spellCheck={true}
                  onChange={isReviewerOrAdmin && !isPosted ? e => setDraftNotes(e.target.value) : undefined}
                  onBlur={isReviewerOrAdmin && !isPosted ? () => saveBody() : undefined}
                  readOnly={!isReviewerOrAdmin || isPosted}
                  placeholder={isReviewerOrAdmin ? 'Overall notes for the writer' : ''}
                />
              )}

              {/* The series panel works only through the longform API, which refuses external accounts. */}
              {isLongform && !user?.isExternal && cols.length > 0 && draftBody?.scenes != null && (
                <LongformSeriesPanel
                  script={selected}
                  draftBody={draftBody}
                  canEdit={!isReadOnly}
                  registryNames={registryNames}
                  onReload={() => selectScript(selected.id)}
                  user={user}
                  refreshKey={lastSaved}
                  onInsertEpisodeBreak={() => insertEpisodeBreakAt(draftBody?.scenes?.length || 0)}
                  onStartWriting={startWritingScene1}
                  onAddEpisode={addEpisodeToScript}
                  onSetEpisodeTitle={setEpisodeTitleInScript}
                  episodesVersion={lfEpisodesVersion}
                  onSceneAdded={handleLongformSceneAdded}
                  onEpisodesChanged={eps => setLfEpisodes(eps)}
                />
              )}

              {/* Scene table */}
              {cols.length > 0 && draftBody?.scenes != null ? (
                <div className="ui-region">
                  {/* Hidden catcher: focused while select-all is active so paste / copy events fire on it (not on a cell
                      textarea, not on nothing). */}
                  {selectAllActive && (
                    <textarea
                      ref={pasteCatcherRef}
                      className="ui-catcher"
                      aria-hidden="true"
                      tabIndex={-1}
                      readOnly
                      value=""
                      onChange={() => {}}
                      onKeyDown={(e) => {
                        if (e.key === 'Escape') {
                          e.preventDefault();
                          setSelectAllActive(false);
                        }
                      }}
                      onPaste={(e) => {
                        e.preventDefault();
                        if (isReadOnly || !format || !draftBody) return;
                        let newScenes = null;
                        const html = e.clipboardData?.getData('text/html');
                        if (html && html.trim()) {
                          const parsed = parseScenesFromHTML(html, format);
                          if (parsed && parsed.length > 0) newScenes = parsed;
                        }
                        if (!newScenes) {
                          const text = e.clipboardData?.getData('text/plain') || '';
                          if (text.trim()) newScenes = parseScenesPositional(text, format, '\t');
                        }
                        if (!newScenes || newScenes.length === 0) {
                          flashCopyFeedback('Clipboard is empty.', 2000);
                          return;
                        }
                        pushUndo(draftBody);
                        setDraftBody({ ...draftBody, scenes: newScenes });
                        setSelectAllActive(false);
                        flashCopyFeedback(
                          `${newScenes.length} row${newScenes.length !== 1 ? 's' : ''} pasted from clipboard.`,
                          2500
                        );
                      }}
                      onCopy={(e) => {
                        e.preventDefault();
                        if (!format || !draftBody?.scenes?.length || !hasRealScriptContent(draftBody.scenes)) {
                          flashCopyFeedback('Nothing to copy: the script is empty.', 2000);
                          return;
                        }
                        const tsv  = buildScriptTSV(draftBody.scenes, format);
                        const html = buildScriptHTML(draftBody.scenes, format);
                        e.clipboardData?.setData('text/plain', tsv);
                        e.clipboardData?.setData('text/html',  html);
                        flashCopyFeedback('Script copied. Paste it into any spreadsheet.', 2000);
                      }}
                    />
                  )}
                  {/* Contextual bar while the # select-all is active */}
                  {selectAllActive && (
                    <div data-clipboard-toolbar className="ui-selbar">
                      <span className="ui-dense">All {draftBody.scenes.length} row{draftBody.scenes.length !== 1 ? 's' : ''} selected</span>
                      <Button variant="secondary" icon={Copy} onClick={copyScriptToClipboard} title="Copy the script as a table to the system clipboard (Ctrl+C)">
                        Copy to clipboard
                      </Button>
                      {!isReadOnly && (
                        <Button variant="secondary" icon={ClipboardPaste} onClick={pasteFromClipboard} title="Paste a range from Google Sheets or Excel (Ctrl+V)">
                          Paste from clipboard
                        </Button>
                      )}
                      <span className="ui-meta">Escape or a click outside clears the selection</span>
                    </div>
                  )}
                  {/* D-117: a blank Longform series shows the panel's entry choices in place of the empty grid. */}
                  <table className="scene-table ui-sheet" hidden={isLongform && !isReadOnly && (draftBody?.scenes?.length || 0) === 0}>
                    <thead className="scene-head ui-sheet__head">
                      <tr>
                        <th className="ui-sheet__th ui-sheet__th--all">
                          <button
                            type="button"
                            className="ui-sheet__allbtn"
                            aria-pressed={selectAllActive}
                            aria-label="Select all rows for copy and paste with a spreadsheet"
                            title="Select all rows for copy and paste with Google Sheets"
                            onClick={() => {
                              // Drop focus out of any cell so the next Ctrl+V doesn't get
                              // swallowed by a textarea's native paste handler.
                              const a = document.activeElement;
                              if (a && (a.tagName === 'TEXTAREA' || a.tagName === 'INPUT')) a.blur();
                              setSelectAllActive(v => !v);
                            }}
                          >#</button>
                        </th>
                        {cols.map((c, colIdx) => {
                          const isLast = colIdx === cols.length - 1 && !showQualNotesCol;
                          return (
                            <th key={c.key} className="ui-sheet__th" style={{ width: `${renderedColPcts[colIdx]}%` }}>
                              {c.key === 'character' && isCharacterFormat && registryClientId ? (
                                // Run 2 §4.2: the Character column header opens the client's character registry.
                                <button
                                  type="button"
                                  className="ui-link"
                                  data-testid="character-header"
                                  title="Manage this client's character list"
                                  onClick={() => setRegistryModalOpen(true)}
                                >
                                  {sentenceLabel(c.label)}<Icon icon={ChevronDown} />
                                </button>
                              ) : sentenceLabel(c.label)}
                              {!isLast && (
                                <div className="ui-sheet__resize" aria-hidden="true" onMouseDown={e => startEditorColDrag(colIdx, e)} />
                              )}
                            </th>
                          );
                        })}
                        {showQualNotesCol && (
                          <th className="ui-sheet__th" style={{ width: `${qualNotesPct}%` }}>Qual notes</th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {draftBody.scenes.map((row, idx) => {
                        const totalRows = draftBody.scenes.length;
                        const gp = lfGroupPlan ? lfGroupPlan[idx] : null;
                        const bandSpan = cols.length + 1 + (showQualNotesCol ? 1 : 0);
                        const ep = gp?.episodeBand;
                        const epOpen = ep ? !lfCollapsedEpisodes.has(ep.key) : true;
                        const epEditing = ep && lfBandEditing?.k === ep.episodeNumber;
                        const sc = gp?.sceneBand;
                        const scOpen = sc ? !lfCollapsedScenes.has(sc.key) : true;
                        const bands = gp && (ep || sc) ? (
                          <>
                            {ep && (
                              <tr className="ui-sheet__band" data-level="episode" data-testid="lf-band-episode" data-band-key={ep.key} data-episode={ep.episodeNumber ?? undefined} data-card={ep.cardId ? 'true' : 'false'}>
                                <td colSpan={bandSpan}>
                                  <div className="ui-sheet__bandrow">
                                    <button type="button" className="ui-sheet__bandbtn" aria-expanded={epOpen} onClick={() => lfToggleEpisode(ep.key)}
                                      aria-label={epEditing ? `${epOpen ? 'Collapse' : 'Expand'} episode ${ep.episodeNumber}` : undefined}
                                      title={epOpen ? 'Collapse this episode' : 'Expand this episode'}>
                                      <Icon icon={ChevronDown} />
                                      {!epEditing && (
                                        <>
                                          <span className="ui-sheet__bandlabel">Episode <span className="ui-sheet__no">{ep.episodeNumber}</span></span>
                                          <span className="ui-sheet__banddesc">{ep.title || <em>Untitled</em>}</span>
                                          <span className="ui-meta">{ep.range}</span>
                                        </>
                                      )}
                                    </button>
                                    {epEditing && (
                                      <Input autoFocus aria-label={`Episode ${ep.episodeNumber} title`} data-testid="lf-band-title-input" value={lfBandEditing.value}
                                        onClick={e => e.stopPropagation()} onChange={e => setLfBandEditing({ k: ep.episodeNumber, value: e.target.value })}
                                        onBlur={() => { const v = String(lfBandEditing?.value || '').trim(); setLfBandEditing(null); if (v !== (ep.title || '')) setEpisodeTitleInScript(ep.episodeNumber, v); }}
                                        onKeyDown={e => { e.stopPropagation(); if (e.key === 'Enter') e.currentTarget.blur(); if (e.key === 'Escape') setLfBandEditing(null); }} />
                                    )}
                                    {!ep.cardId ? <span className="ui-meta">No card yet</span>
                                      : ep.state === 'Planned' ? <Tag>Planned</Tag>
                                      : <StageChip stage={stageOf({ cardStatus: ep.state })} />}
                                    {!isReadOnly && <Button variant="quiet" iconOnly icon={Pencil} data-testid="lf-band-rename"
                                      label={ep.untitled ? 'Name this episode (writes its EPISODE header row)' : 'Rename this episode (edits its EPISODE header row)'}
                                      onClick={() => setLfBandEditing({ k: ep.episodeNumber, value: ep.title || '' })} />}
                                    {ep.cardId && <Button variant="quiet" icon={ExternalLink} data-testid="lf-band-open-card" title="Open this episode's production card" onClick={() => navigate(`/production/${ep.cardId}`)}>Card</Button>}
                                    {!isReadOnly && ep.episodeNumber > 1 && (
                                      <Button variant="danger" icon={X} data-testid="lf-band-remove" title="Remove this episode break: the rows below join the episode above"
                                        onClick={async () => {
                                          const k = ep.episodeNumber;
                                          const ok = await confirm({
                                            title: `Remove the break that starts episode ${k}?`,
                                            body: `Its rows join episode ${k - 1}.`,
                                            confirmLabel: 'Remove break',
                                            destructive: true,
                                          });
                                          if (ok) removeEpisodeBoundary(k);
                                        }}>
                                        Break
                                      </Button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            )}
                            {sc && (
                              <tr className="ui-sheet__band" data-level="scene" data-testid="lf-band-scene" data-scene={sc.sceneNumber} data-episode={sc.episodeNumber ?? undefined}>
                                <td colSpan={bandSpan}>
                                  <button type="button" className="ui-sheet__bandbtn" aria-expanded={scOpen} onClick={() => lfToggleScene(sc.key)} title={scOpen ? 'Collapse this scene' : 'Expand this scene'}>
                                    <Icon icon={ChevronDown} />
                                    <span className="ui-sheet__bandlabel">{sc.sceneNumber ? <>Scene <span className="ui-sheet__no">{sc.sceneNumber}</span></> : 'Unnumbered'}</span>
                                    {sc.description && <span className="ui-sheet__banddesc">{sc.description}</span>}
                                    <span className="ui-sheet__count">{sc.rows} row{sc.rows === 1 ? '' : 's'}</span>
                                  </button>
                                </td>
                              </tr>
                            )}
                          </>
                        ) : null;
                        if (gp?.hidden) return <React.Fragment key={idx}>{bands}</React.Fragment>;
                        return (
                          <React.Fragment key={idx}>
                          {bands}
                          <SceneRow
                            idx={idx}
                            row={row}
                            cols={cols}
                            format={format}
                            totalRows={totalRows}
                            isReadOnly={isReadOnly}
                            editorFontSize={editorFontSize}
                            showQualNotesCol={showQualNotesCol}
                            canEditQualNotes={canEditQualNotes}
                            registryNames={registryNames}
                            canEditRegistry={canEditRegistry}
                            isSelected={selectedRows.has(idx) || selectAllActive}
                            isCut={cutRows.has(idx)}
                            isHovered={hoveredRow === idx}
                            dropMarker={
                              dropTargetIdx === idx
                                ? 'above'
                                : (dropTargetIdx === idx + 1 && idx === totalRows - 1 ? 'end' : null)
                            }
                            autocomplete={autocomplete && autocomplete.rowIdx === idx ? autocomplete : null}
                            focusedColKey={focusedCell?.rowIdx === idx ? focusedCell.colKey : null}
                            editingColKey={editingCell?.rowIdx === idx ? editingCell.colKey : null}
                            editingMode={editingCell?.rowIdx === idx ? editingCell.mode : null}
                            selectedColRange={(() => {
                              // Spec 1.5: per-row signature for the active cell range.
                              // String form so React.memo's shallow compare can skip rows
                              // whose membership didn't change during a drag.
                              if (!cellSelection) return '';
                              const r1 = Math.min(cellSelection.startRow, cellSelection.endRow);
                              const r2 = Math.max(cellSelection.startRow, cellSelection.endRow);
                              if (idx < r1 || idx > r2) return '';
                              const c1 = Math.min(cellSelection.startCol, cellSelection.endCol);
                              const c2 = Math.max(cellSelection.startCol, cellSelection.endCol);
                              if (r1 === r2 && c1 === c2) return ''; // 1×1, no highlight
                              return `${c1}-${c2}`;
                            })()}
                            multiSelectedColIdxs={(() => {
                              // Non-contiguous multi-selection: comma-joined colIdxs in this row.
                              if (cellMultiSelection.size === 0) return '';
                              const prefix = `${idx}:`;
                              const idxs = [];
                              for (const id of cellMultiSelection) {
                                if (id.startsWith(prefix)) {
                                  const ci = colKeyToIdx[id.slice(prefix.length)];
                                  if (ci != null) idxs.push(ci);
                                }
                              }
                              if (!idxs.length) return '';
                              idxs.sort((a, b) => a - b);
                              return idxs.join(',');
                            })()}
                            moveTargetColRange={(() => {
                              // Spec 4.1/4.2: per-row target colIdx range during a move-drag.
                              // Empty string when this row isn't a target row.
                              if (!moveDrag) return '';
                              const offsetR = moveDrag.targetRow - moveDrag.anchorRow;
                              const offsetC = moveDrag.targetCol - moveDrag.anchorCol;
                              const tr1 = moveDrag.r1 + offsetR;
                              const tr2 = moveDrag.r2 + offsetR;
                              if (idx < tr1 || idx > tr2) return '';
                              return `${moveDrag.c1 + offsetC}-${moveDrag.c2 + offsetC}`;
                            })()}
                            autofillHandleColIdx={(() => {
                              // Spec 5.2: anchor cell for the autofill handle — bottom-right
                              // of the multi-cell range, or the focused cell if no range.
                              if (cellSelection && (cellSelection.startRow !== cellSelection.endRow || cellSelection.startCol !== cellSelection.endCol)) {
                                const anchorR = Math.max(cellSelection.startRow, cellSelection.endRow);
                                if (anchorR !== idx) return null;
                                return Math.max(cellSelection.startCol, cellSelection.endCol);
                              }
                              if (focusedCell?.rowIdx !== idx) return null;
                              return colKeyToIdx[focusedCell.colKey] ?? null;
                            })()}
                            autofillPreviewRange={(() => {
                              // Spec 5.2: per-row colIdx range that's previewing fill content.
                              // Only rows BELOW the source range (sourceR2 < idx <= targetEndRow)
                              // get a preview tint, since source cells already show their values.
                              if (!autofillDrag) return '';
                              if (idx <= autofillDrag.sourceR2 || idx > autofillDrag.targetEndRow) return '';
                              return `${autofillDrag.sourceC1}-${autofillDrag.sourceC2}`;
                            })()}
                            colorPickerOpen={colorPickerRow === idx}
                            colorPickerCellKey={colorPickerCell?.rowIdx === idx ? colorPickerCell.colKey : null}
                            linkPopoverColKey={linkPopover?.rowIdx === idx ? linkPopover.colKey : null}
                            linkDraft={linkPopover?.rowIdx === idx ? linkDraft : null}
                            setHoveredRow={setHoveredRow}
                            setColorPickerRow={setColorPickerRow}
                            setColorPickerCell={setColorPickerCell}
                            setLinkPopover={setLinkPopover}
                            setLinkDraft={setLinkDraft}
                            setAutocomplete={setAutocomplete}
                            setSelectedRows={setSelectedRows}
                            setSelectionAnchor={setSelectionAnchor}
                            setSelectAllActive={setSelectAllActive}
                            setFocusedCell={setFocusedCell}
                            handlers={rowHandlers}
                            draftBodyRef={draftBodyRef}
                            selectedRowsSizeRef={selectedRowsSizeRef}
                            selectAllActiveRef={selectAllActiveRef}
                            lastFocusBodyRef={lastFocusBodyRef}
                          />
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                !format && (
                  <p className="ui-p ui-dense ui-text-2">This script has no format, so it has no scene table.</p>
                )
              )}

              {/* Activity */}
              <section className="ui-section">
                <h2 className="ui-section__title">
                  <button
                    type="button"
                    className="ui-disclosure"
                    aria-expanded={activityOpen}
                    onClick={async () => {
                      if (!activityOpen) {
                        setActivityLoading(true);
                        try {
                          const res = await api.get(`/api/audit/script/${selected.id}`);
                          setActivityLog(res.data);
                        } catch {
                          setActivityLog([]);
                        } finally {
                          setActivityLoading(false);
                        }
                      }
                      setActivityOpen(o => !o);
                    }}
                  >
                    <Icon icon={ChevronDown} />Activity
                  </button>
                </h2>
                {activityOpen && (
                  activityLoading ? (
                    <Skeleton variant="line" width="40%" label="Loading the activity" />
                  ) : activityLog.length === 0 ? (
                    <p className="ui-p ui-dense ui-text-2">No activity recorded yet.</p>
                  ) : (
                    <ul className="ui-log">
                      {activityLog.map(entry => {
                        const who = clean(entry.changedByMember?.name) || 'Unknown';
                        const from = clean(entry.oldValue), to = clean(entry.newValue);
                        let label = '';
                        if (entry.fieldChanged === 'qual_status') {
                          if (!entry.oldValue) label = 'Script created';
                          else if (entry.newValue === 'Needs Review')   label = entry.oldValue === 'Approved' ? 'Pulled back to Needs review' : 'Submitted for review';
                          else if (entry.newValue === 'Approved')       label = 'Approved';
                          else if (entry.newValue === 'Needs Revision') label = 'Sent back for revision';
                          else if (entry.newValue === 'Backburner')     label = 'Moved to Backburner';
                          else if (entry.newValue === 'I/P')            label = entry.oldValue === 'Backburner' ? 'Restored from Backburner' : 'Returned to in progress';
                          else label = `Status set to ${to}`;
                        } else if (entry.fieldChanged === 'deleted') {
                          label = 'Script deleted';
                        } else if (entry.fieldChanged === 'script_body') {
                          label = 'Script content edited';
                        } else if (entry.fieldChanged === 'script_saved') {
                          label = 'Saved manually';
                        } else if (entry.fieldChanged === 'script_name') {
                          label = 'Title changed';
                        } else if (entry.fieldChanged === 'production_records_deleted') {
                          label = 'Production cards removed when it left Approved';
                        } else if (entry.fieldChanged === 'client_id') {
                          label = `Reassigned from ${from} to ${to}`;
                        } else if (entry.fieldChanged === 'posting_slots_cleared') {
                          label = 'Posting slot cleared (reassigned)';
                        } else {
                          label = entry.fieldChanged?.replace(/_/g, ' ');
                        }
                        return (
                          <li key={entry.id} className="ui-log__row">
                            <span className="ui-meta">{fmtWhen(entry.changedAt)}</span>
                            <span>{label}<span className="ui-meta">, by {who}</span></span>
                            {entry.oldValue && entry.newValue ? <span className="ui-meta">{from} to {to}</span> : <span />}
                          </li>
                        );
                      })}
                    </ul>
                  )
                )}
              </section>

              {/* Version history */}
              {!isReadOnly && (
                <section className="ui-section">
                  <h2 className="ui-section__title">
                    <button
                      type="button"
                      className="ui-disclosure"
                      aria-expanded={versionsOpen}
                      onClick={async () => {
                        if (!versionsOpen) {
                          setVersionsLoading(true);
                          try {
                            const res = await api.get(`/api/scripts/${selected.id}/versions`);
                            setVersions(res.data);
                          } catch { setVersions([]); }
                          finally { setVersionsLoading(false); }
                        }
                        setVersionsOpen(o => !o);
                      }}
                    >
                      <Icon icon={ChevronDown} />Version history
                    </button>
                  </h2>
                  {versionsOpen && (
                    versionsLoading ? (
                      <Skeleton variant="line" width="40%" label="Loading the versions" />
                    ) : versions.length === 0 ? (
                      <p className="ui-p ui-dense ui-text-2">No saved versions yet. A version is kept each time the script is saved.</p>
                    ) : (
                      <ul className="ui-log">
                        {versions.map(v => (
                          <li key={v.id} className="ui-log__row">
                            <span className="ui-meta">{fmtWhen(v.savedAt)}</span>
                            <span>{clean(v.label) || 'Snapshot'}<span className="ui-meta">, by {clean(v.savedByMember?.name) || 'Unknown'}</span></span>
                            <Button
                              variant="secondary"
                              icon={Undo2}
                              onClick={async () => {
                                const ok = await confirm({
                                  title: 'Restore this version?',
                                  body: `The script goes back to the version saved ${fmtWhen(v.savedAt)}. The current text is kept in the version history.`,
                                  confirmLabel: 'Restore version',
                                  destructive: true,
                                });
                                if (!ok) return;
                                try {
                                  const res = await api.post(`/api/scripts/${selected.id}/restore/${v.id}`);
                                  setSelected(prev => ({ ...prev, scriptBody: res.data.scriptBody }));
                                  // HF-FIX-PASTE-1: the restored body is the new base, so the autosave doesn't re-send its rows.
                                  savedBodyRef.current = res.data.scriptBody;
                                  if (typeof res.data?.rev === 'number') revRef.current = res.data.rev;
                                  setDraftBody(res.data.scriptBody);
                                  const vRes = await api.get(`/api/scripts/${selected.id}/versions`);
                                  setVersions(vRes.data);
                                } catch { toast('The version was not restored. Try again.', { tone: 'danger' }); }
                              }}
                            >
                              Restore
                            </Button>
                          </li>
                        ))}
                      </ul>
                    )
                  )}
                </section>
              )}
            </div>

            {/* Scene footer: docked at the bottom of the editor, outside the scroller */}
            {!isReadOnly && cols.length > 0 && draftBody?.scenes != null && (
              <div className="ui-footbar ui-inset ui-inset--bar ui-toolbar" role="toolbar" aria-label="Rows">
                <Button variant="quiet" icon={Plus} onClick={addRow}>Add scene row</Button>
                {draftBody?.format === 'Longform' && (
                  <Button variant="quiet" icon={Plus} data-testid="lf-next-scene"
                    title="Start the next scene of the current episode: adds its first row at the end (the scene number is this episode's next) and puts the cursor in Character. Type the description in the row."
                    onClick={() => appendSceneRow(longformNextSceneNumber(draftBody?.scenes), '')}>
                    Next scene
                  </Button>
                )}
                <input ref={csvInputRef} type="file" accept=".csv" hidden onChange={handleImportCSV} />
                <Button variant="quiet" icon={FileUp} onClick={() => csvInputRef.current?.click()}>Import CSV</Button>
                <Button variant="quiet" icon={ClipboardPaste} onClick={pasteFromClipboard} title="Paste from Google Sheets or Excel (replaces every row)">
                  Paste from spreadsheet
                </Button>
                <Button variant="quiet" icon={Download} onClick={() => downloadTemplate(format)}>Download template</Button>
                {selectedRows.size > 0 && (
                  <span className="ui-cluster ui-cluster--end ui-grow" data-keep-row-selection="true">
                    <Button variant="secondary" icon={ArrowUp} onClick={() => moveSelectedBlock(-1)} disabled={Math.min(...selectedRows) === 0}>
                      Move up
                    </Button>
                    <Button variant="secondary" icon={ArrowDown} onClick={() => moveSelectedBlock(1)} disabled={Math.max(...selectedRows) === (draftBody?.scenes?.length ?? 0) - 1}>
                      Move down
                    </Button>
                    <Button variant="danger" icon={Trash2} onClick={() => setMultiDeleteConfirm(true)}>
                      Delete {selectedRows.size} row{selectedRows.size !== 1 ? 's' : ''}
                    </Button>
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Clio drawer */}
          {clioOpen && (
            <aside className="ui-drawer" aria-label="Clio">
              <div className="ui-drawer__head">
                <h2 className="ui-heading">Clio</h2>
                <Button variant="quiet" iconOnly icon={X} label="Close Clio" onClick={() => setClioOpen(false)} />
              </div>
              <ClioChat
                mode="drawer"
                sessionKey={selected?.id ? `helicon_chat_script_${selected.id}` : 'helicon_chat_drawer'}
                key={selected?.id || 'no_script'}
              />
            </aside>
          )}
        </div>

        {/* Find and replace (spec 6.1) */}
        {findReplaceOpen && format && draftBody && (
          <FindReplaceModal
            draftBody={draftBody}
            cols={cols}
            onApply={next => { pushUndo(draftBody); setDraftBody(next); }}
            onFocusMatch={focusFindMatch}
            onClose={() => setFindReplaceOpen(false)}
          />
        )}

        {/* Run 2: the client character-registry modal (Character column header) */}
        {registryModalOpen && selected?.client?.id && (
          <CharacterRegistryModal
            clientId={selected.client.id}
            clientName={selected.client.name}
            onClose={() => setRegistryModalOpen(false)}
            canEdit={canEditRegistry}
            canMapTalent={canMapTalent}
            onChanged={loadRegistry}
          />
        )}

        {/* Multi-row delete: a two-step destructive confirm. It stays inside data-keep-row-selection (as the old dialog
            did) so a click in it does not clear the row selection it acts on. */}
        {multiDeleteConfirm && (
          <div data-keep-row-selection="true">
            <RowsDeleteDialog
              count={selectedRows.size}
              onCancel={() => setMultiDeleteConfirm(false)}
              onDelete={deleteSelectedRows}
            />
          </div>
        )}

        {/* Share links (kind "script": the three script share calls) */}
        <ShareLinkManager resourceType="script" resourceId={selected.id} open={shareOpen} onOpenChange={setShareOpen} />

        {/* Phase 5: right-click row menu */}
        {contextMenu && (() => {
          const { x, y, targetRows, clickedCell } = contextMenu;
          const n = targetRows.length;
          const totalRows = draftBody?.scenes?.length || 0;
          const hasClip = !!rowClipboardRef.current;
          const close = () => setContextMenu(null);
          const run = (fn) => { fn(); close(); };
          const label = (verb) => `${verb} ${n} row${n !== 1 ? 's' : ''}`;
          // Cell-colour preview: how many cells the cell colour entry would hit.
          const cellTargets = clickedCell
            ? cellColorTargets(clickedCell.rowIdx, clickedCell.colKey)
            : [];
          const cellLabel = cellTargets.length > 1 ? `Color ${cellTargets.length} cells` : 'Color cell';
          // Initial position = the click point; the layout effect above measures
          // the real menu and clamps it fully on-screen before paint.
          const Item = ({ label: lbl, onClick, disabled, danger }) => (
            <li role="none">
              <button
                type="button"
                role="menuitem"
                className={`ui-menu__item${danger ? ' ui-menu__item--danger' : ''}`}
                disabled={disabled}
                onMouseDown={e => { e.preventDefault(); if (!disabled) onClick(); }}
              >{lbl}</button>
            </li>
          );
          return (
            <div
              ref={ctxMenuRef}
              data-gutter-context-menu="true"
              className="ui ui-ctxmenu"
              style={{ left: x, top: y }}
            >
              <ul className="ui-menu" role="menu" aria-label="Rows">
                <Item label={n === 1 ? 'Insert row above' : `Insert ${n} rows above`} onClick={() => run(() => insertRowAt(targetRows[0], n))} />
                <Item label={n === 1 ? 'Insert row below' : `Insert ${n} rows below`} onClick={() => run(() => insertRowAt(targetRows[targetRows.length - 1] + 1, n))} />
                {format === 'Longform' && (() => {
                  const allDir = targetRows.every(i => draftBody?.scenes?.[i]?.kind === 'direction');
                  return (
                    <>
                      <Item label={allDir ? label('Mark as dialogue') : label('Mark as direction')} onClick={() => run(() => setRowsKind(targetRows, allDir ? null : 'direction'))} />
                      <Item label="Insert episode break below" onClick={() => run(() => insertEpisodeBreakAt(targetRows[targetRows.length - 1] + 1))} />
                    </>
                  );
                })()}
                <Item label={label('Duplicate')} onClick={() => run(() => duplicateRowsByIndices(targetRows))} />
                <Item label={label('Cut')} onClick={() => run(() => cutRowsByIndices(targetRows))} />
                <Item label={label('Copy')} onClick={() => run(() => {
                  // Respect an active cell selection (copy the cell block); otherwise
                  // copy the right-clicked whole rows. Both update the app clipboard.
                  const sel = getActiveSelection();
                  if (sel && sel.kind === 'cells') copySelectionToAppClipboard();
                  else copyRowsByIndices(targetRows);
                })} />
                <Item label="Paste above" onClick={() => run(() => pasteAboveRow(targetRows[0]))} disabled={!hasClip} />
                <li className="ui-menu__sep" role="separator" />
              </ul>
              <div className="ui-ctxmenu__label">{n === 1 ? 'Color row' : `Color ${n} rows`}</div>
              <ColorSwatch onSelect={color => run(() => applyRowColorToIndices(targetRows, color))} />
              {clickedCell && (
                <>
                  <div className="ui-ctxmenu__label">{cellLabel}</div>
                  <ColorSwatch onSelect={color => run(() => applyCellColorToTargets(cellTargets, color))} />
                </>
              )}
              <ul className="ui-menu" role="menu" aria-label="Delete rows">
                <li className="ui-menu__sep" role="separator" />
                <Item
                  label={label('Delete')}
                  danger
                  onClick={() => run(() => {
                    // If deleting every row, leave one blank row behind (deleteRowsByIndices handles this).
                    deleteRowsByIndices(targetRows);
                  })}
                  disabled={totalRows - n <= 0 && totalRows > 0 && n >= totalRows}
                />
              </ul>
            </div>
          );
        })()}

        {/* Phase 3: a label at the pointer while rows are dragged to a new position */}
        {dragPreview && (
          <div className="ui ui-dragchip" style={{ left: dragPreview.x + 12, top: dragPreview.y + 12 }}>
            Moving {dragPreview.count} row{dragPreview.count !== 1 ? 's' : ''}
          </div>
        )}
      </div>
    );
  }

  return null;
});

export default ScriptEditor;

// Registers the shell's page header while a script is open (the list's own header unregisters when the list hides).
function EditorHeader(spec) { usePageHeader(spec); return null; }

// "Oct 3, 2026, 2:15 PM"
function fmtWhen(d) {
  const date = new Date(d);
  return `${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}, ${date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
}

// ── Toolbar menus (Export, Actions) ───────────────────────────────
// A secondary Button opening a src/ui Menu. `items` is a list of { label, icon?, onSelect, danger?, dividerBefore? };
// an item with no onSelect is dropped, which is how the caller expresses the old per-button conditions without this
// component knowing about roles or script state. dividerBefore puts a separator above the item (fences off Delete).
function EditorMenu({ label, icon, items }) {
  const [open, setOpen] = useState(false);
  const shown = items.filter(it => it && typeof it.onSelect === 'function');
  if (shown.length === 0) return null;
  const menuItems = shown.flatMap(it => (it.dividerBefore ? [{ separator: true }, it] : [it]));
  return (
    <Popover open={open} onOpenChange={setOpen} align="end" label={label}
      trigger={({ toggle, ref, props }) => (
        <Button ref={ref} variant="secondary" icon={icon} onClick={toggle} {...props}>{label}</Button>
      )}>
      <Menu label={label} items={menuItems} onClose={() => setOpen(false)} />
    </Popover>
  );
}

// ── Delete selected rows: two-step destructive confirm ────────────
// The shared Confirm renders at the app root; this one renders in place so it can sit inside data-keep-row-selection.
function RowsDeleteDialog({ count, onCancel, onDelete }) {
  const [armed, setArmed] = useState(false);
  return (
    <Modal
      title={`Delete ${count} row${count !== 1 ? 's' : ''}?`}
      onClose={onCancel}
      footer={(
        <>
          <Button variant="quiet" onClick={onCancel}>Cancel</Button>
          {armed
            ? <Button variant="danger" confirmStep onClick={onDelete}>Delete {count} row{count !== 1 ? 's' : ''}</Button>
            : <Button variant="danger" onClick={() => setArmed(true)}>Delete {count} row{count !== 1 ? 's' : ''}</Button>}
        </>
      )}
    >
      <p className="ui-p">The {count} selected row{count !== 1 ? 's are' : ' is'} removed from the script. Ctrl+Z brings {count !== 1 ? 'them' : 'it'} back until you leave the script.</p>
    </Modal>
  );
}

// ── Find and replace (spec 6.1) ───────────────────────────────────
// Searches across all cells of the current draftBody. Replace + Replace all
// commit through onApply, which pushes a single undo entry per action.
function FindReplaceModal({ draftBody, cols, onApply, onFocusMatch, onClose }) {
  const [find,     setFind]     = useState('');
  const [replace,  setReplace]  = useState('');
  const [matchIdx, setMatchIdx] = useState(0);

  // Build the flat list of matches every time find or body changes.
  // Each match: { rowIdx, colKey, start, end }
  const matches = useMemo(() => {
    if (!find || !draftBody?.scenes) return [];
    const needle = find.toLowerCase();
    const out = [];
    draftBody.scenes.forEach((row, rowIdx) => {
      cols.forEach(c => {
        const v = row[c.key];
        if (typeof v !== 'string' || !v) return;
        const hay = v.toLowerCase();
        let from = 0;
        while (from <= hay.length) {
          const i = hay.indexOf(needle, from);
          if (i === -1) break;
          out.push({ rowIdx, colKey: c.key, start: i, end: i + needle.length });
          from = i + Math.max(1, needle.length);
        }
      });
    });
    return out;
  }, [find, draftBody, cols]);

  // Keep matchIdx in range as matches shrink/grow.
  useEffect(() => { if (matchIdx >= matches.length) setMatchIdx(0); }, [matches.length, matchIdx]);

  // Focus + select the current match — only when the user explicitly
  // navigates (Prev/Next/Replace). Without this gate, every keystroke in the
  // Find input would steal focus from the input back to the matched cell.
  // Delegates to the parent's focusFindMatch which brings the cell into edit
  // mode (so the matched text range can actually be selected in a textarea).
  const userNavRef = useRef(false);
  useEffect(() => {
    if (!userNavRef.current) return;
    userNavRef.current = false;
    if (matches.length === 0) return;
    const m = matches[matchIdx];
    if (!m) return;
    onFocusMatch?.(m.rowIdx, m.colKey, m.start, m.end);
  }, [matchIdx, matches, onFocusMatch]);

  function navigate(delta) {
    if (matches.length === 0) return;
    userNavRef.current = true;
    setMatchIdx(i => (i + delta + matches.length) % matches.length);
  }

  function applyReplaceAt(body, m, replacement) {
    const scenes = [...body.scenes];
    const row    = { ...scenes[m.rowIdx] };
    const v      = String(row[m.colKey] || '');
    row[m.colKey] = v.slice(0, m.start) + replacement + v.slice(m.end);
    scenes[m.rowIdx] = row;
    return { ...body, scenes };
  }

  function handleReplace() {
    if (matches.length === 0) return;
    const m = matches[matchIdx];
    const next = applyReplaceAt(draftBody, m, replace);
    // After the body re-renders, focus the new "current" match (matchIdx is
    // unchanged but now points at what used to be matches[matchIdx + 1]).
    userNavRef.current = true;
    onApply(next);
  }

  function handleReplaceAll() {
    if (matches.length === 0) return;
    // Walk matches back-to-front so earlier offsets stay valid.
    let next = draftBody;
    for (let i = matches.length - 1; i >= 0; i--) {
      next = applyReplaceAt(next, matches[i], replace);
    }
    onApply(next);
  }

  // Escape closes (the Modal handles it). Enter in a field advances to the next match without stealing focus from the
  // field, so the user can keep typing and replacing without re-clicking.
  function handleKey(e) {
    if (e.key === 'Enter' && !e.shiftKey && document.activeElement?.tagName === 'INPUT') {
      e.preventDefault();
      setMatchIdx(i => (matches.length === 0 ? 0 : (i + 1) % matches.length));
    }
  }

  return (
    <Modal
      title="Find and replace"
      onClose={onClose}
      footer={(
        <>
          <Button variant="quiet" onClick={onClose}>Close</Button>
          <Button variant="secondary" icon={Replace} disabled={matches.length === 0} onClick={handleReplace}>Replace</Button>
          <Button variant="primary" disabled={matches.length === 0} data-fr-btn="all" onClick={handleReplaceAll}>Replace all</Button>
        </>
      )}
    >
      <div className="ui-stack" onKeyDown={handleKey}>
        <Input label="Find" data-autofocus value={find} onChange={e => { setFind(e.target.value); setMatchIdx(0); }} placeholder="Text to search for" />
        <Input label="Replace with" value={replace} onChange={e => setReplace(e.target.value)} placeholder="Replacement text" />
        <div className="ui-cluster" aria-live="polite">
          {find === '' ? (
            <span className="ui-meta">Type to search every cell of the script.</span>
          ) : matches.length === 0 ? (
            <span className="ui-dense ui-text-danger">No matches</span>
          ) : (
            <>
              <span className="ui-dense ui-num">{matchIdx + 1} of {matches.length} matches</span>
              <Button variant="quiet" icon={ArrowUp} onClick={() => navigate(-1)}>Previous</Button>
              <Button variant="quiet" icon={ArrowDown} onClick={() => navigate(+1)}>Next</Button>
            </>
          )}
        </div>
      </div>
    </Modal>
  );
}
